/**
 * Oeffentliche Angebotsseite (Stitch: angebot_catering_gourmet) — OHNE Login.
 * Route /angebot/:token; laedt GET /api/public/offer/:token (same-origin, der
 * Host traegt den Mandanten) und sendet die Wahl an POST .../respond.
 *
 * Vorschau-Modus (05.08.2026, Antoines Wunsch): DIESELBE Komponente laeuft zusaetzlich
 * eingeloggt unter /veranstaltungen/:id/angebotsseite (prop `vorschau`). Sie laedt dann
 * authentifiziert von GET /api/events/:id/angebots-vorschau (gleiche JSON-Form, aber jeder
 * Status inkl. Entwurf), zeigt oben einen Vorschau-Banner und deaktiviert alles, was eine
 * Gast-Willenserklaerung waere (Annehmen/Absagen/Menue bestaetigen — keine POSTs moeglich).
 * Bewusst KEIN Layout-Fork: eine Komponente, zwei Datenquellen.
 */
import { fokusStil } from "../ui/BildAusschnittDialog";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { meldeAngebotsEreignis, type AngebotsEreignisArt } from "../lib/angebots-ereignis";
import { useGastEinblicke } from "../lib/angebots-einblicke-gast";
import type { Payment, PublicOffer, PublicOfferItem } from "../ui/types";
import { formatDate, money } from "../ui/format";
import { Icon, Zeichenzaehler, Button, ButtonLink, Input, Select, Checkbox, Radio } from "../ui/components";
import { apiFetch } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { zahlungsstandZeile } from "../lib/zahlungs-kennzahlen";
import { BetriebsKopf, betriebsName } from "../ui/BetriebsKopf";
import { AdresseFelder, type AdresseFeld, type AdresseWerte } from "../ui/AdresseFelder";
import { VorfreudeKarte } from "../ui/VorfreudeKarte";
import { LAENDER, LAENDER_EN } from "../lib/laender";
// LMIV: Allergen-Codes + Legende gehören VOR die Kaufentscheidung des Kunden (sql/137).
import { lmivLabel, DIETARY } from "../ui/lmiv";
import { anlassAkkusativ, anlassUeberschrift } from "@maestro/angebots-betreff";
import { variantenReihenfolge, vorauswahl, zusatzZeilen } from "../lib/varianten-empfehlung";
import { A_LA_CARTE_GAST } from "../lib/a-la-carte";
import { TerminReservierungHinweis } from "../ui/TerminReservierungHinweis";
import type { AngebotsseiteV2 } from "../ui/types";
import { Bewertungen, FESTE_LEISTE_PLATZ, FesteLeiste, FrageKarte, MobileLeiste, PersoenlicheNotiz, PreiseGleichwertig, TeilenKnopf, V2T, scrolleZu, vornameAus } from "../ui/AngebotsseiteV2";
import { BegruessungsVideo } from "../ui/BegruessungsVideo";
import { AT as AUSWAHL_T, AngebotsAuswahl, akzentFarben, type AnzahlungAnzeige } from "../ui/AngebotsAuswahl";
import {
  alternativenAus,
  gaengeAus,
  menuewahlenAus,
  menuewahlLage,
  menuewahlSperrt,
  offeneWahlgruppen,
  rechne,
  wahlStand,
  wahlZeile,
  wahlgaengeAus,
  zubuchungenAus,
  zubuchungsAuswahl,
  type Mengen,
} from "../ui/angebot-auswahl-modell";
import { NachtragGast } from "../ui/NachtragGast";
import { MenuewahlGastNachBuchung } from "../ui/MenuewahlGast";
import { KostenuebernahmeNebenweg, ZusageAngaben, ZusageSchritt1, ZusageSchritt2Kopf, type ZusageAuswahl } from "../ui/ZusageZweiSchritte";

type Phase = "loading" | "ready" | "submitting" | "done" | "declined" | "error";

/** Deko-Fotos je Options-Karte (Design-Vorlage: docs/design/DESIGN-SYSTEM.md), zyklisch je Index. */
const OPTION_PHOTOS = ["/img/offer-1-v2.webp", "/img/offer-2-v2.webp", "/img/offer-3-v2.webp"];

/** Rubrik-Labels fuer die neuen offer_items-Sektionen, je Sprache. */
const SECTION_LABEL: Record<"de" | "en", Record<string, string>> = {
  de: { menu: "Menü", drinks: "Getränke", equipment: "Equipment", staff: "Personal", other: "Weiteres" },
  en: { menu: "Menu", drinks: "Drinks", equipment: "Equipment", staff: "Staff", other: "Other" },
};

/**
 * Statische UI-Texte DE/EN (Block 4: nur DE/EN, kein IT/FR laut Antoine). Gesteuert über
 * `offer.event.offer_language` — sobald die Daten geladen sind, ist die gesamte Seite (bis auf
 * den Vor-Lade-Fehlerzustand, der die Sprache noch nicht kennt) in der gewählten Sprache.
 */
const T = {
  de: {
    heroLabel: "Ihr persönliches Angebot",
    guestsSuffix: (n: number) => `${n} Gäste`,
    validUntil: (date: string) => `gültig bis ${date}`,
    intro: (dateText: string, guestText: string) => `Wir freuen uns sehr, Ihnen folgendes Angebot${dateText}${guestText} präsentieren zu dürfen.`,
    /** A3: „ für Ihren 60. Geburtstag am 17. Oktober 2026“ — Anlass aus `anlassAkkusativ()`, neutral „Ihre Veranstaltung“. */
    introFor: (anlass: string, date: string | null) => ` für ${anlass}${date ? ` am ${date}` : ""}`,
    /*
     * Buchungsstrecke A3 (Mockup Station 6): „Es ist fix!“ statt „Vielen Dank für Ihre Auswahl!“ —
     * Vorfreude statt Transaktion. Aber nur, wenn es STIMMT: verlangt der Betrieb eine Anzahlung
     * (oder eine Kostenübernahme), ist der Termin erst damit fest. Bis dahin „fast fix“.
     */
    doneTitle: "Es ist fix!",
    doneBody: (anlass: string) => `Wir freuen uns auf ${anlass} und melden uns in Kürze mit den nächsten Schritten bei Ihnen.`,
    fastFixTitle: "Es ist fast fix!",
    fastFixBody: (anlass: string, vollzahlung: boolean, kostenuebernahme: boolean) =>
      `${
        kostenuebernahme
          ? "Sobald die Kostenübernahme vorliegt"
          : vollzahlung
            ? "Sobald Ihre Zahlung eingegangen ist"
            : "Sobald Ihre Anzahlung eingegangen ist"
      }, ist Ihr Termin fest reserviert. Wir freuen uns auf ${anlass}!`,
    /** Punkt 103: Vorgang voll erstattet — Seite bleibt lesbar, ohne jeden Zahlungsweg. */
    erstattetTitle: "Betrag erstattet",
    erstattetBody: "Der gezahlte Betrag wurde Ihnen vollständig erstattet. Es ist keine Zahlung mehr offen.",
    perPerson: "pro Person",
    total: "gesamt",
    selectedBadge: "Ausgewählt",
    /** Buchungsstrecke B1: die Empfehlung DES BETRIEBS — nie „meistgewählt“ (Konzept § 5). */
    recommendedBadge: "Unsere Empfehlung",
    zusatzWeitere: (n: number) => `+ ${n} weitere ${n === 1 ? "Position" : "Positionen"}`,
    // §5.21: Wortlaut identisch mit dem Angebotsdokument (apps/api/src/lib/offer-document.ts,
    // `optionalTag`). „(optional)" allein liess offen, ob der Posten im Gesamtpreis steckt —
    // genau die Frage, aus der beim Kunden Streit ueber die Rechnung entsteht.
    optionalTag: "optional — nicht im Gesamtpreis enthalten",
    // Migration 722: „zzgl.“ in einem Pauschale-Block — fest bestellt, im Gesamtpreis enthalten.
    surchargeTag: "zusätzlich zur Pauschale berechnet",
    /** Migration 725: Paket nur für einen Teil der Gäste. */
    teilgruppe: (n: number, name: string, preis: string) => `${n} × ${name} à ${preis} je Gast`,
    /** Migration 450: steht über einer Wahlgruppe. „eines davon" statt „oder" als Trenner —
     *  ein „oder" zwischen drei Zeilen liest sich zweideutig, die Ansage darüber nicht. */
    wahlHinweis: "Sie wählen eines dieser Gerichte:",
    /** Punkt 61 (Migration 451): nach der Annahme wird aus dem Hinweis eine echte Wahl. */
    wahlAufforderung: "Bitte wählen Sie ein Gericht:",
    wahlBlockHeading: "Ihre Auswahl aus dem Menü",
    /*
     * Punkt 32 (Antoine 16.09.2026): „auf Wunsch zubuchbar muss der Gast optional dazu buchen
     * können." Sie-Form wie auf der ganzen Seite. „Auf Wunsch dazu" statt „Zusatzleistungen":
     * das eine ist ein Angebot, das andere ein Katalogbegriff.
     */
    addonHeading: "Auf Wunsch dazu",
    addonIntro: "Diese Leistungen sind nicht im Angebotspreis enthalten. Sie entscheiden, ob Sie sie dazubuchen möchten.",
    addonPerPerson: (preis: string, gesamt: string) => `${preis} pro Person · ${gesamt} gesamt`,
    addonJeStueck: (preis: string) => `${preis} je Stück`,
    addonZeilensumme: (menge: number, preis: string, summe: string) => `${menge} × ${preis} = ${summe}`,
    addonMenge: "Menge",
    addonGebucht: "verbindlich gebucht",
    addonNichtGebucht: "nicht gebucht",
    /** Die Summe am Annahme-Block: nur sichtbar, solange überhaupt etwas dazugebucht ist. */
    addonSummeAngebot: "Angebot",
    addonSummeZubuchungen: "Zubuchungen",
    addonSummeGesamt: "Gesamt",
    wahlGespeichert: "Ihre Wahl ist gespeichert.",
    wahlFehler: "Ihre Wahl konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    wahlOffen: (n: number) => (n === 1 ? "Eine Wahl ist noch offen." : `${n} Wahlen sind noch offen.`),
    // §5.19: Gaestezahl JE Variante, damit der €/Gast-Preis und die Personenzahl zusammenpassen.
    forGuests: (n: number) => `für ${n} Gäste`,
    // B5 (UX-Polish 25.08.2026): Die Mail haengt KEIN PDF an — sie traegt den Link hierher.
    // Damit die Kundin trotzdem etwas in der Hand hat, druckt diese Seite sich selbst; die
    // Druckvorschau jedes Browsers bietet „Als PDF sichern" an. Bewusst KEIN serverseitiges PDF
    // (das gibt es nicht, s. `routes/offer-items.ts`) — der Druckweg braucht keins.
    printCta: "Als PDF herunterladen / Drucken",
    // Einblicke (730): die beim Versand aufbewahrte PDF, ueber den persoenlichen Link.
    pdfCta: "Angebot als PDF",
    pdfCtaNr: (n: number) => `Angebot als PDF (Fassung ${n})`,
    printHint: "Öffnet die Druckansicht — dort lässt sich das Angebot auch als PDF sichern.",
    notesHeading: "Haben Sie noch Anmerkungen?",
    notesPlaceholder: "Teilen Sie uns gerne besondere Wünsche, Allergien oder Fragen mit …",
    // sql/344 (Antoine 30.08.2026): Rechnungsadresse + Telefon vor der Annahme-Checkbox.
    billingHeading: "Rechnungsadresse",
    billingNameLabel: "Name",
    billingCompanyLabel: "Firma (falls zutreffend)",
    billingStreetLabel: "Straße und Hausnummer",
    billingZipLabel: "PLZ",
    billingCityLabel: "Ort",
    billingAdditionLabel: "Adresszusatz (optional)",
    billingCountryLabel: "Land",
    billingPhoneLabel: "Telefonnummer",
    // § 8 (Migration 345): bei paymentPathway='kostenuebernahme' wird dasselbe Feld verpflichtend
    // als Handynummer behandelt (SMS-TAN der Kostenuebernahme-Signatur braucht ein Mobilfunkgeraet).
    billingPhoneLabelHandynummer: "Handynummer",
    /** Punkt 57: warum es eine Mobilnummer sein muss — der Grund gehört ans Feld, nicht in eine Fußnote. */
    handynummerGrund: "Bitte eine Mobilnummer angeben: Zum Unterschreiben wird ein Code per SMS dorthin geschickt.",
    /* Punkt 57 Etappe 3: der Gast fordert die Kostenübernahme selbst an. */
    kuHeading: "Kostenübernahme unterschreiben",
    kuIntro: "Statt hier anzunehmen, können Sie eine Kostenübernahme elektronisch unterschreiben. Sie gilt als verbindliche Zusage.",
    kuSignerHeading: "Wer unterschreibt?",
    kuSignerHint: "Leer lassen, wenn Sie selbst unterschreiben. Bestellt jemand für eine andere Person — etwa das Sekretariat für die Geschäftsführung —, gehören hier die Angaben der unterzeichnenden Person hin.",
    kuSignerName: "Name der unterzeichnenden Person",
    kuSignerEmail: "E-Mail der unterzeichnenden Person",
    kuSignerCompany: "Firma der unterzeichnenden Person",
    kuAgbLabel: "Ich habe die Allgemeinen Geschäftsbedingungen gelesen und akzeptiere sie.",
    kuStornoLabel: "Ich habe die Stornobedingungen gelesen und akzeptiere sie.",
    // S0: auch die Unterschrift ist eine verbindliche Bestellung — das Wort „zahlungspflichtig“ gehört an den Knopf.
    kuCta: "Kostenübernahme zahlungspflichtig unterschreiben",
    kuBusy: "Wird vorbereitet …",
    kuHintPflicht: "Bitte Anschrift und Mobilnummer ausfüllen und beide Bestätigungen setzen.",
    kuBereitHeading: "Ihre Kostenübernahme liegt bereit",
    kuBereitBody: "Der Link führt zur Unterschrift. Den Code dafür erhalten Sie per SMS.",
    kuBereitCta: "Jetzt unterschreiben",
    kuSigniert: (datum: string) => `Kostenübernahme unterschrieben am ${datum}.`,
    kuErsetztHeading: "Ihre geänderte Kostenübernahme liegt bereit",
    kuErsetzt: (datum: string) => `Geänderte Fassung – ersetzt Ihre Kostenübernahme vom ${datum}. Bitte unterschreiben Sie, damit wir die gewünschten Änderungen umsetzen können.`,
    kuFehler: "Die Unterschrift konnte gerade nicht vorbereitet werden. Bitte versuchen Sie es in Kürze erneut.",
    billingRefLabel: "Referenz für Ihre Buchhaltung (z. B. Veranstaltungs-/Bestellnummer, Kostenstelle) — optional",
    hintBillingIncomplete: "Bitte Rechnungsadresse und Telefonnummer vollständig angeben.",
    requiredMarker: "* Pflichtfeld (Name oder Firma, Anschrift, Telefonnummer)",
    requiredMarkerHandynummer: "* Pflichtfeld (Name oder Firma, Anschrift, Handynummer)",
    agbLabel: "Ich nehme dieses Angebot verbindlich an und akzeptiere die genannten Zahlungsbedingungen.",
    // Punkt 50 b2: sobald der Betrieb AGB gepflegt hat, nennt das Haekchen sie ausdruecklich.
    agbLabelMitAgb:
      "Ich nehme dieses Angebot verbindlich an und akzeptiere die Allgemeinen Geschäftsbedingungen sowie die genannten Zahlungs- und Stornobedingungen.",
    agbAnzeigen: "Allgemeine Geschäftsbedingungen anzeigen",
    agbAusblenden: "Allgemeine Geschäftsbedingungen ausblenden",
    agbHeading: "Allgemeine Geschäftsbedingungen",
    paymentHeading: "Zahlungs- & Stornobedingungen",
    stornoHeading: "Stornobedingungen",
    // Antoine, 16.09.2026: „Es sollte hier zu sehen sein, dass im zweiten Step ein Stripe Link
    // kommt, falls dem so ist." Der Knopf ist ausgegraut und traegt den Betrag — er zeigt, was
    // kommt, ohne es vorwegzunehmen.
    vorschauCta: (amount: string) => `Jetzt bezahlen · ${amount}`,
    vorschauArtAnzahlung: "Anzahlung",
    vorschauArtVollzahlung: "Zahlung des Gesamtbetrags",
    vorschauHinweis: "Bitte nehmen Sie das Angebot zuerst an.",
    ctaSubmitting: "Wird gesendet …",
    /*
     * Buchungsstrecke S0 (§ 312j Abs. 3 BGB): der Knopf, mit dem der Gast bestellt, muss
     * ausdrücklich auf die Zahlungspflicht hinweisen. „Annehmen“ allein reicht nicht — fehlt das
     * Wort, kommt bei Verbrauchern schlimmstenfalls gar kein Vertrag zustande. Deshalb trägt
     * JEDE Variante „zahlungspflichtig“.
     */
    ctaAccept: "Angebot zahlungspflichtig annehmen",
    // § 8: acceptanceFlow='direct_payment' - der Annahme-Schritt fuehrt sofort in den
    // Zahlungsschritt, die CTA benennt das direkt statt nur "annehmen".
    ctaAcceptDirectStripe: "Angebot zahlungspflichtig annehmen & bezahlen",
    ctaAcceptDirectKostenuebernahme: "Angebot zahlungspflichtig annehmen & Kostenübernahme starten",
    /* S0: die Zusammenfassung direkt über dem Knopf — was der Gast bestätigt, steht dort, wo er klickt. */
    summaryHeading: "Ihre Buchung im Überblick",
    summaryDatum: "Datum",
    summaryAnzahlung: "Anzahlung nach Annahme",
    summaryVollzahlung: "Zahlung nach Annahme",
    /* Nachbesserung PR #1495: solange der Serverbetrag zur aktuellen Auswahl fehlt — kein Betrag, nur dieser Satz. */
    anzahlungSpaeter: "Die Anzahlung wird aus dem Gesamtbetrag inkl. Zubuchungen berechnet und nach der Annahme angezeigt.",
    /* S0 (§ 312g Abs. 2 Nr. 9 BGB): kein Widerrufsrecht bei Bewirtung zu festem Termin — aber Hinweispflicht. */
    widerrufsHinweis: "Ein Widerrufsrecht besteht bei Bewirtung zu einem festen Termin nicht.",
    hintChooseOption: "Bitte wählen Sie zuerst eine Option aus.",
    leisteWeiter: "Weiter",
    hintAcceptAgb: "Bitte bestätigen Sie die verbindliche Annahme, um fortzufahren.",
    // § 8: paymentPathway='kostenuebernahme' - nach der Annahme entsteht (bewusst) noch KEIN
    // automatischer Zahlungslink (H-41-Konsistenz, s. Konzept-Dokument § 8) - der Kunde bekommt
    // stattdessen diesen Hinweis, das Backoffice bereitet die Kostenuebernahme vor.
    kostenuebernahmeHeading: "Kostenübernahme",
    kostenuebernahmeBody: "Vielen Dank für Ihre Annahme! Ihr Ansprechpartner bereitet die Kostenübernahme zur digitalen Unterschrift vor und sendet Ihnen den Unterschriftslink in Kürze per E-Mail.",
    footerTagline: "Premium Catering & Hospitality",
    menuHeading: "Finale Menüauswahl",
    /* Nach der Annahme stand hier nur ein Knopf - nirgends, WELCHES Menü gemeint ist
       (Antoine, 16.09.2026: "Stattdessen sollte dort das Menü angezeigt werden"). */
    menuGebuchtHeading: "Ihre gebuchte Auswahl",
    damaligeAuswahl: "Ihre damalige Auswahl",
    angenommenBannerTitel: "Vielen Dank, Ihr Angebot ist angenommen.",
    angenommenBanner: "Menü und Zubuchungen sind verbindlich gebucht.",
    angenommenBannerKu: "Verbindlich wird die Buchung, sobald die Kostenübernahme unterschrieben ist.",
    angenommenBannerZahlung: "Verbindlich wird die Buchung, sobald die Zahlung eingegangen ist.",
    angenommenKastenKu: "Angenommen. Verbindlich, sobald die Kostenübernahme unterschrieben ist.",
    angenommenKastenZahlung: "Angenommen. Verbindlich, sobald die Zahlung eingegangen ist.",
    angenommenIntro: "Hier sehen Sie noch einmal, was Sie gebucht haben.",
    menuLeer: "Für diesen Vorgang sind noch keine Speisen hinterlegt.",
    menuBody: "Bitte bestätigen Sie kurz Ihre finale Menüauswahl, damit wir alles rechtzeitig für Sie vorbereiten können.",
    menuCta: "Menü verbindlich bestätigen",
    menuConfirmedAt: (date: string) => `Menü bestätigt am ${date}`,
    depositHeading: "Anzahlung",
    /* Deckt die Zahlung die GANZE Angebotssumme, ist es keine Anzahlung mehr (16.09.2026,
       Antoine: "einen Stripe Link ohne Anzahlung mit der gesamten Summe"). Eigene Texte statt
       einer Zahl in einem Satz, der etwas anderes behauptet. */
    vollzahlungHeading: "Zahlung",
    vollzahlungBody: (amount: string) => `Zur Bestätigung Ihrer Buchung bitten wir um die Zahlung des Gesamtbetrags von ${amount}.`,
    vollzahlungCta: "Jetzt bezahlen",
    vollzahlungPaid: (amount: string) => `Zahlung über ${amount} erhalten – vielen Dank!`,
    depositBody: (amount: string) => `Zur Bestätigung Ihrer Buchung bitten wir um eine Anzahlung von ${amount}.`,
    depositCta: "Jetzt Anzahlung bezahlen",
    depositPaid: (amount: string) => `Anzahlung über ${amount} erhalten – vielen Dank!`,
    depositRestOffen: (offen: string) => `Nach einer Rückerstattung sind noch ${offen} offen. Bei Fragen wenden Sie sich bitte an Ihren Ansprechpartner.`,
    depositNoLink: "Die Zahlungsinformationen erhalten Sie in Kürze separat von Ihrem Ansprechpartner.",
    restHeading: "Restbetrag offen",
    restBody: (offen: string) => `Für Ihre Veranstaltung sind noch ${offen} offen.`,
    restCta: "Restbetrag bezahlen",
    restInfo: "Restbetrag",
    /* E3 (08.10.2026): Anzahlung als Netto/MwSt/Brutto — Beträge vom Server (deposit.aufschluesselung). */
    betragNetto: "Netto",
    betragMwst: (satz: string) => `MwSt. ${satz} %`,
    betragBrutto: "Brutto",
    /*
     * WARTESCHLANGE Punkt 49c (Antoine, 18.09.2026): „Ich möchte auch nicht, dass ein
     * Zahlungslink überhaupt angefordert werden muss. Da muss ein Stripe Button sein. Wenn ich bei
     * Amazon bezahlen möchte, muss ich doch nicht einen Zahlungslink anfordern."
     *
     * Der Knopf heisst deshalb genau wie im Normalfall („Jetzt bezahlen" / „Jetzt Anzahlung
     * bezahlen", s. `vollzahlungCta`/`depositCta` oben) — der Gast soll gar nicht merken, dass
     * hinter den Kulissen erst noch ein Link entsteht. Das Wort „Zahlungslink" kommt auf dieser
     * Seite nicht mehr vor; es beschreibt unsere Technik, nicht seine Absicht.
     */
    depositRetryBusy: "Zahlung wird vorbereitet …",
    depositRetryError: "Die Zahlung konnte gerade nicht gestartet werden. Bitte versuchen Sie es in ein paar Minuten erneut oder wenden Sie sich an Ihren Ansprechpartner.",
    depositKeineOffen: "Für diese Buchung ist keine Anzahlung mehr offen.",
    // WARTESCHLANGE Punkt 26 (Antoine, 15./16.09.2026, HAT PRIO): die Rechnung zur Anzahlung
    // erscheint direkt neben dem Zahlungslink, sobald sie erzeugt wurde.
    invoiceCta: "Rechnung ansehen",
    invoiceCtaWithNumber: (num: string) => `Rechnung ${num} ansehen`,
    expiredHeading: "Dieses Angebot ist abgelaufen",
    expiredBody: "Die Gültigkeitsfrist dieses Angebots ist verstrichen. Gerne erstellen wir Ihnen ein aktualisiertes Angebot – bitte kontaktieren Sie uns einfach.",
    errExpired: "Dieses Angebot ist inzwischen abgelaufen und kann nicht mehr angenommen werden.",
    errConflict: "Dieses Angebot wurde soeben bereits verbindlich angenommen. Bitte laden Sie die Seite neu.",
    errAlreadyDeclined: "Dieses Angebot wurde bereits abgesagt.",
    fassungAktualisiert: "Dieses Angebot wird gerade aktualisiert. Sie erhalten in Kürze die neue Fassung.",
    // Fund D (20.09.2026): der Betreiber hat zwischenzeitlich geaendert und neu versendet.
    errChanged: "Das Angebot wurde inzwischen aktualisiert. Wir haben den neuen Stand geladen – bitte prüfen Sie ihn und nehmen Sie dann an.",
    // sql/565 (R1): es liegt bereits eine gültige, unterschriebene Kostenübernahme vor (Vorgang
    // "für Änderung geöffnet", V1 gilt nach dem Leitprinzip weiter) — Annehmen/Ablehnen ist hier
    // gesperrt, die Änderung läuft nur über den Betrieb.
    errGueltigeKue: "Für diese Veranstaltung liegt bereits eine unterschriebene Kostenübernahme vor. Bitte wenden Sie sich an Ihren Ansprechpartner.",
    // E4.6: Ablehnen-Weg (vom Betrieb an-/abschaltbar). Bewusst zurueckhaltend formuliert —
    // der Weg soll ehrlich moeglich sein, nicht zum Absagen einladen.
    declineToggle: "Das Angebot passt nicht? Hier können Sie absagen.",
    declineHeading: "Angebot absagen",
    declineBody: "Das ist völlig in Ordnung. Wenn Sie mögen, verraten Sie uns kurz den Grund – das hilft uns, künftige Angebote besser zu machen.",
    declinePlaceholder: "Grund (optional) – z. B. zu teuer, Termin hat sich geändert …",
    declineCta: "Verbindlich absagen",
    declineCancel: "Zurück zum Angebot",
    declineSubmitting: "Wird gesendet …",
    declinedTitle: "Vielen Dank für Ihre Rückmeldung",
    declinedBody: "Wir haben Ihre Absage erhalten. Falls sich etwas ändert oder wir Ihnen ein angepasstes Angebot erstellen dürfen, melden Sie sich jederzeit gerne.",
    // K5.2 (E-K11): Änderungswunsch — Wording von Antoine WÖRTLICH abgenommen (Formular 05.08.2026).
    // Gleiche Zurückhaltung wie der Ablehnen-Weg: ehrlich möglich, keine Einladung.
    wunschToggle: "Sie wünschen sich Anpassungen? Änderungswunsch senden.",
    wunschHeading: "Was dürfen wir anpassen?",
    wunschBody: "Beschreiben Sie kurz, was wir ändern sollen – z. B. Menüwahl, Budget oder Gästezahl. Wir melden uns zeitnah mit einem überarbeiteten Vorschlag.",
    wunschPlaceholder: "Ihr Änderungswunsch …",
    wunschCta: "Änderungswunsch senden",
    wunschSubmitting: "Wird gesendet …",
    wunschDanke: "Vielen Dank! Wir prüfen Ihren Wunsch und melden uns mit einem angepassten Angebot.",
    wunschErr: "Der Änderungswunsch konnte nicht gesendet werden. Bitte erneut versuchen.",
    // CX-Audit 2026-07-17: Fehler-/Ablauf-Karten sagten „kontaktieren Sie uns", boten aber
    // keinerlei Kontaktweg — der Chat ist der einzige loginfreie Kanal auf derselben Domain.
    contactCta: "Nachricht über den Chat senden",
    allergenHeading: "Allergene (LMIV)",
    allergenNone: "ohne deklarationspflichtige Allergene",
    allergenOnRequest:
      "Zu einzelnen Positionen liegen noch keine Allergenangaben vor – Informationen zu Allergenen und Unverträglichkeiten erhalten Sie jederzeit auf Anfrage.",
  },
  en: {
    heroLabel: "Your personal offer",
    guestsSuffix: (n: number) => `${n} guests`,
    validUntil: (date: string) => `valid until ${date}`,
    intro: (dateText: string, guestText: string) => `We are delighted to present the following offer${dateText}${guestText} to you.`,
    introFor: (anlass: string, date: string | null) => ` for ${anlass}${date ? ` on ${date}` : ""}`,
    doneTitle: "It's official!",
    doneBody: (anlass: string) => `We're looking forward to ${anlass} and will be in touch shortly with the next steps.`,
    fastFixTitle: "Almost official!",
    fastFixBody: (anlass: string, vollzahlung: boolean, kostenuebernahme: boolean) =>
      `${
        kostenuebernahme
          ? "As soon as the cost acceptance is in"
          : vollzahlung
            ? "As soon as your payment has arrived"
            : "As soon as your deposit has arrived"
      }, your date is firmly reserved. We're looking forward to ${anlass}!`,
    erstattetTitle: "Amount refunded",
    erstattetBody: "The amount you paid has been refunded in full. No payment is outstanding.",
    perPerson: "per person",
    total: "total",
    selectedBadge: "Selected",
    recommendedBadge: "Our recommendation",
    zusatzWeitere: (n: number) => `+ ${n} more ${n === 1 ? "item" : "items"}`,
    optionalTag: "optional — not included in the total",
    surchargeTag: "charged in addition to the flat rate",
    teilgruppe: (n: number, name: string, preis: string) => `${n} × ${name} at ${preis} per guest`,
    wahlHinweis: "Choose one of these dishes:",
    wahlAufforderung: "Please choose one dish:",
    wahlBlockHeading: "Your choice from the menu",
    addonHeading: "Add if you wish",
    addonIntro: "These services are not part of the offer price. It is up to you whether to add them.",
    addonPerPerson: (preis: string, gesamt: string) => `${preis} per person · ${gesamt} in total`,
    addonJeStueck: (preis: string) => `${preis} each`,
    addonZeilensumme: (menge: number, preis: string, summe: string) => `${menge} × ${preis} = ${summe}`,
    addonMenge: "Quantity",
    addonGebucht: "bindingly booked",
    addonNichtGebucht: "not booked",
    addonSummeAngebot: "Offer",
    addonSummeZubuchungen: "Add-ons",
    addonSummeGesamt: "Total",
    wahlGespeichert: "Your choice has been saved.",
    wahlFehler: "Your choice could not be saved. Please try again.",
    wahlOffen: (n: number) => (n === 1 ? "One choice is still open." : `${n} choices are still open.`),
    forGuests: (n: number) => `for ${n} guests`,
    printCta: "Download as PDF / Print",
    pdfCta: "Offer as PDF",
    pdfCtaNr: (n: number) => `Offer as PDF (version ${n})`,
    printHint: "Opens the print view — from there you can also save the offer as a PDF.",
    notesHeading: "Any comments or requests?",
    notesPlaceholder: "Feel free to share special requests, allergies, or questions …",
    billingHeading: "Billing address",
    billingNameLabel: "Name",
    billingCompanyLabel: "Company (if applicable)",
    billingStreetLabel: "Street and house number",
    billingZipLabel: "Postal code",
    billingCityLabel: "City",
    billingAdditionLabel: "Address line 2 (optional)",
    billingCountryLabel: "Country",
    billingPhoneLabel: "Phone number",
    billingPhoneLabelHandynummer: "Mobile number",
    handynummerGrund: "Please provide a mobile number: a code will be sent there by SMS to sign.",
    kuHeading: "Sign a cost acceptance",
    kuIntro: "Instead of accepting here, you can sign a cost acceptance electronically. It counts as a binding confirmation.",
    kuSignerHeading: "Who signs?",
    kuSignerHint: "Leave empty if you sign yourself. If someone orders on behalf of another person — an assistant for the management, say — enter the signing person's details here.",
    kuSignerName: "Name of the signing person",
    kuSignerEmail: "Email of the signing person",
    kuSignerCompany: "Company of the signing person",
    kuAgbLabel: "I have read and accept the terms and conditions.",
    kuStornoLabel: "I have read and accept the cancellation terms.",
    kuCta: "Sign cost acceptance with obligation to pay",
    kuBusy: "Preparing …",
    kuHintPflicht: "Please complete the address and mobile number, and tick both confirmations.",
    kuBereitHeading: "Your cost acceptance is ready",
    kuBereitBody: "The link takes you to the signature. You will receive the code by SMS.",
    kuBereitCta: "Sign now",
    kuSigniert: (datum: string) => `Cost acceptance signed on ${datum}.`,
    kuErsetztHeading: "Your updated cost acceptance is ready",
    kuErsetzt: (datum: string) => `Updated version – replaces your cost acceptance dated ${datum}. Please sign so that we can put the requested changes into effect.`,
    kuFehler: "The signature could not be prepared right now. Please try again shortly.",
    billingRefLabel: "Reference for your accounting (e.g. event/order number, cost center) — optional",
    hintBillingIncomplete: "Please provide a complete billing address and phone number.",
    requiredMarker: "* Required (name or company, address, phone number)",
    requiredMarkerHandynummer: "* Required (name or company, address, mobile number)",
    agbLabel: "I bindingly accept this offer and agree to the stated payment terms.",
    agbLabelMitAgb: "I bindingly accept this offer and agree to the general terms and conditions as well as the stated payment and cancellation terms.",
    agbAnzeigen: "Show general terms and conditions",
    agbAusblenden: "Hide general terms and conditions",
    agbHeading: "General terms and conditions",
    paymentHeading: "Payment & cancellation terms",
    stornoHeading: "Cancellation terms",
    vorschauCta: (amount: string) => `Pay now · ${amount}`,
    vorschauArtAnzahlung: "Deposit",
    vorschauArtVollzahlung: "Payment of the full amount",
    vorschauHinweis: "Please accept the offer first.",
    ctaSubmitting: "Sending …",
    ctaAccept: "Accept offer with obligation to pay",
    ctaAcceptDirectStripe: "Accept offer with obligation to pay & pay now",
    ctaAcceptDirectKostenuebernahme: "Accept offer with obligation to pay & start cost acceptance",
    summaryHeading: "Your booking at a glance",
    summaryDatum: "Date",
    summaryAnzahlung: "Deposit after acceptance",
    summaryVollzahlung: "Payment after acceptance",
    anzahlungSpaeter: "The deposit is calculated from the total including add-ons and shown after acceptance.",
    widerrufsHinweis: "There is no right of withdrawal for catering services on a fixed date.",
    hintChooseOption: "Please select an option first.",
    leisteWeiter: "Continue",
    hintAcceptAgb: "Please confirm the binding acceptance to proceed.",
    kostenuebernahmeHeading: "Cost acceptance",
    kostenuebernahmeBody: "Thank you for accepting! Your contact person is preparing the cost acceptance for digital signature and will send you the signing link shortly by email.",
    footerTagline: "Premium Catering & Hospitality",
    menuHeading: "Final menu selection",
    menuGebuchtHeading: "Your booked selection",
    damaligeAuswahl: "Your earlier selection",
    angenommenBannerTitel: "Thank you, your offer is accepted.",
    angenommenBanner: "Menu and add-ons are booked and binding.",
    angenommenBannerKu: "The booking becomes binding once the cost acceptance is signed.",
    angenommenBannerZahlung: "The booking becomes binding once the payment has been received.",
    angenommenKastenKu: "Accepted. Binding once the cost acceptance is signed.",
    angenommenKastenZahlung: "Accepted. Binding once the payment has been received.",
    angenommenIntro: "Here is what you have booked.",
    menuLeer: "No dishes have been added to this booking yet.",
    menuBody: "Please take a moment to confirm your final menu selection so we can prepare everything for you in time.",
    menuCta: "Confirm menu",
    menuConfirmedAt: (date: string) => `Menu confirmed on ${date}`,
    depositHeading: "Deposit",
    vollzahlungHeading: "Payment",
    vollzahlungBody: (amount: string) => `To confirm your booking, we kindly ask for payment of the full amount of ${amount}.`,
    vollzahlungCta: "Pay now",
    vollzahlungPaid: (amount: string) => `Payment of ${amount} received – thank you!`,
    depositBody: (amount: string) => `To confirm your booking, we kindly ask for a deposit of ${amount}.`,
    depositCta: "Pay deposit now",
    depositPaid: (amount: string) => `Deposit of ${amount} received – thank you!`,
    depositRestOffen: (offen: string) => `Following a refund, ${offen} is still outstanding. If you have any questions, please contact your contact person.`,
    depositNoLink: "You will receive the payment details separately from your contact person shortly.",
    restHeading: "Outstanding balance",
    restBody: (offen: string) => `${offen} is still outstanding for your event.`,
    restCta: "Pay outstanding balance",
    restInfo: "Outstanding balance",
    betragNetto: "Net",
    betragMwst: (satz: string) => `VAT ${satz} %`,
    betragBrutto: "Gross",
    // Punkt 49c — wie oben: kein „payment link", der Knopf heisst wie im Normalfall.
    depositRetryBusy: "Preparing payment …",
    depositRetryError: "The payment could not be started right now. Please try again in a few minutes or contact your contact person.",
    depositKeineOffen: "There is no deposit outstanding for this booking any more.",
    invoiceCta: "View invoice",
    invoiceCtaWithNumber: (num: string) => `View invoice ${num}`,
    expiredHeading: "This offer has expired",
    expiredBody: "The validity period of this offer has passed. We would be happy to prepare an updated offer for you – please just get in touch.",
    errExpired: "This offer has since expired and can no longer be accepted.",
    errConflict: "This offer has just been bindingly accepted. Please reload the page.",
    errAlreadyDeclined: "This offer has already been declined.",
    fassungAktualisiert: "This offer is currently being updated. You will receive the new version shortly.",
    errChanged: "This offer has been updated in the meantime. We have loaded the new version – please review it, then accept.",
    // sql/565 (R1): a valid, signed cost acceptance already exists for this event.
    errGueltigeKue: "A signed cost acceptance already exists for this event. Please contact your contact person.",
    declineToggle: "This offer does not fit? You can decline here.",
    declineHeading: "Decline offer",
    declineBody: "That is perfectly fine. If you like, let us know the reason – it helps us make future offers better.",
    declinePlaceholder: "Reason (optional) – e.g. too expensive, date changed …",
    declineCta: "Decline bindingly",
    declineCancel: "Back to the offer",
    declineSubmitting: "Sending …",
    declinedTitle: "Thank you for letting us know",
    declinedBody: "We have received your cancellation. If anything changes or you would like an adjusted offer, please feel free to get in touch at any time.",
    // K5.2 (E-K11): change request — sinngemäße Übersetzung des abgenommenen DE-Wordings.
    wunschToggle: "You would like adjustments? Send a change request.",
    wunschHeading: "What may we adjust?",
    wunschBody: "Briefly describe what you would like us to change – e.g. menu choice, budget, or number of guests. We will get back to you shortly with a revised proposal.",
    wunschPlaceholder: "Your change request …",
    wunschCta: "Send change request",
    wunschSubmitting: "Sending …",
    wunschDanke: "Thank you! We will review your request and get back to you with an adjusted offer.",
    wunschErr: "Your change request could not be sent. Please try again.",
    contactCta: "Send us a message via chat",
    allergenHeading: "Allergens (EU FIC)",
    allergenNone: "no declarable allergens",
    allergenOnRequest:
      "Allergen details for some items are not yet on file – information on allergens and intolerances is available on request at any time.",
  },
} as const;

/** Passendes Material-Symbol je Menue-Rubrik (rein dekorativ, Fallback: restaurant). */
function sectionIcon(section: string): string {
  const s = section.toLowerCase();
  if (s.includes("vorspeise") || s.includes("suppe") || s.includes("gruß") || s.includes("starter") || s.includes("soup")) return "soup_kitchen";
  if (s.includes("haupt") || s.includes("main")) return "set_meal";
  if (s.includes("dessert") || s.includes("nachspeise") || s.includes("süß") || s.includes("sweet")) return "icecream";
  if (s.includes("getränk") || s.includes("wein") || s.includes("bar") || s.includes("aperitif") || s.includes("drink") || s.includes("wine"))
    return "wine_bar";
  if (s.includes("zwischen") || s.includes("interm")) return "local_dining";
  return "restaurant";
}

// Gänge/Alternativen/Wahlgruppen wohnen seit der Zubuch-Auswahl im Modell (gemeinsam mit der Rechnung der Seite).
export { gaengeAus, alternativenAus, offeneWahlgruppen };

/**
 * Punkt 57 Etappe 3 (sql/452): was `GET /api/public/offer/:token/kostenuebernahme` liefert.
 *
 * `freigegeben` heißt: der Betreiber hat erlaubt, dass der Gast auslöst, UND die Erlaubnis ist
 * noch nicht verbraucht. Ein Dauerrecht wäre etwas anderes als das, worum Antoine gebeten hat.
 */
export interface KostenuebernahmeStatus {
  freigegeben: boolean;
  esign_bereit: boolean;
  status: "keine" | "angefordert" | "signiert";
  sign_url: string | null;
  signiert_am: string | null;
  /** KÜ V2 (S2, sql/566): offene geänderte Fassung — Unterschriftsdatum der Kostenübernahme, die bis dahin gilt. */
  ersetzt_signiert_am?: string | null;
  kunde?: { name?: string | null; company?: string | null; email?: string | null; phone?: string | null } | null;
}


/**
 * Die Menüwahl des Gastes — Gänge, Alternativen, Auswahlknöpfe.
 *
 * Eine Komponente für ZWEI Stellen: unter den Variantenkarten (vor der Zusage) und im gebuchten
 * Menü (danach). Antoine, 19.09.2026, mit Bildschirmfoto: „ich habe eine oder Version in diesem
 * Menü und ich sehe es im Front end auf der Kunden Seite nicht […] hier müsste ich jetzt zwischen
 * den oder Varianten wählen können."
 *
 * Meine ursprüngliche Einschränkung — Wahl erst NACH der Zusage, weil vorher nicht feststehe,
 * welche Variante gilt — war für den häufigsten Fall falsch: bei einer einzigen Variante steht es
 * fest, und auch bei mehreren ist die gerade angesehene gemeint. Der Gast wählt sein Gericht,
 * während er das Menü liest, nicht in einem zweiten Schritt danach.
 *
 * Die Wahl hängt am Vorgang, nicht an der Variante (der Server prüft nur, dass die Position zu
 * diesem Vorgang und zu genau dieser Wahlgruppe gehört) — ein Variantenwechsel macht eine bereits
 * getroffene Wahl also nicht ungültig, solange dieselbe Gruppe darin vorkommt.
 */
function MenuWahl({
  speisen,
  menuWahl,
  busyGruppe,
  vorschau,
  onWahl,
  texte,
  angenommeneMengen,
}: {
  speisen: PublicOfferItem[];
  menuWahl: Record<string, string>;
  busyGruppe: string | null;
  vorschau: boolean;
  onWahl: (gruppeId: string, itemId: string) => void;
  texte: { wahlHinweis: string; wahlAufforderung: string };
  /** Angenommene Fassung mit Gäste-Zuordnung (sql/752): statt Auswahlknöpfen steht „15 × Gericht“. */
  angenommeneMengen?: Record<string, Record<string, number>> | null;
}) {
  const zeilen = speisen.map((it) => ({ ...it, courseLabel: it.course_label, choiceGroupId: it.choice_group_id ?? null }));
  return (
    <div className="mt-3 space-y-3">
      {gaengeAus(zeilen).map((gang, gi) => (
        <div key={`${gang.label ?? "ohne"}-${gi}`}>
          {gang.label && (
            <h4 className="font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">{gang.label}</h4>
          )}
          {alternativenAus(gang.lines).map((block, bi) => {
            const gruppeId = block.alternativen ? (block.lines[0]?.choiceGroupId ?? null) : null;
            const gewaehlt = gruppeId ? menuWahl[gruppeId] : undefined;
            return (
              <div key={bi} className={block.alternativen ? "mt-1.5 border-l-2 border-outline-variant pl-3 dark:border-outline/40" : ""}>
                {block.alternativen && (
                  <p className="font-label-md text-label-md italic text-secondary dark:text-secondary-fixed-dim">
                    {gruppeId ? texte.wahlAufforderung : texte.wahlHinweis}
                  </p>
                )}
                {gruppeId && angenommeneMengen?.[gruppeId] ? (
                  <ul className="mt-1 space-y-2" data-testid={`menu-mengen-${gruppeId}`}>
                    {block.lines
                      .filter((it) => (angenommeneMengen[gruppeId]?.[it.id] ?? 0) > 0)
                      .map((it) => (
                        <li key={it.id} className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
                          <span className="font-semibold">
                            {angenommeneMengen[gruppeId]?.[it.id]} × {it.name}
                          </span>
                          {it.description && <span className="mt-0.5 block text-secondary dark:text-secondary-fixed-dim">{it.description}</span>}
                        </li>
                      ))}
                  </ul>
                ) : gruppeId ? (
                  <fieldset className="mt-1 space-y-2" disabled={vorschau || busyGruppe === gruppeId}>
                    {block.lines.map((it) => (
                      <label
                        key={it.id}
                        title={vorschau ? VORSCHAU_DEAKTIVIERT : undefined}
                        className="flex cursor-pointer items-start gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface"
                      >
                        <Radio
                          name={`menu-wahl-${gruppeId}`}
                          value={it.id}
                          checked={gewaehlt === it.id}
                          onChange={() => onWahl(gruppeId, it.id)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-semibold">{it.name}</span>
                          {it.description && <span className="mt-0.5 block text-secondary dark:text-secondary-fixed-dim">{it.description}</span>}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <ul className="mt-1 space-y-2">
                    {block.lines.map((it) => (
                      <li key={it.id} className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
                        <span className="font-semibold">{it.name}</span>
                        {it.description && <span className="mt-0.5 block text-secondary dark:text-secondary-fixed-dim">{it.description}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Betreiber-Hinweise im Vorschau-Modus — bewusst NUR Deutsch (Produktsprache), unabhaengig von
 *  der Angebotssprache: sie richten sich an den Betreiber, nicht an den Gast. */
const VORSCHAU_DEAKTIVIERT = "In der Vorschau deaktiviert";

/**
 * sql/350/351 (Auftragsadresse): dieselbe "alle drei Felder oder keins zaehlt"-Regel wie
 * serverseitig (lib/rechnungsadresse.ts#effektiveAdresse, EINE Quelle fuer die Definition
 * "vollstaendig") - fuer die Vorbefuellung des Rechnungsadresse-Blocks hier im Client
 * nachgebildet, damit sich Anzeige und Backend nie unterscheiden. Nur die ANSCHRIFT kann
 * ueberschrieben werden, Name/Firma bleiben immer die des Kunden (bewusst nicht Teil dieser
 * Funktion).
 */
function effektiveAuftragsadresse(
  event:
    | Pick<PublicOffer["event"], "event_address_street" | "event_address_zip" | "event_address_city" | "event_address_addition" | "event_address_country_code">
    | undefined,
  customer: NonNullable<PublicOffer["customer"]> | null | undefined,
): { addressStreet: string; addressZip: string; addressCity: string; addressAddition: string; addressCountryCode: string } {
  const vollstaendig =
    Boolean(event?.event_address_street?.trim()) && Boolean(event?.event_address_zip?.trim()) && Boolean(event?.event_address_city?.trim());
  if (vollstaendig) {
    return {
      addressStreet: event!.event_address_street ?? "",
      addressZip: event!.event_address_zip ?? "",
      addressCity: event!.event_address_city ?? "",
      addressAddition: event!.event_address_addition ?? "",
      addressCountryCode: event!.event_address_country_code ?? "",
    };
  }
  return {
    addressStreet: customer?.addressStreet ?? "",
    addressZip: customer?.addressZip ?? "",
    addressCity: customer?.addressCity ?? "",
    addressAddition: customer?.addressAddition ?? "",
    addressCountryCode: customer?.addressCountryCode ?? "",
  };
}

/**
 * Die Bilanz eines Probelaufs (docs/KONZEPT-ANGEBOTSSEITE-PROBELAUF.md § 3). Sie kommt fertig
 * formuliert vom Server (`lib/probelauf.ts#baueBilanz`) — hier wird sie NUR angezeigt, nicht
 * ausgelegt. Das ist Absicht: derselbe Wortlaut muss im Chat-Werkzeug und in einem spaeteren
 * Protokoll gleich lauten, und eine zweite Formulierungsstelle hier liefe davon weg.
 */
interface ProbelaufBilanz {
  probelaufEventId: string;
  erstelltAm: string;
  ablaufAm: string;
  angebot: { eventId: string; nummer: string | null; variante: string; betragCents: number };
  schritte: {
    schluessel: string;
    titel: string;
    stand: "gelungen" | "fehlgeschlagen" | "uebersprungen";
    zeilen: { bezeichnung: string; wert: string }[];
    ursache?: string;
  }[];
  stripeUrl: string | null;
  stripeLivemode: boolean | null;
  grenzen: string[];
}

/** Betreiber-Texte des Probelaufs — bewusst nur Deutsch (Produktsprache), wie VORSCHAU_DEAKTIVIERT. */
const P = {
  starten: "Probelauf starten",
  laeuft: "Probelauf läuft …",
  entfernen: "Probelauf entfernen",
  entfernenLaeuft: "Wird entfernt …",
  einleitung:
    "Drückt „Angebot annehmen“ wie ein Gast — über denselben Weg, dieselbe Anzahlungs-Berechnung, " +
    "denselben Stripe-Aufruf. Auf einer Kopie dieses Angebots, damit der echte Vorgang unberührt bleibt.",
  offenHinweis: "Zu diesem Angebot liegt noch ein Probelauf. Bitte erst entfernen.",
  stripeOeffnen: "Stripe-Seite öffnen",
  grenzenTitel: "Was dieser Probelauf NICHT beweist",
};

export default function PublicOfferPage({ vorschau = false }: { vorschau?: boolean }) {
  // /angebot/:token (oeffentlich) bzw. /veranstaltungen/:id/angebotsseite (Vorschau, eingeloggt).
  const { token, id } = useParams<{ token: string; id: string }>();
  const [offer, setOffer] = useState<PublicOffer | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [bedingungenOffen, setBedingungenOffen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // Migration 731: die Annahme-Antwort trug `aLaCarte: true` - nichts zu zahlen, die Bestätigung sagt „fix“.
  const [aLaCarteAngenommen, setALaCarteAngenommen] = useState(false);
  const [notes, setNotes] = useState("");
  const [agbAccepted, setAgbAccepted] = useState(false);
  // Google-Bewertungsbitte (sql/542): freiwilliges Haekchen, nicht vorausgewaehlt, nicht an die Annahme gekoppelt.
  const [bewertungEinwilligung, setBewertungEinwilligung] = useState(false);
  const bewertungsbitte = offer?.bewertungsbitte ?? null;
  // Punkt 50 b2: die AGB stehen hinter einem Klick (Antoine: „Nur AGBs mit Klick zu sehen").
  const [agbOffen, setAgbOffen] = useState(false);
  // Fund D (20.09.2026): nach 409 `offer_changed` den aktuellen Stand neu laden — der Effekt unten
  // haengt an diesem Zaehler, sonst saehe der Gast weiter die veraltete Fassung.
  const [reloadKey, setReloadKey] = useState(0);
  // 13.10.2026: in der Betreiber-Vorschau steht bei der gewaehlten Variante, was schon bezahlt ist
  // und was noch offen ist (bestehende Zahlungs-API). Der Gast-Link laedt nichts davon.
  const zahlungenQ = useApi<Payment[]>(vorschau && id ? `/api/events/${id}/payments` : null);
  const zahlungen = Array.isArray(zahlungenQ.data) ? zahlungenQ.data : [];
  // sql/344 (Antoine 30.08.2026): Rechnungsadresse + Telefon werden hier miterhoben — der
  // zentrale Weg fuer den normalen Anfrage->Angebot-Pfad. Vorbefuellt aus offer.customer,
  // sobald die Daten geladen sind (Effekt unten) — bereits Vorhandenes wird nicht nochmal leer
  // abgefragt.
  // PR4 (Adressmodell, WARTESCHLANGE Punkt 7): die ganze Adresse ueber die gemeinsame
  // `AdresseFelder`-Komponente. Seit sql/485 auch Zusatz + Land: vorbefuellt aus dem Kunden (bzw.
  // der vollstaendigen Auftragsadresse), Land ohne gespeicherten Wert vorausgewaehlt „DE“.
  const [billingAdresse, setBillingAdresse] = useState<AdresseWerte>({ company: "", name: "", street: "", addition: "", zip: "", city: "", countryCode: "" });
  const [billingPhone, setBillingPhone] = useState("");
  const [billingRef, setBillingRef] = useState("");
  /*
   * Buchungsstrecke C1 (nur neue Seite, `v2`): Zwei-Schritt-Zusage. Schritt 1 = Variante bestaetigen,
   * Schritt 2 = nur die fehlenden Angaben. Der Schritt ist reine Anzeige — die Eingaben oben bleiben
   * beim Hin und Her stehen, `accept()` und seine Pflichtregeln sind dieselben wie ohne Schalter.
   * `zusageKontaktOffen`: welche Angaben in Schritt 2 als Formular statt als Zusammenfassung stehen
   * (beim Betreten festgelegt, damit ein gerade vollstaendig getipptes Feld nicht unter der Hand zuklappt).
   */
  const [zusageSchritt, setZusageSchritt] = useState<1 | 2>(1);
  const [zusageSchrittGewechselt, setZusageSchrittGewechselt] = useState(false);
  const [zusageKontaktOffen, setZusageKontaktOffen] = useState<{ adresse: boolean; telefon: boolean }>({ adresse: false, telefon: false });
  const [zusageAllesDa, setZusageAllesDa] = useState(false);
  const [kuNebenwegOffen, setKuNebenwegOffen] = useState(false);
  /**
   * Buchungsstrecke A5 (sql/585): Stationen der Buchungsstrecke messen — erste eigene
   * Variantenwahl und erstes Anfassen des Annahme-Bereichs. Fire and forget, je Art einmal,
   * nie in der Betreiber-Vorschau (lib/angebots-ereignis.ts).
   */
  const ereignisseGesendet = useRef(new Set<AngebotsEreignisArt>());
  function meldeEreignis(art: AngebotsEreignisArt) {
    meldeAngebotsEreignis(token, art, ereignisseGesendet.current, vorschau);
  }
  /**
   * Angebot · Einblicke (sql/730): die Kennung DIESES Aufrufs (aus der Antwort auf den Abruf)
   * lebt nur im Arbeitsspeicher - nichts im Browser gespeichert. Damit meldet die Seite beim
   * Verlassen Verweildauer und Lesefortschritt; `preis_gesehen` kommt aus dem Sichtbereich.
   */
  const aufrufIdRef = useRef<string | null>(null);
  useGastEinblicke({
    token,
    vorschau,
    bereit: phase === "ready" || phase === "submitting",
    aufrufId: () => aufrufIdRef.current,
    melde: meldeEreignis,
  });
  function onBillingFeldChange(feld: AdresseFeld, wert: string) {
    meldeEreignis("zusage_begonnen");
    setBillingAdresse((prev) => ({ ...prev, [feld]: wert }));
  }
  const billingComplete =
    ((billingAdresse.name ?? "").trim() !== "" || (billingAdresse.company ?? "").trim() !== "") &&
    (billingAdresse.street ?? "").trim() !== "" &&
    (billingAdresse.zip ?? "").trim() !== "" &&
    (billingAdresse.city ?? "").trim() !== "" &&
    billingPhone.trim() !== "";
  // C1: dieselbe Regel wie `billingComplete`, nur nach Adresse und Telefon getrennt (Schritt 2 fragt je Teil).
  const billingAdresseKomplett =
    ((billingAdresse.name ?? "").trim() !== "" || (billingAdresse.company ?? "").trim() !== "") &&
    (billingAdresse.street ?? "").trim() !== "" &&
    (billingAdresse.zip ?? "").trim() !== "" &&
    (billingAdresse.city ?? "").trim() !== "";
  const [error, setError] = useState<string | null>(null);
  // WARTESCHLANGE Punkt 9 (Antoine, 14.09.2026): eigener Busy-/Fehlerzustand fuer den
  // Zahlungslink-Retry - bewusst getrennt von `error` oben (der gehoert zur Annahme-CTA, die
  // auf der "done"-Karte nicht mehr sichtbar ist).
  const [depositRetryBusy, setDepositRetryBusy] = useState(false);
  const [depositRetryError, setDepositRetryError] = useState<string | null>(null);
  /** Der Server sagt: hier ist keine Anzahlung mehr offen (storniert/Konditionen geaendert). */
  const [depositKeineOffen, setDepositKeineOffen] = useState(false);
  // E4.6: Ablehnen-Weg (nur wenn der Betrieb ihn anbietet, offer.ablehnenEnabled).
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineGrund, setDeclineGrund] = useState("");
  const [declining, setDeclining] = useState(false);
  // K5.2 (E-K11): Änderungswunsch — läuft serverseitig in DIESELBE v2-Pipeline wie eine Mail-Antwort.
  const [wunschOpen, setWunschOpen] = useState(false);
  const [wunschText, setWunschText] = useState("");
  const [wunschSending, setWunschSending] = useState(false);
  const [wunschDone, setWunschDone] = useState(false);
  // Buchungsstrecke B2: derselbe Nachrichtenweg traegt auf der neuen Seite auch die „Frage an …“ —
  // nur Ueberschrift und Texte unterscheiden sich, der Versand (POST …/aenderungswunsch) ist derselbe.
  const [wunschArt, setWunschArt] = useState<"wunsch" | "frage">("wunsch");
  // Probelauf (docs/KONZEPT-ANGEBOTSSEITE-PROBELAUF.md) — nur im Vorschau-Modus.
  const [probelaufBusy, setProbelaufBusy] = useState(false);
  const [probelaufBilanz, setProbelaufBilanz] = useState<ProbelaufBilanz | null>(null);
  const [probelaufFehler, setProbelaufFehler] = useState<string | null>(null);
  const [probelaufOffen, setProbelaufOffen] = useState(false);
  /*
   * Punkt 57 Etappe 3 (sql/452): der Gast fordert die Kostenübernahme selbst an.
   *
   * Antoine, 18.09.2026: „Darf der Gast das mehrfach auslösen? Nur wenn der Betreiber es
   * freigibt" · „Er muss AGB und Stornobedingungen separat anklicken und akzeptieren" ·
   * „Meistens bestellt z. B. die Sekretärin und der Chef muss unterschreiben."
   *
   * Die Rechnungsdaten kommen aus dem Block darüber, der sie ohnehin erhebt (sql/344) — ein
   * zweiter Satz derselben Felder wäre für den Gast dieselbe Arbeit zweimal. Neu sind hier nur
   * die unterzeichnende Person und die beiden getrennten Bestätigungen.
   */
  const [kuStatus, setKuStatus] = useState<KostenuebernahmeStatus | null>(null);
  const [kuSignerName, setKuSignerName] = useState("");
  const [kuSignerEmail, setKuSignerEmail] = useState("");
  const [kuSignerCompany, setKuSignerCompany] = useState("");
  const [kuAgb, setKuAgb] = useState(false);
  const [kuStorno, setKuStorno] = useState(false);
  const [kuBusy, setKuBusy] = useState(false);
  const [kuFehler, setKuFehler] = useState<string | null>(null);
  const [kuSignUrl, setKuSignUrl] = useState<string | null>(null);

  /**
   * Löst die Kostenübernahme aus. Die Antwort trägt den Signaturlink — bewusst KEINE Mail: der
   * Gast steht gerade auf dieser Seite, und ein Medienbruch mitten im Vorgang kostet Abschlüsse.
   */
  async function kostenuebernahmeAusloesen() {
    if (vorschau || !token || kuBusy) return;
    setKuBusy(true);
    setKuFehler(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/kostenuebernahme`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          rechnungName: billingAdresse.name,
          rechnungFirma: billingAdresse.company,
          strasse: billingAdresse.street,
          plz: billingAdresse.zip,
          ort: billingAdresse.city,
          ...(billingAdresse.addition?.trim() ? { zusatz: billingAdresse.addition.trim() } : {}),
          ...(billingAdresse.countryCode ? { land: billingAdresse.countryCode } : {}),
          telefon: billingPhone.trim(),
          ...(kuSignerName.trim() ? { unterzeichnerName: kuSignerName.trim() } : {}),
          ...(kuSignerEmail.trim() ? { unterzeichnerEmail: kuSignerEmail.trim() } : {}),
          ...(kuSignerCompany.trim() ? { unterzeichnerFirma: kuSignerCompany.trim() } : {}),
          agb: true,
          storno: true,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { data?: { signUrl?: string }; message?: string };
      if (!res.ok || !body.data?.signUrl) {
        // Die Meldung des Servers hat Vorrang: sie sagt, WAS fehlt (etwa unvollständige
        // Vertragsangaben) — ein allgemeiner Satz ließe den Gast raten.
        setKuFehler(body.message ?? t.kuFehler);
        return;
      }
      setKuSignUrl(body.data.signUrl);
      setKuStatus((prev) => (prev ? { ...prev, status: "angefordert", sign_url: body.data!.signUrl! } : prev));
    } catch {
      setKuFehler(t.kuFehler);
    } finally {
      setKuBusy(false);
    }
  }
  /*
   * Punkt 61 (Antoine, 19.09.2026): „Der Gast muss diese dann auch wählen können mit einem Radio
   * Button." Die Wahl je Wahlgruppe — `{ "<choice_group_id>": "<offer_item_id>" }`, gespiegelt aus
   * `offer.menu_wahl` (Migration 451).
   *
   * Gespeichert wird SOFORT beim Klick, nicht über einen „Speichern"-Knopf: eine Wahl, die der Gast
   * getroffen und dann weggeklickt hat, waere sonst verloren, ohne dass er es merkt. Deshalb auch
   * optimistisch mit Rollback — der Knopf springt zurueck, wenn der Server sie nicht annimmt,
   * statt eine Wahl anzuzeigen, die nirgends steht.
   */
  const [menuWahl, setMenuWahl] = useState<Record<string, string>>({});
  const [wahlBusyGruppe, setWahlBusyGruppe] = useState<string | null>(null);
  const [wahlFehler, setWahlFehler] = useState(false);

  /*
   * ═══ Punkt 32: was der Gast dazubuchen möchte ═══
   *
   * `{ "<offer_item_id>": <menge> }`, NUR im Browser, bis die Annahme abgeschickt wird — anders
   * als die Menüwahl, die sofort gespeichert wird. Der Unterschied ist Absicht: die Menüwahl
   * kostet nichts, eine Zubuchung kostet Geld. Sie darf erst in dem Moment verbindlich werden, in
   * dem der Gast die Annahme mit der Gesamtsumme vor Augen bestätigt — vorher wäre jeder Klick
   * eine Bestellung, die er nie als solche gemeint hat.
   *
   * Nach der Annahme kommt die Wahl nicht mehr von hier, sondern aus `chosen_addons` der Fassung
   * (sql/480) — die übersteht jeden Reload.
   */
  const [addonWahl, setAddonWahl] = useState<Record<string, number>>({});

  /*
   * ═══ Gäste je Gericht bei Wahlgängen („oder“) ═══
   *
   * `{ "<wahlgruppe>": { "<position>": <anzahl gäste> } }`. Anders als `addonWahl` kostet das nichts und
   * wird deshalb schon beim Eingeben gespeichert (entprellt, `POST …/menu-mengen`); die Annahme prüft der
   * Server noch einmal: je Wahlgang muss die Summe genau den Gästen mit Hauptmenü entsprechen.
   */
  const [mengen, setMengen] = useState<Mengen>({});
  const [mengenFehler, setMengenFehler] = useState(false);
  const [fehltZeigen, setFehltZeigen] = useState(false);
  const [blattOffen, setBlattOffen] = useState(false);
  const mengenZeitgeber = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mengenLetzte = useRef<Mengen | null>(null);

  async function speichereMengen(neu: Mengen): Promise<boolean> {
    if (vorschau || !token) return true;
    try {
      const res = await fetch(`/api/public/offer/${token}/menu-mengen`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ wahl: neu }),
      });
      if (!res.ok) throw new Error("menu_mengen_failed");
      setMengenFehler(false);
      if (mengenLetzte.current === neu) mengenLetzte.current = null;
      return true;
    } catch {
      setMengenFehler(true);
      return false;
    }
  }
  function setzeMenge(gruppeId: string, itemId: string, n: number) {
    // Vorschau des Betriebs (Antoine nimmt die Seite darüber ab): alles bedienbar, aber NUR im Browser - kein Gast-Token,
    // nichts wird gespeichert.
    if (!vorschau && !token) return;
    const neu: Mengen = { ...mengen, [gruppeId]: { ...(mengen[gruppeId] ?? {}), [itemId]: n } };
    setMengen(neu);
    setFehltZeigen(false);
    if (vorschau) return;
    mengenLetzte.current = neu;
    if (mengenZeitgeber.current) clearTimeout(mengenZeitgeber.current);
    mengenZeitgeber.current = setTimeout(() => {
      mengenZeitgeber.current = null;
      void speichereMengen(neu);
    }, 400);
  }
  /** Vor der Annahme: ausstehende Zuordnung sofort speichern (der Server liest sie bei der Annahme). */
  async function mengenJetztSpeichern(): Promise<boolean> {
    if (mengenZeitgeber.current) {
      clearTimeout(mengenZeitgeber.current);
      mengenZeitgeber.current = null;
    }
    const offen = mengenLetzte.current;
    return offen ? await speichereMengen(offen) : true;
  }

  async function waehleGericht(gruppeId: string, itemId: string) {
    // Vorschau schreibt nie in echte Daten (wie alle anderen Wege auf dieser Seite auch): die Wahl bleibt im Browser.
    if (vorschau) {
      setMenuWahl((vorher) => ({ ...vorher, [gruppeId]: itemId }));
      return;
    }
    if (!token) return;
    if (menuWahl[gruppeId] === itemId) return;
    const vorher = menuWahl;
    const neu = { ...menuWahl, [gruppeId]: itemId };
    setMenuWahl(neu);
    setWahlBusyGruppe(gruppeId);
    setWahlFehler(false);
    try {
      const res = await fetch(`/api/public/offer/${token}/menu-wahl`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ wahl: neu }),
      });
      if (!res.ok) throw new Error("menu_wahl_failed");
      setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, menu_wahl: neu } } : prev));
    } catch {
      setMenuWahl(vorher);
      setWahlFehler(true);
    } finally {
      setWahlBusyGruppe(null);
    }
  }

  useEffect(() => {
    if (vorschau ? !id : !token) return;
    (async () => {
      try {
        // Vorschau: authentifiziert aus den Live-Tabellen (jeder Status) statt vom Public-Token.
        const res = vorschau ? await apiFetch(`/api/events/${id}/angebots-vorschau`) : await fetch(`/api/public/offer/${token}`);
        if (!res.ok)
          throw new Error(
            res.status === 404
              ? vorschau
                ? "Dieser Vorgang wurde nicht gefunden."
                : "Dieses Angebot ist nicht (mehr) verfügbar."
              : "Fehler beim Laden.",
          );
        const body = (await res.json().catch(() => {
          throw new Error("Fehler beim Laden. Bitte versuchen Sie es später erneut.");
        })) as { data: PublicOffer };
        setOffer(body.data);
        aufrufIdRef.current = !vorschau && typeof body.data.aufrufId === "string" ? body.data.aufrufId : null;
        // Punkt 61: eine bereits getroffene Wahl bleibt beim naechsten Aufruf der Seite stehen.
        setMenuWahl(body.data.event.menu_wahl ?? {});
        setMengen(body.data.event.menu_mengen ?? {});
        // sql/344: bereits vorhandene Kundenwerte vorbefuellen, statt sie nochmal leer abzufragen.
        // sql/350/351: die Anschrift (nicht Name/Firma/Telefon) weicht davon ab, sobald eine
        // VOLLSTAENDIGE Auftragsadresse gesetzt ist - effektiveAuftragsadresse() bildet dieselbe
        // Regel wie serverseitig nach (KEINE Logik-Abweichung Client/Server).
        const cust = body.data.customer;
        // Fund D: beim Neuladen nach `offer_changed` bleibt stehen, was der Gast schon eingetippt
        // hat — nur der ERSTE Aufruf befuellt aus den Kundendaten vor.
        if (reloadKey === 0) {
          if (cust) {
            setBillingPhone(cust.phone ?? "");
          }
          const adresse = effektiveAuftragsadresse(body.data.event, cust);
          setBillingAdresse((prev) => ({
            ...prev,
            name: cust?.name ?? "",
            company: cust?.company ?? "",
            street: adresse.addressStreet,
            zip: adresse.addressZip,
            city: adresse.addressCity,
            addition: adresse.addressAddition,
            // Ohne gespeichertes Land: Deutschland vorausgewaehlt (freigegebenes Mockup, 15.09.2026).
            countryCode: adresse.addressCountryCode || "DE",
          }));
        }
        /*
         * Punkt 57 Etappe 3: der Stand der Kostenübernahme, in einem eigenen Aufruf. Bewusst
         * best-effort wie Betriebskontakt und Bilder: scheitert er, fehlt der Block — das
         * Angebot selbst bleibt vollständig bedienbar. Ein Zusatzweg darf den Hauptweg nie
         * blockieren (die oberste Regel: der Kunde muss zusagen können).
         */
        if (!vorschau && token) {
          try {
            const kuRes = await fetch(`/api/public/offer/${token}/kostenuebernahme`);
            if (kuRes.ok) {
              const kuBody = (await kuRes.json()) as { data: KostenuebernahmeStatus };
              setKuStatus(kuBody.data);
              if (kuBody.data.sign_url) setKuSignUrl(kuBody.data.sign_url);
            }
          } catch {
            /* Stand nicht ermittelbar -> der Block bleibt weg, mehr passiert nicht. */
          }
        }
        const chosen = body.data.options.find((o) => o.is_chosen);
        // "paid" zaehlt wie "offer_chosen" als verbindlich angenommen (Block 6: Menü-Bestaetigung
        // bleibt bis zur Durchfuehrung moeglich, auch nach vollstaendiger Zahlung).
        if (body.data.event.status === "offer_declined") {
          // E4.6: bereits abgesagt (dieser oder ein anderer Tab) -> Absage-Karte statt Formular.
          setPhase("declined");
        } else if (body.data.event.status === "refunded") {
          // Punkt 103: voll erstattet -> Abschlusskarte mit Hinweis, nie das Annahme-Formular.
          if (chosen) setSelected(chosen.id);
          setPhase("done");
        } else if (chosen) {
          setSelected(chosen.id);
          setPhase(["offer_chosen", "paid", "balance_due"].includes(body.data.event.status) ? "done" : "ready");
        } else {
          /*
           * EINE Variante ist keine Auswahl — sie ist das Angebot.
           *
           * Antoine, 17.09.2026: „Wenn es nur eine Variante gibt, soll diese immer vorausgewählt
           * sein." Bis hierher startete `selected` auf `null`, und der Gast musste erst den Kreis
           * anklicken, bevor „Annehmen" ueberhaupt bedienbar wurde (`disabled={... || !selected}`).
           * Bei einer einzigen Variante ist dieser Klick keine Entscheidung, sondern eine Huerde
           * vor der einzigen moeglichen Antwort.
           *
           * BEWUSST NUR BEI GENAU EINER. Stehen mehrere zur Wahl, bleibt die Auswahl leer: eine
           * vorausgewaehlte Variante waere dort eine Empfehlung, die niemand ausgesprochen hat —
           * und der Gast koennte versehentlich die teuerste annehmen, weil sie zufaellig oben
           * stand.
           *
           * Buchungsstrecke B1 (sql/587): AUSGESPROCHEN hat sie dann der Betrieb — seine
           * Empfehlung ist vorausgewaehlt. Nur die Ansicht: angenommen ist damit nichts, der Gast
           * muss weiterhin selbst zusagen (Knopf, AGB, Zusammenfassung unveraendert).
           */
          const vorgewaehlt = vorauswahl(body.data.options);
          if (vorgewaehlt) setSelected(vorgewaehlt);
          setPhase("ready");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    })();
  }, [token, id, vorschau, reloadKey]);

  /** Block 6: finale Menüauswahl verbindlich bestätigen. */

  /**
   * WARTESCHLANGE Punkt 9 (Antoine, 14.09.2026: "der Gast soll es selbst machen können") -
   * schlaegt die automatische Linkerzeugung bei der Annahme fehl (`offer.deposit` ohne
   * `checkout_url`), fordert der Gast hier selbst einen Zahlungslink an. Server ist
   * preis-autoritativ: der Betrag kommt aus der Antwort (amountCents), NIE ein Client-Wert.
   * Idempotent auf Server-Seite (POST /api/public/offer/:token/deposit-retry) - ein
   * Doppel-Klick erzeugt keinen zweiten Stripe-Link.
   */
  /** sql/727: Restforderung — derselbe Weg wie oben, nur final-retry (sql/697). Betrag vom Server. */
  async function retryRest() {
    if (vorschau || !token) return;
    setDepositRetryBusy(true);
    setDepositRetryError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/final-retry`, { method: "POST" });
      const body = (await res.json().catch(() => null)) as { data?: { url: string } } | null;
      if (!res.ok || !body?.data) throw new Error(t.depositRetryError);
      window.location.href = body.data.url;
    } catch (err) {
      setDepositRetryError(err instanceof Error ? err.message : t.depositRetryError);
    } finally {
      setDepositRetryBusy(false);
    }
  }

  async function retryDeposit() {
    if (vorschau || !token) return;
    setDepositRetryBusy(true);
    setDepositRetryError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/deposit-retry`, { method: "POST" });
      const body = (await res.json().catch(() => null)) as { data?: { url: string; amountCents: number }; error?: string } | null;
      // Keine offene Anzahlung mehr (z. B. nach Aenderung der Konditionen storniert): kein
      // "versuchen Sie es erneut", sondern eine klare Aussage ohne Knopf.
      if (!res.ok && body?.error === "no_pending_deposit") {
        setDepositKeineOffen(true);
        return;
      }
      if (!res.ok || !body?.data) throw new Error(t.depositRetryError);
      setOffer((prev) =>
        // WARTESCHLANGE Punkt 26: `invoice` bleibt hier unveraendert bestehen (die Rechnung
        // entstand bereits bei der Annahme, unabhaengig vom Zahlungslink - ein Retry des Links
        // darf sie nicht aus der Anzeige werfen).
        prev ? { ...prev, deposit: { amount_cents: body.data!.amountCents, status: "processing", checkout_url: body.data!.url, invoice: prev.deposit?.invoice ?? null, aufschluesselung: prev.deposit?.amount_cents === body.data!.amountCents ? prev.deposit?.aufschluesselung ?? null : null } } : prev,
      );
      /*
       * WARTESCHLANGE Punkt 49c — DIREKT WEITER ZU STRIPE, kein zweiter Klick.
       *
       * Bis hierher endete dieser Weg damit, dass ein Zahlungs-Knopf ERSCHIEN, den der Gast dann
       * noch einmal druecken musste. Zwei Klicks fuer eine Absicht, und zwischen ihnen eine Seite,
       * auf der man haengenbleiben kann. Antoine, 18.09.2026: „Wenn ich bei Amazon bezahlen
       * möchte, muss ich doch nicht einen Zahlungslink anfordern."
       *
       * Gleicher Weg wie beim Ablauf „direkt zur Zahlung" weiter unten (`window.location.href`,
       * nicht `window.open`): eine Weiterleitung im selben Fenster wird von keinem Browser als
       * Pop-up geblockt, ein neues Fenster nach einem `await` regelmaessig schon — und ein
       * geblocktes Fenster waere aus Sicht des Gasts genau das, was hier behoben wird: ein Klick,
       * nach dem nichts passiert. Der Zustand oben bleibt trotzdem gesetzt: kehrt der Gast ueber
       * „zurueck" wieder her, steht der Knopf mit dem fertigen Link da.
       */
      window.location.href = body.data.url;
    } catch (err) {
      setDepositRetryError(err instanceof Error ? err.message : t.depositRetryError);
    } finally {
      setDepositRetryBusy(false);
    }
  }

  async function accept() {
    // Vorschau: Annehmen ist Sache des Gasts — hier strukturell nicht moeglich (kein Token).
    // sql/344: ohne vollstaendige Rechnungsadresse + Telefon keine Annahme - diese Fuehrung ist
    // BEWUSST nur clientseitig (kein Server-Backstop hier, "keine bestehenden Formulare
    // brechen" - die harte Sperre sitzt an der Rechnungserstellung, lib/auto-invoice.ts).
    if (vorschau || !token || !selected || !agbAccepted || !billingComplete || wahlLuecke) return;
    // Gäste je Gericht: eine noch nicht gespeicherte Eingabe geht ZUERST raus — der Server liest sie bei der Annahme.
    // Nur wenn die gewählte Variante überhaupt Wahlgänge hat (eingefrorenes Verzeichnis): sonst gibt es nichts zu
    // speichern, und ein alter Speicherfehler (z. B. von einer anderen Variante) darf die Annahme nicht blockieren.
    const gewaehlteFassung = offer?.options.find((o) => o.id === selected) ?? null;
    const brauchtMengen = wahlgaengeAus(gewaehlteFassung).length > 0 || menuewahlenAus(gewaehlteFassung).length > 0;
    if (brauchtMengen && !(await mengenJetztSpeichern())) {
      setError(AUSWAHL_T[lang].speichertFehler);
      return;
    }
    // A5: wer direkt auf „Annehmen" drueckt (alles vorbefuellt), hat die Zusage hier begonnen.
    meldeEreignis("zusage_begonnen");
    // Menge 0 wird weggelassen statt als Null gesendet — „nicht gebucht" ist keine Buchung über
    // nichts. Direkt aus dem State gelesen, nicht über die Anzeige-Helfer weiter unten: was
    // abgeschickt wird, soll nicht davon abhängen, was die Seite gerade zeichnet.
    // sql/763: Gäste-Zähler senden `gaeste` mit (`zubuchungsAuswahl`), sonst rechnet der Server „für alle“.
    const gewaehlteZubuchungen = zubuchungsAuswahl(offer?.options.find((o) => o.id === selected) ?? null, addonWahl);
    setPhase("submitting");
    setError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          optionId: selected,
          // Fund D: die Fassung, die der Gast gerade vor sich hat — der Server nimmt nur zu ihr an.
          offerVersion: offer?.event.current_offer_version ?? undefined,
          notes: notes || undefined,
          agbAccepted: true,
          name: billingAdresse.name?.trim() || undefined,
          company: billingAdresse.company?.trim() || undefined,
          addressStreet: billingAdresse.street?.trim(),
          addressZip: billingAdresse.zip?.trim(),
          addressCity: billingAdresse.city?.trim(),
          // sql/485: leer heisst „nichts geaendert“ - der Server laesst Bestehendes dann stehen.
          addressAddition: billingAdresse.addition?.trim() || undefined,
          addressCountryCode: billingAdresse.countryCode || undefined,
          phone: billingPhone.trim(),
          kundenreferenz: billingRef.trim() || undefined,
          /*
           * Punkt 32: die Zubuchungen gehen NUR hier mit, zusammen mit der Annahme. Menge 0 wird
           * weggelassen statt als Null gesendet — „nicht gebucht" ist keine Buchung über nichts.
           * Der Server prüft Zugehörigkeit und Obergrenze neu und rechnet die Summe selbst
           * (sql/475); die Zahl, die der Gast hier sieht, ist Vorschau, nie Grundlage.
           */
          chosenAddons: gewaehlteZubuchungen.length > 0 ? gewaehlteZubuchungen : undefined,
          bewertungEinwilligung: bewertungsbitte?.aktiv ? bewertungEinwilligung : undefined,
          bewertungSprache: offer?.event.offer_language === "en" ? "en" : "de",
        }),
      });
      if (res.status === 422) {
        // Kein sql/344-Backstop mehr (bewusst entfernt, s. accept() oben) - dieser Zweig faengt
        // nur noch eine unerwartete Validierungsablehnung ab, z. B. ein zu langes Freitextfeld.
        throw new Error(t.hintBillingIncomplete);
      }
      if (res.status === 410) {
        // H-16: waehrend der Sitzung abgelaufen -> Flag setzen, die Annahme-UI weicht der
        // Ablauf-Karte (der Server hat nichts mutiert).
        setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, offer_expired: true } } : prev));
        throw new Error(t.errExpired);
      }
      if (res.status === 409) {
        // Fund D: der Betreiber hat inzwischen geaendert und neu versendet -> den neuen Stand
        // laden und es dem Gast sagen; seine Eingaben bleiben stehen (s. Lade-Effekt).
        const konflikt = (await res.json().catch(() => null)) as { error?: string } | null;
        // sql/565 (R1): eine gueltige, unterschriebene Kostenuebernahme steht (V1 gilt nach dem
        // Leitprinzip weiter) — nichts wurde geschrieben. Annehmen/Ablehnen bleibt ab jetzt aus.
        if (konflikt?.error === "gueltige_kostenuebernahme") {
          setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, gueltigeKue: true } } : prev));
          throw new Error(t.errGueltigeKue);
        }
        // sql/770 („Alte Fassung kurz sperren“): der Betreiber aktualisiert das Angebot gerade; nichts wurde geschrieben.
        if (konflikt?.error === "fassung_wird_aktualisiert") {
          setOffer((prev) => (prev ? { ...prev, fassungVeraltet: true } : prev));
          throw new Error(t.fassungAktualisiert);
        }
        if (konflikt?.error === "offer_changed") {
          setReloadKey((k) => k + 1);
          throw new Error(t.errChanged);
        }
        // H-17 / Randfall E1 (20.09.2026): ein anderer Tab oder Klick war schneller — die Zusage
        // (oder Absage) IST da. Statt den Gast zum Neuladen aufzufordern, laedt die Seite den
        // echten Stand selbst und zeigt die Dank- bzw. Absage-Karte (Test:
        // offer-public-zweiter-tab-verliert.test.tsx).
        setReloadKey((k) => k + 1);
        throw new Error(konflikt?.error === "already_declined" ? t.errAlreadyDeclined : t.errConflict);
      }
      if (!res.ok) throw new Error("Die Auswahl konnte nicht gespeichert werden. Bitte erneut versuchen.");
      // H-15: Der Response traegt den gerade erzeugten Anzahlungslink - vorher wurde der Body
      // verworfen und der Kunde bekam seinen Zahlungsweg nie zu sehen.
      const body = (await res.json().catch(() => null)) as {
        data?: {
          depositUrl?: string | null;
          depositCents?: number | null;
          // WARTESCHLANGE Punkt 26 (Antoine, 15./16.09.2026, HAT PRIO): die Anzahlungsrechnung
          // kommt bereits in DIESER Annahme-Antwort mit (lib/offer-accept.ts), damit der Kunde sie
          // sofort auf dem Ergebnis-Bildschirm sieht, nicht erst nach einem Reload.
          depositInvoicePdfUrl?: string | null;
          depositInvoiceNumber?: string | null;
          /** Migration 731: „À la carte“ ohne Zahlung - die Antwort sagt es ausdrücklich. */
          aLaCarte?: boolean;
        };
      } | null;
      if (body?.data?.aLaCarte === true) setALaCarteAngenommen(true);
      const depositUrl = body?.data?.depositUrl ?? null;
      const depositCents = body?.data?.depositCents ?? null;
      const depositInvoicePdfUrl = body?.data?.depositInvoicePdfUrl ?? null;
      const depositInvoiceNumber = body?.data?.depositInvoiceNumber ?? null;
      const depositInvoice = depositInvoicePdfUrl ? { invoiceNumber: depositInvoiceNumber, pdfUrl: depositInvoicePdfUrl } : null;
      setOffer((prev) => {
        if (!prev) return prev;
        // E2E-Betreiberweg Fund B (20.09.2026): der Server hat die gewaehlte Variante soeben mit
        // `is_chosen` markiert - die Seite muss das SOFORT wissen. Der Erfolgsblock "Ihr Menü"
        // sucht `angenommeneVariante` ueber genau dieses Kennzeichen; ohne die Uebernahme hier
        // stand dort bis zum Neuladen "Für diesen Vorgang sind noch keine Speisen hinterlegt.",
        // obwohl die Variante Gaenge trug (Test: offer-public-menue-nach-zusage.test.tsx).
        const options = prev.options.map((o) => ({ ...o, is_chosen: o.id === selected }));
        // WARTESCHLANGE Punkt 26: die Rechnung entsteht UNABHAENGIG vom Stripe-Link (eigener
        // try/catch in lib/offer-accept.ts) - deshalb hier NICHT an `depositUrl` gekoppelt. Ohne
        // beides bleibt der Anzahlungsblock unveraendert (kein Anzahlungsweg fuer dieses Angebot).
        if (!depositUrl && !depositInvoice) return { ...prev, options };
        return {
          ...prev,
          options,
          deposit: {
            amount_cents: depositCents ?? prev.deposit?.amount_cents ?? 0,
            status: depositUrl ? "processing" : (prev.deposit?.status ?? "pending"),
            checkout_url: depositUrl ?? prev.deposit?.checkout_url ?? null,
            invoice: depositInvoice ?? prev.deposit?.invoice ?? null,
          },
        };
      });
      setPhase("done");
      /**
       * § 8 (acceptanceFlow='direct_payment'): "das Vorhandensein/Öffnen der Seite plus der
       * Zahlungsschritt selbst gilt als Annahme" — hier gewaehlt: der Zahlungsschritt wird
       * automatisch AUSGELOEST, sobald der Kunde die Annahme bestaetigt (derselbe Klick wie bei
       * acceptanceFlow='explicit', nur ohne den zusaetzlichen manuellen Klick auf den
       * Anzahlungs-Link auf der "done"-Karte) — nicht schon beim blossen Laden der Seite (GET),
       * damit ein Crawler/Vorschau-Fetch niemals eine echte Zahlung ausloest. Fuer
       * paymentPathway='kostenuebernahme' gibt es keinen Link zum Weiterleiten - dort erscheint
       * stattdessen sofort der Kostenuebernahme-Hinweis (s. "done"-Karte unten).
       */
      if (offer?.checkoutMode?.acceptanceFlow === "direct_payment" && depositUrl) {
        window.location.href = depositUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("ready");
    }
  }

  /** E4.6: Angebot ablehnen (optionaler Grund geht als Lernsignal an den Betrieb). */
  async function decline() {
    // Vorschau: Absagen ist Sache des Gasts — hier strukturell nicht moeglich (kein Token).
    if (vorschau || !token || declining) return;
    setDeclining(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ grund: declineGrund.trim() || undefined }),
      });
      // Paralleler zweiter Tab hat schon geantwortet (Annahme ODER Absage) -> den echten Stand
      // laden (E1), die Seite zeigt dann die passende Karte.
      if (res.status === 409) {
        const konflikt = (await res.json().catch(() => null)) as { error?: string } | null;
        // sql/565 (R1): siehe accept() oben — nichts wurde geschrieben, Ablehnen bleibt ab jetzt aus.
        if (konflikt?.error === "gueltige_kostenuebernahme") {
          setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, gueltigeKue: true } } : prev));
          throw new Error(t.errGueltigeKue);
        }
        setReloadKey((k) => k + 1);
        throw new Error(konflikt?.error === "already_declined" ? t.errAlreadyDeclined : t.errConflict);
      }
      if (!res.ok) throw new Error("Die Absage konnte nicht gespeichert werden. Bitte erneut versuchen.");
      setPhase("declined");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeclining(false);
    }
  }

  /** K5.2 (E-K11): Änderungswunsch senden — der Server legt ihn als eingehende Nachricht am
   *  Vorgang ab und stößt dieselbe v2-Pipeline an wie eine Mail-Antwort. */
  async function sendeAenderungswunsch() {
    // Vorschau: keine Gast-Äußerung auslösbar (Button ist zusätzlich disabled).
    if (vorschau || !token || wunschSending) return;
    const text = wunschText.trim();
    if (text.length < 5) return; // Server erzwingt 5–2000 ohnehin (422 mit Klartext)
    setWunschSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/aenderungswunsch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // B2 (Zweitpruefung M2): die Frage wird als Frage gekennzeichnet, der Aenderungswunsch bleibt wie bisher.
        body: JSON.stringify(v2 && wunschArt === "frage" ? { text, art: "frage" } : { text }),
      });
      if (!res.ok) throw new Error(v2 && wunschArt === "frage" ? v2t.frageFehler : t.wunschErr);
      // Erfolg: Bestätigung zeigen, Feld zu — der Wunsch liegt jetzt sichtbar am Vorgang.
      setWunschDone(true);
      setWunschOpen(false);
      setWunschText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWunschSending(false);
    }
  }

  /**
   * Konzept § 5.6 Ebene 3 (Sichtbarkeit): ein liegengebliebener Probelauf ist nicht unsichtbar,
   * er steht dem Betreiber im Weg. Deshalb wird beim Oeffnen der Vorschau gefragt, ob noch einer
   * offen ist — best-effort: schlaegt die Abfrage fehl, laedt die Vorschau trotzdem.
   */
  useEffect(() => {
    if (!vorschau || !id) return;
    void (async () => {
      try {
        const res = await apiFetch(`/api/events/${id}/probelauf`);
        if (!res.ok) return;
        const body = (await res.json()) as { data?: { offen?: boolean } };
        setProbelaufOffen(body.data?.offen === true);
      } catch {
        /* Der Probelauf-Stand ist Komfort; die Vorschau haengt nicht daran. */
      }
    })();
  }, [vorschau, id]);

  async function starteProbelauf() {
    if (!vorschau || !id || probelaufBusy) return;
    setProbelaufBusy(true);
    setProbelaufFehler(null);
    try {
      const res = await apiFetch(`/api/events/${id}/probelauf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selected ? { optionId: selected } : {}),
      });
      const body = (await res.json().catch(() => null)) as { data?: ProbelaufBilanz; message?: string; error?: string } | null;
      if (!res.ok || !body?.data) {
        // Antoines Regel vom 13.09.2026: die Meldung des Servers im Klartext durchreichen,
        // niemals durch einen eigenen, allgemeineren Satz ersetzen.
        throw new Error(body?.message ?? body?.error ?? `Der Probelauf konnte nicht gestartet werden (HTTP ${res.status}).`);
      }
      setProbelaufBilanz(body.data);
      setProbelaufOffen(true);
    } catch (err) {
      setProbelaufFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setProbelaufBusy(false);
    }
  }

  async function entferneProbelauf() {
    if (!vorschau || !id || probelaufBusy) return;
    setProbelaufBusy(true);
    setProbelaufFehler(null);
    try {
      const res = await apiFetch(`/api/events/${id}/probelauf`, { method: "DELETE" });
      const body = (await res.json().catch(() => null)) as { data?: { hinweis?: string }; message?: string; error?: string } | null;
      if (!res.ok) throw new Error(body?.message ?? `Der Probelauf konnte nicht entfernt werden (HTTP ${res.status}).`);
      setProbelaufBilanz(null);
      setProbelaufOffen(false);
      // Ein nicht deaktivierter Stripe-Link ist kein Fehlschlag des Aufraeumens, aber er darf
      // nicht verschwiegen werden (Konzept § 5.6).
      if (body?.data?.hinweis) setProbelaufFehler(body.data.hinweis);
    } catch (err) {
      setProbelaufFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setProbelaufBusy(false);
    }
  }

  const tenantLabel = betriebsName(offer?.contact?.company_name);
  const lang: "de" | "en" = offer?.event.offer_language === "en" ? "en" : "de";
  const t = T[lang];
  /*
   * Buchungsstrecke B2 (sql/591): die neue Angebotsseite, NUR wenn der Server sie fuer diesen
   * Betrieb einschaltet (`angebotsseite.v2`). Ohne Schalter bleibt `v2` null und jede Stelle unten
   * zeichnet genau das, was sie vorher gezeichnet hat. Annahme, Zahlung, Anzahlung und
   * Kostenuebernahme sind davon unberuehrt — die neue Seite ordnet nur an und zeigt an.
   */
  const v2: AngebotsseiteV2 | null = offer?.angebotsseite?.v2 === true ? (offer.angebotsseite as AngebotsseiteV2) : null;
  const v2t = V2T[lang];
  const v2Vorname = vornameAus(v2?.ansprechpartner);
  // „Mit Mitentscheider teilen“: derselbe Gast-Link, kein neuer Token. In der Betreiber-Vorschau gibt es keinen.
  const gastLink = !vorschau && token && typeof window !== "undefined" ? `${window.location.origin}/angebot/${token}` : null;
  function oeffneNachricht(art: "wunsch" | "frage") {
    setWunschArt(art);
    setWunschDone(false);
    setWunschOpen(true);
    setTimeout(() => scrolleZu("gast-nachricht"), 0);
  }
  const dateLocale = lang === "en" ? "en-GB" : "de-DE";
  const sectionLabel = SECTION_LABEL[lang];
  // H-16: abgelaufen -> Angebot bleibt lesbar, aber ohne Annahme-UI (Server lehnt ohnehin mit 410 ab).
  const expired = offer?.event.offer_expired === true;
  // sql/565 (R1): nach dem ersten 409 {error:'gueltige_kostenuebernahme'} blendet die Seite
  // Annehmen/Ablehnen aus (die Fehlermeldung oben bleibt als Hinweis stehen).
  // KÜ V2 (S2): liegt eine geänderte Fassung zur Unterschrift bereit, ist die Unterschrift zugleich
  // die Annahme (E6) — Annehmen/Ablehnen per Klick greift ohnehin nicht (sql/565), also gar nicht erst zeigen.
  const gueltigeKueBlock = offer?.event.gueltigeKue === true || Boolean(kuStatus?.ersetzt_signiert_am) || offer?.fassungVeraltet === true;
  // Zweitpruefung PR #1506 (N5): Leiste und Platz fuer sie nur, wenn sie wirklich erscheint.
  const leistenVariante =
    v2 && offer && (phase === "ready" || phase === "submitting") && !expired && !gueltigeKueBlock
      ? (offer.options.find((o) => o.id === selected) ?? null)
      : null;
  // Punkt 103 (sql/507): voll erstattet -> ansehen ja, zahlen/waehlen nein.
  const erstattet = offer?.event.status === "refunded";
  /**
   * Deckt die erzeugte Zahlung die GANZE Summe der angenommenen Variante, ist sie keine
   * Anzahlung mehr, sondern die Zahlung (16.09.2026, Antoine: „einen Stripe Link ohne Anzahlung
   * mit der gesamten Summe"). Der Block traegt dann „Zahlung" statt „Anzahlung" — ein Satz, der
   * „Anzahlung von 731,00 €" sagt, obwohl 731,00 € die ganze Summe sind, ist schlicht falsch.
   *
   * Bezugspunkt ist bewusst die ANGENOMMENE Variante (`is_chosen`), nicht die erste oder
   * teuerste: nur sie ist der Betrag, den der Gast zugesagt hat. Ohne gewaehlte Variante gibt es
   * auch keine Zahlungszeile — der Block erscheint dann ohnehin nicht.
   *
   * `>=` statt `===`: ein Fixbetrag in den Zahlungskonditionen kann die Summe theoretisch
   * uebersteigen. `computeDepositCents` deckelt zwar auf die Summe, aber die Beschriftung soll
   * nicht davon abhaengen, dass das serverseitig so bleibt.
   */
  const angenommeneVariante = offer?.options.find((o) => o.is_chosen) ?? null;
  const istVollzahlung =
    offer?.deposit != null &&
    angenommeneVariante != null &&
    angenommeneVariante.amount_total_cents > 0 &&
    offer.deposit.amount_cents >= angenommeneVariante.amount_total_cents;
  // § 8 (Migration 345): Default 'explicit'/'stripe' - heutiges Verhalten unveraendert, solange
  // der Betreiber nichts pflegt bzw. der Abruf (best-effort) scheitert.
  const acceptanceFlow = offer?.checkoutMode?.acceptanceFlow ?? "explicit";
  const paymentPathway = offer?.checkoutMode?.paymentPathway ?? "stripe";
  const isKostenuebernahme = paymentPathway === "kostenuebernahme";
  /**
   * Buchungsstrecke A3: „Es ist fix!“ nur, wenn NACHWEISLICH nichts mehr aussteht — sonst „Es ist
   * fast fix!“ mit dem Grund (Wahrheit vor Wohlgefühl). Geld-Zweitprüfung PR #1499: die bloße
   * Existenz einer Anzahlungszeile reichte nicht. Scheitert die Linkanlage bei der Zusage (kein
   * Stripe-Zugang) oder zahlt der Gast „vor Ort“/per „Rechnung vorab“, gibt es keine Zeile — die
   * Anzahlung ist trotzdem geschuldet. Fix ist es deshalb nur, wenn der SERVER es belegt:
   *   - Kostenübernahme-Weg ohne Zahlungszeile: der Unterschriftsstand sagt „signiert“;
   *   - eine Zahlungszeile: bezahlt und nichts mehr offen;
   *   - Vorgang bereits „bezahlt“ (Zahlungsstatus des Servers);
   *   - sonst: der Server sagt für die angenommene Variante ausdrücklich „keine“ Zahlung nach der
   *     Zusage (`zahlungNachZusage`). Fehlt die Angabe (Fehler, unbekannt): „fast fix“.
   * Nichts davon wird im Browser gerechnet.
   */
  const fixGrund: "fix" | "zahlung" | "kostenuebernahme" = (() => {
    if (!offer) return "zahlung";
    if ((offer.restforderung?.offen_cents ?? 0) > 0) return "zahlung";
    if (isKostenuebernahme && !offer.deposit) return kuStatus?.status === "signiert" ? "fix" : "kostenuebernahme";
    // Migration 731: „À la carte“ ohne Betrag - es gibt nichts zu zahlen, der Termin ist mit der Zusage fix.
    if (aLaCarteAngenommen && !offer.deposit) return "fix";
    if (offer.deposit) return offer.deposit.status === "succeeded" && (offer.deposit.offen_cents ?? 0) <= 0 ? "fix" : "zahlung";
    if (offer.event.status === "paid") return "fix";
    const verlangt = angenommeneVariante ? offer.zahlungNachZusage?.[angenommeneVariante.id] : undefined;
    return verlangt === "keine" ? "fix" : "zahlung";
  })();
  const nochNichtFix = !erstattet && fixGrund !== "fix";
  /** Satz im „fast fix“: Zahlung statt Anzahlung, wenn die Zeile bzw. der Server die ganze Summe nennt. */
  const fastFixVollzahlung =
    offer?.deposit != null
      ? istVollzahlung
      : angenommeneVariante != null && offer?.zahlungNachZusage?.[angenommeneVariante.id] === "vollzahlung";
  const anlassDa = Boolean(offer?.event.occasion?.trim());
  const ctaLabel =
    acceptanceFlow === "direct_payment" ? (isKostenuebernahme ? t.ctaAcceptDirectKostenuebernahme : t.ctaAcceptDirectStripe) : t.ctaAccept;

  /*
   * ═══ Punkt 32, Konzept § 7: die dazubuchbaren Positionen einer Fassung ═══
   *
   * Buchbar ist eine Position nur, wenn sie optional IST und einen beim Versand eingefrorenen
   * Einzelpreis trägt (sql/480). Fehlt der Preis — alte Fassung ohne Snapshot —, bleibt sie die
   * Textzeile, die sie heute ist. Ein Bedienelement an einer Position, deren Preis sich nach dem
   * Versand geändert haben kann, wäre schlimmer als gar keins: der Gast bucht dann zu einer Zahl,
   * die er nie gesehen hat.
   */
  const gewaehlteOption = offer?.options.find((o) => o.id === selected) ?? null;
  /*
   * Die Auswahl des Gastes als Vorschau-Rechnung (`ui/angebot-auswahl-modell.ts`): Zubuchungen, ersetzende
   * Zubuchungen (das Hauptmenü sinkt um die eingefrorene Abzugstabelle) und Aufstockung auf den
   * Mindestumsatz — nur Addition/Subtraktion beim Versand eingefrorener Ganzzahlen. Verbindlich rechnet
   * der Server bei der Annahme (sql/752). `zubuchungenSummeCents` ist der UNTERSCHIED zum Variantenpreis
   * und kann bei ersetzenden Zubuchungen auch negativ sein; `hatZubuchung` sagt, ob überhaupt etwas
   * gewählt ist.
   */
  const rechnung = gewaehlteOption ? rechne(gewaehlteOption, addonWahl, mengen) : null;
  const zubuchungenSummeCents = rechnung?.deltaCents ?? 0;
  const hatZubuchung = (rechnung?.gewaehlt ?? 0) > 0;
  /* Wahlgänge („oder“): die Annahme geht erst, wenn in JEDEM Wahlgang der gewählten Variante die Gäste
     genau aufgehen (Gäste mit Hauptmenü = Gäste minus ersetzte). Der Server prüft dasselbe noch einmal. */
  const offenerGang = gewaehlteOption && rechnung ? (wahlStand(gewaehlteOption, mengen, rechnung.menuGaeste).find((x) => !x.stand.ok) ?? null) : null;
  // sql/772: Menüwahl im Paket - unvollständig hält die Annahme an, außer die Fassung erlaubt die Wahl später (`spaeter`).
  const offenesMenue = gewaehlteOption ? (menuewahlenAus(gewaehlteOption).map((m) => ({ m, lage: menuewahlLage(m, mengen) })).find((x) => menuewahlSperrt(x.m, x.lage)) ?? null) : null;
  const wahlLuecke = offenerGang != null || offenesMenue != null || rechnung?.ueberbucht === true;
  const wahlLueckeText = (() => {
    const at = AUSWAHL_T[lang];
    if (rechnung?.ueberbucht) return at.hinweisUeberbucht;
    if (!offenerGang && offenesMenue) return offenesMenue.lage.zuViel > 0 ? at.hinweisMenueZuViel(offenesMenue.lage.zuViel) : at.hinweisMenue(offenesMenue.lage.offen);
    if (!offenerGang) return null;
    const gang = offenerGang.gang.gang ?? at.wahlgangName;
    return offenerGang.stand.zuViel > 0 ? at.hinweisZuViel(gang, offenerGang.stand.zuViel) : at.hinweisWahl(gang, offenerGang.stand.offen);
  })();
  /*
   * Migration 731 (Antoine, 19.10.2026): „À la carte · Abrechnung vor Ort“. Die Variante hat keinen
   * Betrag, die Gäste bestellen im Restaurant von der Karte. Dann zeigt die Seite KEINE Summe, keine
   * Zahlungsbedingungen, keinen Zahlungsstand, und der Knopf heißt „Verbindlich reservieren“ - nie der
   * Zahlungs-Knopf. Nur eine Variante MIT Kennzeichen UND ohne Betrag zählt so; eine bepreiste Variante
   * daneben verhält sich wie bisher. Wählt der Gast Zubuchungen (> 0), gilt der Normalweg - der Server
   * rechnet dann eine Zahlung auf genau diesen Betrag. Gerechnet wird hier nichts, nur entschieden,
   * was gezeigt wird.
   */
  const istALaCarteOhneBetrag = (o: { a_la_carte?: boolean; amount_total_cents: number } | null | undefined): boolean =>
    o?.a_la_carte === true && o.amount_total_cents <= 0;
  const alcT = A_LA_CARTE_GAST[lang];
  const aLaCarteGewaehlt = istALaCarteOhneBetrag(offer?.options.find((o) => o.id === selected)) && !hatZubuchung;

  /*
   * ═══ Buchungsstrecke C1: Zwei-Schritt-Zusage (nur neue Seite) ═══
   *
   * Nur mit `v2` und solange es ueberhaupt einen Annehmen-Knopf gibt (sql/565: bei gueltiger
   * Kostenuebernahme bleibt die Seite wie bisher). Die Schritte ordnen nur an: dieselben Felder,
   * dieselbe Zusammenfassung (S0), derselbe Knopf, dasselbe `accept()`.
   */
  const zweiSchritte = v2 != null && !gueltigeKueBlock;
  const zusageSchritt1 = zweiSchritte && zusageSchritt === 1;
  // Die gast-ausgeloeste Kostenuebernahme (Punkt 57): auf der neuen Seite ein Nebenweg hinter einem Link.
  const kuWegVerfuegbar = Boolean(kuStatus?.freigegeben && kuStatus.esign_bereit && !kuSignUrl);
  const zusageAuswahl: ZusageAuswahl | null = (() => {
    const v = offer?.options.find((o) => o.id === selected);
    if (!offer || !v) return null;
    const ms = v.menu_selection ?? {};
    return {
      titel: v.title || ms.title || (istALaCarteOhneBetrag(v) ? alcT.nachKarte : money(v.amount_total_cents)),
      gaeste: v.guest_count,
      datum: offer.event.event_date ? formatDate(offer.event.event_date, dateLocale) : null,
      // Anzeige wie die S0-Zusammenfassung darunter; verbindlich rechnet der Server bei der Annahme.
      gesamt: istALaCarteOhneBetrag(v) && !hatZubuchung ? alcT.nachKarte : money(v.amount_total_cents + zubuchungenSummeCents),
      mitZubuchungen: hatZubuchung,
      ohneBetrag: istALaCarteOhneBetrag(v) && !hatZubuchung,
      zubuchungen: zubuchungenAus(v)
        .filter((z) => (addonWahl[z.id] ?? 0) > 0)
        .map((z) => {
          const name = z.item.translations?.[lang]?.name ?? z.item.name;
          // sql/759/763: Gäste-Zähler zeigen „Name · k Gäste“ (auch „1 Gast“).
          if (z.gaesteCents) return `${name} · ${AUSWAHL_T[lang].gaeste(addonWahl[z.id] ?? 0)}`;
          return z.art === 'zaehler' && (addonWahl[z.id] ?? 0) > 1 ? `${addonWahl[z.id]} × ${name}` : name;
        }),
    };
  })();
  function zusageWeiter() {
    // A5: „Ja, weiter“ ist das erste Anfassen der Zusage.
    meldeEreignis("zusage_begonnen");
    const adresseFehlt = !billingAdresseKomplett;
    const telefonFehlt = billingPhone.trim() === "";
    setZusageKontaktOffen((prev) => ({ adresse: prev.adresse || adresseFehlt, telefon: prev.telefon || telefonFehlt }));
    setZusageAllesDa(!adresseFehlt && !telefonFehlt);
    setZusageSchritt(2);
    setZusageSchrittGewechselt(true);
  }
  function zusageZurueck() {
    setZusageSchritt(1);
    setZusageSchrittGewechselt(true);
  }
  function kuNebenwegOeffnen() {
    // Die Kostenuebernahme braucht dieselbe Rechnungsadresse wie die Annahme — darum in Schritt 2.
    if (zusageSchritt === 1) zusageWeiter();
    setKuNebenwegOffen(true);
    setTimeout(() => scrolleZu("ku-nebenweg"), 0);
  }
  // Rechnungsadresse und Telefon: EIN Satz Felder fuer beide Anordnungen (bisheriger Block und C1-Schritt 2).
  const billingAdresseFelder = (
    <AdresseFelder
      werte={billingAdresse}
      onFeldChange={onBillingFeldChange}
      firmaName="gast"
      variante="platzhalter"
      laender={lang === "en" ? LAENDER_EN : LAENDER}
      labels={{
        addition: t.billingAdditionLabel,
        land: t.billingCountryLabel,
        name: t.billingNameLabel,
        firma: t.billingCompanyLabel,
        street: `${t.billingStreetLabel} *`,
        zip: `${t.billingZipLabel} *`,
        city: `${t.billingCityLabel} *`,
      }}
      testIdPrefix="offer-billing"
    />
  );
  const billingTelefonFeld = (
    <input
      className="input sm:col-span-2"
      placeholder={`${isKostenuebernahme ? t.billingPhoneLabelHandynummer : t.billingPhoneLabel} *`}
      type="tel"
      value={billingPhone}
      onChange={(e) => {
      meldeEreignis("zusage_begonnen");
      setBillingPhone(e.target.value);
    }}
    />
  );
  const billingHandyGrund = isKostenuebernahme ? (
    <p className="sm:col-span-2 -mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
      {t.handynummerGrund}
    </p>
  ) : null;
  const billingRefFeld = (
    <input className="input sm:col-span-2" placeholder={t.billingRefLabel} value={billingRef} onChange={(e) => {
      meldeEreignis("zusage_begonnen");
      setBillingRef(e.target.value);
    }} />
  );
  const billingPflichtHinweis = (
    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant/80 dark:text-secondary-fixed-dim/80">
      {isKostenuebernahme ? t.requiredMarkerHandynummer : t.requiredMarker}
    </p>
  );

  /*
   * ═══ Anzahlung nach Annahme MIT Zubuchungen — vom Server, nie im Browser ═══
   *
   * Prüfbefund zu PR #1495: `zahlungsVorschau` aus dem GET rechnet auf den VARIANTENPREIS. Die
   * Annahme fordert aber auf den angenommenen Betrag inkl. Zubuchungen an. Sobald der Gast etwas
   * dazubucht, fragt die Seite deshalb `POST /api/public/offer/:token/zahlungsvorschau` mit genau
   * seiner Auswahl — der Server rechnet mit denselben Bausteinen wie die Annahme (sql/579).
   *
   * Solange die Antwort zur AKTUELLEN Auswahl fehlt (lädt, Fehler; die Betreiber-Vorschau fragt `POST /api/events/:id/zahlungsvorschau`),
   * steht KEIN Betrag da, auch kein alter: nur der Satz `anzahlungSpaeter`. Die Antwort wird über
   * den Schlüssel (Variante + Auswahl) an die Auswahl gebunden, zu der sie gehört.
   */
  // sql/763: dieselbe Abbildung wie bei der Annahme (Gäste-Zähler mit `gaeste`) - für Gast UND Betreiber-Vorschau.
  const zubuchAuswahl = zubuchungsAuswahl(offer?.options.find((o) => o.id === selected) ?? null, addonWahl);
  /*
   * Betreiber-Vorschau (28.10.2026): dort sind die Gäste-Zähler (`mengen`) nur im Browser und nicht beim
   * Server gespeichert - sie gehören deshalb in die Anfrage UND in den Schlüssel. Auch ein reiner Zähler
   * ohne Zubuchung ändert bei bepreisten Wahlgängen den Betrag; der Gast-Weg liest sie dagegen aus dem
   * Speicher des Servers (`/menu-mengen`) und fragt weiter nur bei Zubuchungen.
   */
  const mengenAktiv = vorschau && Object.values(mengen).some((g) => Object.values(g).some((n) => n > 0));
  const zubuchSchluessel =
    (hatZubuchung || mengenAktiv) && selected
      ? `${selected}|${JSON.stringify(zubuchAuswahl)}${vorschau ? `|${JSON.stringify(mengen)}` : ""}`
      : null;
  type ZahlVorschauWert = { betragCents: number; vollzahlung: boolean; artText: string | null; prozentBp?: number | null };
  const [serverZahlVorschau, setServerZahlVorschau] = useState<{ schluessel: string; vorschau: ZahlVorschauWert | null } | null>(null);
  useEffect(() => {
    if (!zubuchSchluessel || !selected || paymentPathway === "kostenuebernahme") return;
    // Gast: Token-Endpunkt. Betreiber-Vorschau: eingeloggter Endpunkt mit Vorgangs-ID, kein Gast-Token.
    if (vorschau ? !id : !token) return;
    const abbruch = new AbortController();
    const zeitgeber = setTimeout(() => {
      void (async () => {
        try {
          const kopf = { "Content-Type": "application/json" };
          const res = vorschau
            ? await apiFetch(`/api/events/${id}/zahlungsvorschau`, {
                method: "POST",
                headers: kopf,
                // Leere Zaehler gar nicht senden: der Server nimmt dann die gespeicherten (`details.menuMengen`) wie beim Gast.
                body: JSON.stringify({ optionId: selected, chosenAddons: zubuchAuswahl, ...(mengenAktiv ? { mengen } : {}) }),
                signal: abbruch.signal,
              })
            : await fetch(`/api/public/offer/${token}/zahlungsvorschau`, {
                method: "POST",
                headers: kopf,
                body: JSON.stringify({ optionId: selected, chosenAddons: zubuchAuswahl }),
                signal: abbruch.signal,
              });
          if (!res.ok) return;
          const body = (await res.json()) as { data?: { zahlungsVorschau?: ZahlVorschauWert | null } };
          if (!abbruch.signal.aborted) setServerZahlVorschau({ schluessel: zubuchSchluessel, vorschau: body.data?.zahlungsVorschau ?? null });
        } catch {
          /* abgebrochen oder Netz weg: kein Betrag, der Satz bleibt stehen */
        }
      })();
    }, 250);
    return () => {
      clearTimeout(zeitgeber);
      abbruch.abort();
    };
    // zubuchAuswahl und mengen stecken vollständig in zubuchSchluessel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zubuchSchluessel, vorschau, token, id, selected, offer?.checkoutMode?.paymentPathway]);
  /**
   * Was die Seite zur Zahlung nach der Annahme anzeigen darf, für eine Variante:
   *   `{ betrag }` — ein Serverwert, der zur aktuellen Auswahl gehört (oder `null`: keine Zahlung);
   *   `"spaeter"` — Zubuchungen gewählt, der passende Serverwert fehlt noch: kein Betrag, nur der Satz.
   */
  function zahlungNachAnnahme(optionId: string | null | undefined): { betrag: ZahlVorschauWert | null } | "spaeter" {
    if (paymentPathway === "kostenuebernahme" || !optionId) return { betrag: null };
    if (optionId === selected && zubuchSchluessel) {
      return serverZahlVorschau?.schluessel === zubuchSchluessel ? { betrag: serverZahlVorschau.vorschau } : "spaeter";
    }
    return { betrag: offer?.zahlungsVorschau?.[optionId] ?? null };
  }

  /*
   * Feste Leiste unten (Antoine, 14.10.2026) — genau dann Platz unten freihalten, wenn eine da ist:
   * Annehmen (Bereitschaft, nicht abgelaufen, keine gueltige KUE, nicht Schritt 1), Weiter zur
   * Zusage (Schritt 1 mit Auswahl) oder Bezahlen (nach der Annahme, Zahlung offen).
   */
  const annahmeLeisteDa = offer != null && (phase === "ready" || phase === "submitting") && !expired && !gueltigeKueBlock && !zusageSchritt1;
  const schrittLeisteDa = leistenVariante != null && zusageSchritt1;
  const zahlLeisteDa =
    phase === "done" &&
    offer?.deposit != null &&
    !erstattet &&
    offer.deposit.status !== "succeeded" &&
    (Boolean(offer.deposit.checkout_url) || !vorschau);
  const festeLeisteDa = annahmeLeisteDa || schrittLeisteDa || zahlLeisteDa;

  // LMIV-Legende: alle im Angebot vorkommenden Allergen-Codes + Hinweis, falls Speise-/
  // Getraenke-Positionen ohne gepflegte Angaben dabei sind (Info „auf Anfrage" statt Schweigen).
  const publicItems = offer?.options.flatMap((o) => o.items ?? []) ?? [];
  const usedAllergenCodes = [
    ...new Set(publicItems.flatMap((it) => (it.section !== "adjustment" && Array.isArray(it.allergens) ? it.allergens : []))),
  ].sort();
  const hasUnresolvedAllergens = publicItems.some(
    (it) => (it.section === "menu" || it.section === "drinks") && it.allergens == null,
  );
  const hasFoodItems = publicItems.some((it) => it.section === "menu" || it.section === "drinks");

  const marke = akzentFarben(offer?.markenfarbe);
  // Nachtrag-Version (sql/764): zu einem angenommenen Auftrag liegt eine Änderung zur Entscheidung vor.
  const nachtragOffen = Boolean(offer?.nachtrag);
  return (
    <div
      className={`ga-marke-seite flex min-h-screen flex-col bg-surface font-sans text-on-background antialiased dark:bg-inverse-surface dark:text-inverse-on-surface${
        festeLeisteDa ? ` ${FESTE_LEISTE_PLATZ} print:pb-0` : ""
      }`}
    >
      {/* Markenfarbe des Betriebs (tenant_settings.brand_primary_color) für Knöpfe und Akzente der Gastseite;
          ohne gültige Farbe bleibt der bisherige Akzent. Kein helles Rot als Fläche. */}
      <style>{`.ga-marke-seite .btn-primary,.ga-marke-seite .bg-primary{background-color:${marke.akzent};color:${marke.auf};box-shadow:none}.ga-marke-seite .btn-primary:hover{background-color:${marke.akzent};filter:brightness(.92)}.ga-marke-seite .btn-primary:disabled,.ga-marke-seite .btn-primary:disabled:hover{background-color:#e4dccb;color:#6f675a;opacity:1;filter:none;cursor:not-allowed}.ga-marke-seite .text-primary{color:${marke.akzent}}.ga-marke-seite .border-primary{border-color:${marke.akzent}}.ga-marke-seite .input:focus,.ga-marke-seite input:focus,.ga-marke-seite textarea:focus{border-color:${marke.akzent};box-shadow:0 0 0 2px color-mix(in srgb,${marke.akzent} 20%,transparent)}.ga-marke-seite .checkbox:checked,.ga-marke-seite .radio:checked{background-color:${marke.akzent};border-color:${marke.akzent}}`}</style>
      {/* Vorschau-Banner (Betreiber-Sicht): deutlich, aber ausserhalb des Gast-Designs — der
          Betreiber soll jederzeit wissen, dass er die Kundenseite nur BETRACHTET. */}
      {vorschau && (
        <div className="flex items-center justify-center gap-2 bg-primary px-margin-mobile py-2.5 text-center font-body-sm text-body-sm font-semibold text-on-primary md:px-margin-desktop">
          <Icon name="visibility" className="text-[18px]" />
          <span>
            Vorschau — so sieht der Gast diese Seite.
            {offer && ["inquiry", "offer_draft"].includes(offer.event.status) ? " Noch nicht versendet." : ""}
          </span>
        </div>
      )}
      {/* Probelauf (docs/KONZEPT-ANGEBOTSSEITE-PROBELAUF.md) — steht BEWUSST hier oben, direkt
          unter dem Vorschau-Banner und ausserhalb des Gast-Designs: Antoines Satz vom 16.09.2026
          nennt genau diesen Ort („wenn der Betreiber die Seite aufruft"). Nie im Gast-Modus. */}
      {vorschau && (
        <div className="maestro-druck-aus border-b border-outline-variant/40 bg-surface-container-lowest px-margin-mobile py-4 dark:border-outline/30 dark:bg-surface-dim/30 md:px-margin-desktop">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">Probelauf</h2>
                <p className="mt-1 max-w-3xl font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{P.einleitung}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                {!probelaufOffen && (
                  <Button type="button" onClick={starteProbelauf} disabled={probelaufBusy} className="flex items-center gap-2 disabled:opacity-60">
                    <Icon name="play_circle" className="text-[18px]" />
                    {probelaufBusy ? P.laeuft : P.starten}
                  </Button>
                )}
                {probelaufOffen && (
                  <Button type="button" variant="secondary" onClick={entferneProbelauf} disabled={probelaufBusy} className="flex items-center gap-2 disabled:opacity-60">
                    <Icon name="delete_sweep" className="text-[18px]" />
                    {probelaufBusy ? P.entfernenLaeuft : P.entfernen}
                  </Button>
                )}
              </div>
            </div>

            {probelaufOffen && !probelaufBilanz && (
              <p className="mt-3 rounded-lg bg-surface-container px-4 py-3 font-body-sm text-body-sm text-on-surface-variant dark:bg-inverse-surface dark:text-secondary-fixed-dim">
                {P.offenHinweis}
              </p>
            )}
            {probelaufFehler && (
              <p className="mt-3 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{probelaufFehler}</p>
            )}

            {probelaufBilanz && (
              <div className="mt-4 space-y-3">
                {/* Schritt fuer Schritt, jeder mit seinen INHALTEN (Konzept § 3 / Regel 2). Es gibt
                    bewusst KEINE zusammenfassende Gesamtmeldung: „Test erfolgreich" ist genau die
                    Meldung, die am 16.09.2026 gruen gewesen waere. */}
                {probelaufBilanz.schritte.map((schritt, i) => (
                  <div
                    key={schritt.schluessel}
                    className={`rounded-xl border px-4 py-3 ${
                      schritt.stand === "fehlgeschlagen"
                        ? "border-error/50 bg-error-container/40"
                        : schritt.stand === "uebersprungen"
                          ? "border-outline-variant/40 bg-surface-container/60"
                          : "border-outline-variant/40 bg-surface"
                    } dark:border-outline/30 dark:bg-inverse-surface`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon
                        name={schritt.stand === "fehlgeschlagen" ? "error" : schritt.stand === "uebersprungen" ? "remove" : "check_circle"}
                        className="mt-0.5 text-[18px]"
                      />
                      <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                        {i + 1}. {schritt.titel}
                      </span>
                    </div>
                    {schritt.zeilen.length > 0 && (
                      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-[minmax(0,14rem)_1fr]">
                        {schritt.zeilen.map((z, k) => (
                          <div key={`${z.bezeichnung}-${k}`} className="contents">
                            <dt className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{z.bezeichnung}</dt>
                            <dd className="break-words font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{z.wert}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {schritt.ursache && (
                      <p className="mt-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{schritt.ursache}</p>
                    )}
                  </div>
                ))}

                {/* Der Link MUSS anklickbar sein — sonst ist der halbe Auftrag nicht erfuellt
                    (Konzept § 3/§ 4.2). Bewusst die `Button`-Komponente mit `window.open` statt
                    eines Ankers mit roher Button-Klasse: die Design-Komponenten-Ratsche
                    (apps/web/test/design-komponenten-bestand.test.ts) zaehlt rohes Button-Markup
                    per TEXTSUCHE und darf nicht wachsen — sie stand beim ersten Lauf dieser
                    Einheit auf 89 statt 88 und war rot (beim zweiten Mal wegen dieses Kommentars
                    selbst, der die Klasse woertlich nannte). „Stripe-Seite oeffnen" ist ohnehin
                    eine Aktion, keine Navigation innerhalb der Seite. */}
                {probelaufBilanz.stripeUrl && (
                  <Button
                    type="button"
                    onClick={() => window.open(probelaufBilanz.stripeUrl!, "_blank", "noopener,noreferrer")}
                    className="flex w-full items-center justify-center gap-2 !py-3 sm:w-auto"
                  >
                    <Icon name="open_in_new" className="text-[18px]" /> {P.stripeOeffnen}
                  </Button>
                )}

                <div className="rounded-xl border border-outline-variant/40 bg-surface-container/60 px-4 py-3 dark:border-outline/30 dark:bg-surface-dim/40">
                  <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{P.grenzenTitel}</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    {probelaufBilanz.grenzen.map((g) => (
                      <li key={g}>{g}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Kopfbalken (Design-Vorlage 2e): Betriebs-Logo + Name — geteilt mit allen Gastseiten. */}
      <BetriebsKopf name={offer?.contact?.company_name} />
      {/* Gebrandeter Einladungs-Kopf mit Hero-Foto (Design-Vorlage: docs/design/DESIGN-SYSTEM.md) */}
      {/* B5: Der Hero traegt hellen Text auf einem dunklen Foto. Hintergrundbilder druckt kein
          Browser von sich aus mit — auf Papier bliebe weisse Schrift auf weissem Grund, also ein
          leerer Block. Er faellt im Druck weg; Betrieb, Datum und Gaestezahl stehen ohnehin in
          der Kopfleiste und im Absatz direkt darunter. */}
      <header className="maestro-druck-aus relative h-72 overflow-hidden bg-inverse-surface md:h-[330px]">
        {/* Erst nach dem Laden entscheiden: eigenes Foto des Betriebs, sonst das Standardbild.
            Waehrend des Ladens kein Bild, sonst blitzt erst das Standardfoto auf (Antoine 14.10.2026). */}
        {offer && (
          <img
            src={offer.branding?.hero_image_url || "/img/hero-v2.webp"}
            alt=""
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-[50%_60%]"
            style={fokusStil(offer.branding?.hero_focal_point)}
            data-testid="angebot-hero-bild"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-inverse-surface/25 to-inverse-surface/70" />
        <div className="relative flex h-full flex-col justify-end gap-2 px-margin-mobile pb-7 pt-4 text-inverse-on-surface md:px-margin-desktop">
          <p className="font-label-md text-label-md font-semibold uppercase tracking-[0.07em] opacity-85">
            {nachtragOffen ? betriebsName(offer?.contact?.company_name) : t.heroLabel}
          </p>
          <h1 className="font-serif text-[34px] font-semibold leading-[1.05] md:text-[44px]">
            {/* Posteingang S2: nie die Vorgangsnummer als Überschrift fuer den Gast — sie steht klein darunter.
                Buchungsstrecke A3: der echte Anlass („Ihr 60. Geburtstag“), sonst „Ihre Veranstaltung“. */}
            {anlassUeberschrift(offer?.event.occasion, lang)}
          </h1>
          {offer?.event.number && (
            <p className="font-label-md text-label-md tracking-[0.07em] opacity-70">{offer.event.number}</p>
          )}
          {offer && (
            <p className="max-w-2xl font-body-md text-[15px] opacity-90">
              {offer.event.event_date ? formatDate(offer.event.event_date, dateLocale) : ""}
              {offer.event.guest_count ? ` · ${t.guestsSuffix(offer.event.guest_count)}` : ""}
              {nachtragOffen ? " (bisher gebucht)" : ""}
            </p>
          )}
          {offer?.event.offer_valid_until && !nachtragOffen && (
            <div className="mt-1 inline-flex items-center gap-2 self-start rounded-full border border-inverse-on-surface/30 bg-inverse-on-surface/15 px-3.5 py-1.5 font-label-md text-label-md font-semibold">
              {t.validUntil(formatDate(offer.event.offer_valid_until, dateLocale))}
            </div>
          )}
        </div>
      </header>
      {offer && (
        <div className="border-b border-outline-variant/30 bg-surface-container-lowest px-margin-mobile py-8 text-center dark:border-outline/30 dark:bg-inverse-surface md:px-margin-desktop">
          <p className="mx-auto max-w-2xl font-body-lg text-body-lg text-secondary dark:text-secondary-fixed-dim" data-testid={nachtragOffen ? "nachtrag-einleitung" : undefined}>
            {nachtragOffen
              ? `${betriebsName(offer.contact?.company_name)} hat Ihre Buchung${
                  offer.event.occasion ? ` für ${anlassAkkusativ(offer.event.occasion, lang)}` : ""
                } angepasst. Bitte prüfen Sie die Änderung und teilen Sie uns Ihre Entscheidung mit.`
              : t.intro(
              offer.event.event_date || anlassDa
                ? t.introFor(anlassAkkusativ(offer.event.occasion, lang), offer.event.event_date ? formatDate(offer.event.event_date, dateLocale) : null)
                : "",
              offer.event.guest_count ? ` (${t.guestsSuffix(offer.event.guest_count)})` : "",
            )}
          </p>
          {/* Buchungsstrecke C2: nur bei laufender Terminreservierung (Server entscheidet) und nur,
              solange das Angebot offen ist — nicht nach Zusage/Absage, nicht nach Ablauf. */}
          {offer.fassungVeraltet === true && (phase === "ready" || phase === "submitting") && !expired && (
            <p data-testid="fassung-wird-aktualisiert" role="status" className="mb-4 rounded-[11px] border border-outline-variant/60 bg-surface-container-low p-3 text-sm font-bold">
              {t.fassungAktualisiert}
            </p>
          )}
          <TerminReservierungHinweis reservierung={phase === "ready" && !expired ? offer.terminReservierung : null} lang={lang} />
          {/* B5 (UX-Polish 25.08.2026) — der Ersatz fuer das PDF, das die Mail NIE angehaengt hat.
              Bis heute versprach der Senden-Dialog „Angebot als Link und PDF anfügen", waehrend
              keine einzige Versandstelle `attachments` uebergibt und serverseitig ueberhaupt kein
              PDF existiert. Statt eins zu bauen (Cloudflare Browser Rendering, ein eigener
              Adapter, laufende Kosten) druckt die Seite sich selbst: JEDER Browser bietet in
              seinem Druckdialog „Als PDF sichern" an — Windows, macOS, iOS, Android. Das Ergebnis
              traegt genau das, was die Kundin auch am Bildschirm gesehen hat, und kann nie
              veralten oder von der Seite abweichen.
              Nur im entscheidungsoffenen Zustand: in `done`/`declined`/`expired` steht auf dem
              Schirm eine Bestaetigungskarte, kein Angebot — ein Druckknopf daneben ergaebe ein
              leeres Blatt. */}
          {(phase === "ready" || phase === "submitting") && (
            <div className="maestro-druck-aus mt-5 flex flex-wrap items-center gap-3">
              {/* Einblicke (Migration 730): die beim Versand aufbewahrte PDF ueber den persoenlichen
                  Link (GET /api/public/offer/:token/pdf). Nur, wenn eine aufbewahrte PDF existiert
                  und nicht in der Betreiber-Vorschau. Der Abruf wird serverseitig gezaehlt. */}
              {!vorschau && token && (offer?.pdfAnzahl ?? 0) > 0 &&
                Array.from({ length: Math.min(offer?.pdfAnzahl ?? 0, 6) }, (_, i) => i + 1).map((nr) => (
                  <a
                    key={nr}
                    href={`/api/public/offer/${encodeURIComponent(token)}/pdf?nr=${nr}`}
                    download
                    data-testid="angebot-pdf"
                    onClick={() => meldeEreignis("pdf_geladen")}
                    className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/60 bg-surface px-4 py-2.5 font-body-sm text-body-sm font-semibold text-on-surface transition-shadow hover:shadow-md dark:border-outline/30 dark:bg-inverse-surface dark:text-inverse-on-surface"
                  >
                    <Icon name="picture_as_pdf" className="text-[18px]" />
                    {(offer?.pdfAnzahl ?? 0) > 1 ? t.pdfCtaNr(nr) : t.pdfCta}
                  </a>
                ))}
              <button
                type="button"
                data-testid="angebot-drucken"
                onClick={() => {
                  // Einblicke (730): Drucken/als PDF sichern ist dieselbe Absicht wie der PDF-Knopf.
                  meldeEreignis("pdf_geladen");
                  window.print();
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/60 bg-surface px-4 py-2.5 font-body-sm text-body-sm font-semibold text-on-surface transition-shadow hover:shadow-md dark:border-outline/30 dark:bg-inverse-surface dark:text-inverse-on-surface"
                title={t.printHint}
              >
                <Icon name="print" className="text-[18px]" /> {t.printCta}
              </button>
            </div>
          )}
        </div>
      )}

      <main className="mx-auto w-full max-w-container-max flex-grow px-margin-mobile py-12 md:px-margin-desktop">
        <div className="mx-auto max-w-5xl space-y-12">
          {phase === "loading" && (
            <>
              <div className="h-64 animate-pulse rounded-xl bg-surface-container-high dark:bg-surface-variant/20" />
              <div className="h-64 animate-pulse rounded-xl bg-surface-container-high dark:bg-surface-variant/20" />
            </>
          )}

          {phase === "error" && (
            <div className="mx-auto max-w-2xl rounded-xl bg-error-container p-10 text-center text-on-error-container">
              <Icon name="link_off" className="text-5xl" />
              <p className="mt-4 font-headline-sm text-headline-sm">{error}</p>
              <p className="mt-2 font-body-sm text-body-sm opacity-80">
                Bitte wenden Sie sich direkt an Ihren Ansprechpartner.
              </p>
              {/* CX-Audit 2026-07-17: vorher Sackgasse ohne jeden Kontaktweg. */}
              <a
                href="/chat"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-surface-container-lowest px-4 py-2.5 font-body-sm text-body-sm font-semibold text-on-surface shadow-sm transition-shadow hover:shadow-md dark:bg-inverse-surface dark:text-inverse-on-surface"
              >
                <Icon name="chat" className="text-[18px]" /> {t.contactCta}
              </a>
            </div>
          )}

          {/* E4.6: Absage-Karte — derselbe respektvolle Abschluss wie bei der Annahme,
              nur ohne Feier-Ikonografie. Kontaktweg bleibt sichtbar (Footer). */}
          {phase === "declined" && offer && (
            <div className="mx-auto max-w-2xl rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-10 text-center shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10 text-secondary dark:bg-secondary-fixed-dim/10 dark:text-secondary-fixed-dim">
                <Icon name="waving_hand" className="text-4xl" />
              </div>
              <h2 className="mt-6 font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">
                {t.declinedTitle}
              </h2>
              <p className="mx-auto mt-3 max-w-md font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
                {t.declinedBody}
              </p>
            </div>
          )}

          {/* Nachtrag-Version (Einheit 4): die Änderung steht VOR der Bestätigungskarte der bisherigen Buchung. Ist nichts
              mehr zu zahlen, ersetzt sie die Karte ganz (Mockup 30.10.2026); ein offener Zahlungsweg der bisherigen Buchung bleibt sichtbar. */}
          {offer?.nachtrag && (
            <NachtragGast offer={offer} token={token} vorschau={vorschau} onVeraltet={() => setReloadKey((k) => k + 1)} />
          )}

          {phase === "done" && offer && !(offer.nachtrag && !offer.deposit?.checkout_url && !offer.restforderung?.zahlung_offen) && (
            /* Mockup „Zubuchen“, Zustand „angenommen“ (Antoine 31.10.2026): EIN Inhaltsrahmen wie im Mockup
               (max. 1176 px = 1240 px Seite minus 2 × 32 px Polster) für Statuskarte, Banner, Überschrift und
               Karte + Kasten — alle auf derselben linken Kante, Überschriften in Newsreader, Text in Manrope. */
            <div className="gast-rahmen text-left font-sans" data-testid="gast-rahmen">
            <div className="rounded-[18px] border border-outline bg-[#fffdf8] p-[22px] text-left dark:border-outline/30 dark:bg-inverse-surface md:px-7 md:py-6" data-testid="angebot-status">
              <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-on-surface-variant dark:bg-inverse-surface dark:text-secondary-fixed-dim">
                <Icon name={erstattet ? "currency_exchange" : "celebration"} className="text-[24px]" />
              </div>
              <div className="min-w-0">
              <h2 className="font-serif text-[24px] font-medium leading-tight text-on-surface dark:text-inverse-on-surface">
                {erstattet ? t.erstattetTitle : nochNichtFix ? t.fastFixTitle : t.doneTitle}
              </h2>
              <p className="mt-1 max-w-[640px] text-[15px] leading-normal text-on-surface-variant dark:text-secondary-fixed-dim">
                {erstattet
                  ? t.erstattetBody
                  : nochNichtFix
                    ? t.fastFixBody(anlassAkkusativ(offer.event.occasion, lang), fastFixVollzahlung, fixGrund === "kostenuebernahme")
                    : t.doneBody(anlassAkkusativ(offer.event.occasion, lang))}
              </p>
              </div>
              </div>
              {/* Unterkarten (Zahlung/Kostenübernahme, Vorfreude) nebeneinander statt schmal zentriert. */}
              <div className="gast-status-teile mt-5 grid items-start gap-4 empty:hidden md:grid-cols-2">
              {/* Buchungsstrecke C3: Tage bis zum Fest, nächste Schritte, Kalendereintrag (sql/597). */}
              {!erstattet && !vorschau && <VorfreudeKarte token={token} lang={lang} offen={fixGrund} />}

              {/* H-15: Anzahlung - der beim Annehmen erzeugte Zahlungsweg wird angezeigt (und
                  bleibt dank Persistenz an der Zahlungszeile auch nach Reload abrufbar). */}
              {offer.deposit && !erstattet && (
                <div className="rounded-[14px] border border-outline-variant bg-surface-container-low p-5 text-left dark:border-outline/30 dark:bg-inverse-surface">
                  <h3 className="font-serif text-[19px] font-medium leading-snug text-on-surface dark:text-inverse-on-surface">
                    {istVollzahlung ? t.vollzahlungHeading : t.depositHeading}
                  </h3>
                  {/* WARTESCHLANGE Punkt 26 (Antoine, 15./16.09.2026, HAT PRIO): "Wenn der Kunde
                      auf seiner Public-Offer-Seite den Stripe-Link bekommt, muss er zum Zahlen
                      auch immer die Rechnung sehen können." Die Anzahlungsrechnung kann bereits
                      VOR Zahlungseingang existieren (lib/auto-invoice.ts#
                      createDepositInvoiceOnAcceptance) - deshalb hier UNABHAENGIG vom
                      Zahlungsstatus direkt unter der Überschrift, neben jedem der drei Zustände
                      darunter (bezahlt/Zahlungslink/kein Link). Erscheint erst gar nicht, wenn die
                      Rechnungserstellung fehlgeschlagen ist (kein LexOffice-Key/unvollständige
                      Adresse) - die Annahme bleibt in diesem Fall trotzdem gültig. */}
                  {offer.deposit.invoice && (
                    <a
                      href={offer.deposit.invoice.pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 font-body-sm text-body-sm text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                    >
                      <Icon name="description" className="text-[16px]" />
                      {offer.deposit.invoice.invoiceNumber ? t.invoiceCtaWithNumber(offer.deposit.invoice.invoiceNumber) : t.invoiceCta}
                    </a>
                  )}
                  {/* E3 (Antoine: „Gastseite zeigt Netto, MwSt und Brutto“): die Anzahlung je
                      USt-Satz, server-gerechnet wie die Anzahlungsrechnung — hier nur angezeigt. */}
                  {offer.deposit.aufschluesselung && (
                    <dl data-testid="anzahlung-aufschluesselung" className="mt-3 space-y-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <div className="flex justify-between gap-4">
                        <dt>{t.betragNetto}</dt>
                        <dd className="tabular-nums">{money(offer.deposit.aufschluesselung.netCents)}</dd>
                      </div>
                      {offer.deposit.aufschluesselung.saetze
                        .filter((s) => s.rateBp > 0)
                        .map((s) => (
                          <div key={s.rateBp} className="flex justify-between gap-4">
                            <dt>{t.betragMwst((s.rateBp / 100).toLocaleString(lang === "en" ? "en-GB" : "de-DE"))}</dt>
                            <dd className="tabular-nums">{money(s.vatCents)}</dd>
                          </div>
                        ))}
                      <div className="flex justify-between gap-4 border-t border-outline-variant/30 pt-1 font-semibold text-on-surface dark:border-outline/30 dark:text-inverse-on-surface">
                        <dt>{t.betragBrutto}</dt>
                        <dd className="tabular-nums">{money(offer.deposit.aufschluesselung.grossCents)}</dd>
                      </div>
                    </dl>
                  )}
                  {/* Punkt 109 (sql/509): nach einer Teil-Rückerstattung zählt netto — fehlt
                      Geld, steht der Rest als offen da statt „bezahlt“. Zahlungsweg unverändert. */}
                  {/* sql/727: bei Restforderung steht der offene Betrag in der eigenen Karte darunter. */}
                  {offer.deposit.status === "succeeded" && offer.restforderung ? null : offer.deposit.status === "succeeded" && (offer.deposit.offen_cents ?? 0) > 0 ? (
                    <p className="mt-3 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <Icon name="info" className="text-[20px] text-secondary dark:text-secondary-fixed-dim" />
                      {t.depositRestOffen(money(offer.deposit.offen_cents ?? 0))}
                    </p>
                  ) : offer.deposit.status === "succeeded" ? (
                    <p className="mt-3 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <Icon name="check_circle" className="text-[20px] text-tertiary dark:text-tertiary-fixed-dim" />
                      {istVollzahlung ? t.vollzahlungPaid(money(offer.deposit.amount_cents)) : t.depositPaid(money(offer.deposit.amount_cents))}
                    </p>
                  ) : offer.deposit.checkout_url ? (
                    <>
                      <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                        {istVollzahlung ? t.vollzahlungBody(money(offer.deposit.amount_cents)) : t.depositBody(money(offer.deposit.amount_cents))}
                      </p>
                      {/* Feste Leiste (Antoine, 14.10.2026): der Zahlknopf steht immer sichtbar unten. */}
                      <FesteLeiste
                        testId="feste-leiste-zahlung"
                        info={`${istVollzahlung ? t.vorschauArtVollzahlung : t.vorschauArtAnzahlung} ${money(offer.deposit.amount_cents)}`}
                      >
                        <ButtonLink
                          href={offer.deposit.checkout_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 !px-4 !py-2.5"
                        >
                          <Icon name="credit_card" className="text-[18px]" /> {istVollzahlung ? t.vollzahlungCta : t.depositCta}
                        </ButtonLink>
                      </FesteLeiste>
                    </>
                  ) : (
                    <>
                      {/* WARTESCHLANGE Punkt 49c: Der Hinweis „Sie erhalten die Zahlungsinformationen
                          separat" gilt nur noch dort, wo der Gast hier wirklich nichts tun kann —
                          in der Vorschau. Sonst steht der Zahlen-Knopf darunter, und ein Satz, der
                          aufs Warten vertroestet, waere daneben schlicht falsch. */}
                      {vorschau && (
                        <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.depositNoLink}</p>
                      )}
                      {/* WARTESCHLANGE Punkt 9 (14.09.2026) hat den Weg gebaut, Punkt 49c
                          (18.09.2026) nimmt dem Gast den Umweg ab: derselbe Knopf, derselbe Text
                          wie im Normalfall — und er fuehrt direkt zu Stripe, statt erst einen
                          zweiten Knopf erscheinen zu lassen. Nicht in der Vorschau (keine
                          Gast-Willenserklaerung/kein Token dort moeglich). */}
                      {!vorschau && depositKeineOffen && (
                        <p data-testid="anzahlung-keine-offen" className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.depositKeineOffen}</p>
                      )}
                      {!vorschau && !depositKeineOffen && (
                        <>
                          <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                            {istVollzahlung ? t.vollzahlungBody(money(offer.deposit.amount_cents)) : t.depositBody(money(offer.deposit.amount_cents))}
                          </p>
                          {/* Design-Komponenten-Ratsche (apps/web/test/design-komponenten-bestand.test.ts):
                              die Button-Komponente aus ui/components.tsx statt eines rohen Buttons,
                              damit der dokumentierte Bestand nicht waechst. */}
                          <FesteLeiste
                            testId="feste-leiste-zahlung"
                            info={`${istVollzahlung ? t.vorschauArtVollzahlung : t.vorschauArtAnzahlung} ${money(offer.deposit.amount_cents)}`}
                          >
                            <Button
                              type="button"
                              onClick={retryDeposit}
                              disabled={depositRetryBusy}
                              className="inline-flex items-center gap-2 !px-4 !py-2.5 disabled:opacity-60"
                            >
                              <Icon name="credit_card" className="text-[18px]" />
                              {depositRetryBusy ? t.depositRetryBusy : istVollzahlung ? t.vollzahlungCta : t.depositCta}
                            </Button>
                          </FesteLeiste>
                          {depositRetryError && (
                            <p className="mt-3 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{depositRetryError}</p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* sql/727 (Antoine 16.10.2026, „Offenen Betrag + Zahlen-Knopf“): Restforderung nach
                  Teilerstattung. Betrag kommt fertig vom Server (event_deckung), nie gerechnet. */}
              {offer.restforderung && offer.restforderung.offen_cents > 0 && !erstattet && (
                <div data-testid="restforderung" className="rounded-[14px] border border-outline-variant bg-surface-container-low p-5 text-left dark:border-outline/30 dark:bg-inverse-surface">
                  <h3 className="font-serif text-[19px] font-medium leading-snug text-on-surface dark:text-inverse-on-surface">{t.restHeading}</h3>
                  <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    {t.restBody(money(offer.restforderung.offen_cents))}
                  </p>
                  {!vorschau && (offer.restforderung.checkout_url || offer.restforderung.zahlung_offen) ? (
                    <FesteLeiste testId="feste-leiste-restforderung" info={`${t.restInfo} ${money(offer.restforderung.offen_cents)}`}>
                      {offer.restforderung.checkout_url ? (
                        <ButtonLink href={offer.restforderung.checkout_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 !px-4 !py-2.5">
                          <Icon name="credit_card" className="text-[18px]" /> {t.restCta}
                        </ButtonLink>
                      ) : (
                        <Button type="button" onClick={retryRest} disabled={depositRetryBusy} className="inline-flex items-center gap-2 !px-4 !py-2.5 disabled:opacity-60">
                          <Icon name="credit_card" className="text-[18px]" />
                          {depositRetryBusy ? t.depositRetryBusy : t.restCta}
                        </Button>
                      )}
                    </FesteLeiste>
                  ) : (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.depositNoLink}</p>
                  )}
                  {depositRetryError && (
                    <p className="mt-3 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{depositRetryError}</p>
                  )}
                </div>
              )}

              {/* § 8 (Migration 345): paymentPathway='kostenuebernahme' - bewusst KEIN
                  automatischer Zahlungslink (H-41-Konsistenz), stattdessen dieser Hinweis. Nur
                  zeigen, solange kein Zahlungsweg (Anzahlung/Deposit) bereits existiert. */}
              {isKostenuebernahme && !offer.deposit && !erstattet && (
                <div className="rounded-[14px] border border-outline-variant bg-surface-container-low p-5 text-left dark:border-outline/30 dark:bg-inverse-surface">
                  <h3 className="font-serif text-[19px] font-medium leading-snug text-on-surface dark:text-inverse-on-surface">{t.kostenuebernahmeHeading}</h3>
                  <p className="mt-2 flex items-start gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="draft" className="mt-0.5 shrink-0 text-[20px] text-secondary dark:text-secondary-fixed-dim" />
                    {t.kostenuebernahmeBody}
                  </p>
                </div>
              )}

              </div>
            </div>
            {/* sql/772: Menüwahl NACH der Buchung - der Betrieb hat die Auswahl mit Frist geschickt, der Gast verteilt die Gäste. */}
            {angenommeneVariante && offer.event.menuewahl && menuewahlenAus(angenommeneVariante).length > 0 && !erstattet && (
              <MenuewahlGastNachBuchung
                lang={lang}
                markenfarbe={offer.markenfarbe}
                token={vorschau ? null : (token ?? null)}
                vorschau={vorschau}
                option={angenommeneVariante}
                lage={offer.event.menuewahl}
                onGespeichert={() => setReloadKey((k) => k + 1)}
              />
            )}
            {/* Nach der Annahme (Mockup „Zubuchen“, Zustand „angenommen“): die gewählte Karte im Kartendesign, nur lesend. */}
            <section className="mt-5" data-testid="gebuchte-auswahl">
              <p className="flex items-start gap-3 rounded-[14px] border border-outline bg-[#fffdf8] px-[18px] py-4 text-left text-[13.5px] leading-normal text-on-surface-variant dark:border-outline/30 dark:bg-inverse-surface dark:text-secondary-fixed-dim" data-testid="angenommen-banner">
                <Icon name="check_circle" className="shrink-0 text-[22px] text-tertiary dark:text-tertiary-fixed-dim" />
                <span>
                  <b className="block font-serif text-[19px] font-medium leading-snug text-on-surface dark:text-inverse-on-surface">{erstattet ? t.damaligeAuswahl : t.angenommenBannerTitel}</b>
                  {erstattet ? null : nochNichtFix ? (fixGrund === "kostenuebernahme" ? t.angenommenBannerKu : t.angenommenBannerZahlung) : t.angenommenBanner}
                </span>
              </p>
              <div className="mb-[18px] mt-[34px] max-w-[640px]">
                <h2 className="font-serif text-[25px] font-medium leading-[1.2] text-on-surface dark:text-inverse-on-surface md:text-[28px]">{erstattet ? t.damaligeAuswahl : t.menuGebuchtHeading}</h2>
                <p className="mt-1.5 text-[15px] leading-normal text-on-surface-variant dark:text-secondary-fixed-dim">{erstattet ? "\u00A0" : t.angenommenIntro}</p>
              </div>
              {(() => {
                const v = angenommeneVariante;
                const speisen = (v?.items ?? []).filter((it) => it.section === "menu" || it.section === "drinks");
                const gebucht = v?.chosen_addons ?? [];
                if (!v || (speisen.length === 0 && gebucht.length === 0)) {
                  return (
                    <p className="text-center font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.menuLeer}</p>
                  );
                }
                const gebuchtWahl: Record<string, number> = {};
                for (const a of gebucht) gebuchtWahl[a.itemId] = typeof a.gaeste === "number" ? a.gaeste : a.qty;
                const gebuchteMengen = v.chosen_alternatives ?? mengen;
                return (
                  <AngebotsAuswahl
                    offer={offer}
                    lang={lang}
                    optionen={[v]}
                    selected={v.id}
                    onSelect={() => undefined}
                    wahl={gebuchtWahl}
                    onWahl={() => undefined}
                    mengen={gebuchteMengen}
                    onMenge={() => undefined}
                    menuWahl={menuWahl}
                    onMenuWahl={(g, i) => void waehleGericht(g, i)}
                    angenommen
                    ohneMenuewahl={offer.event.menuewahl?.offen === true}
                    verbindlich={!nochNichtFix}
                    neutral={erstattet}
                    serverGesamtCents={offer.event.amount_total_cents}
                    angenommenZeile={erstattet ? t.damaligeAuswahl + "." : nochNichtFix ? (fixGrund === "kostenuebernahme" ? t.angenommenKastenKu : t.angenommenKastenZahlung) : undefined}
                    nurAnsicht={erstattet}
                    vorschau={vorschau}
                    markenfarbe={offer.markenfarbe}
                    anzahlung={offer.deposit && !erstattet && offer.deposit.amount_cents > 0 ? { cents: offer.deposit.amount_cents, vollzahlung: istVollzahlung, spaeter: false } : null}
                    onAnnehmen={() => undefined}
                    annehmenMoeglich={false}
                    blattOffen={false}
                    onBlatt={() => undefined}
                    speicherFehler={false}
                    foto={(idx) => ({
                      src: offer.branding?.option_photo_urls?.[idx % 3] || OPTION_PHOTOS[idx % OPTION_PHOTOS.length]!,
                      stil: fokusStil(offer.branding?.option_focal_points?.[idx % 3]),
                    })}
                    sektionLabel={(section) => sectionLabel[section] ?? section}
                    positionsName={(it, o) => {
                      const baseName = it.translations?.[lang]?.name ?? it.name;
                      return it.guest_count != null && it.price_mode === "per_person" && it.guest_count < o.guest_count
                        ? t.teilgruppe(it.guest_count, baseName, money(it.unit_price_cents))
                        : baseName;
                    }}
                    zusatz={() => []}
                    aLaCarte={istALaCarteOhneBetrag}
                    proPersonCents={(o) => v2?.proPersonCents[o.id] ?? (o.guest_count > 0 ? Math.round(o.amount_total_cents / o.guest_count) : null)}
                    optionalTag={t.optionalTag}
                    surchargeTag={t.surchargeTag}
                    fehltZeigen={false}
                    onFehltZeigen={() => undefined}
                  />
                );
              })()}
              <div className="max-w-[640px] text-left">
                {/*
                  Punkt 61: Rueckmeldung zur Wahl. Der offene Rest steht als Zahl da, statt dass der
                  Gast selbst durch die Gaenge zaehlen muss, was er noch nicht entschieden hat —
                  und ein Fehlschlag wird ausgesprochen, weil der Knopf beim Rollback sonst
                  kommentarlos zurueckspringt.
                */}
                {(() => {
                  const speisen = (angenommeneVariante?.items ?? []).filter(
                    (it) => it.section === "menu" || it.section === "drinks",
                  );
                  const offen = offeneWahlgruppen(
                    speisen.map((it) => ({ courseLabel: it.course_label, choiceGroupId: it.choice_group_id ?? null })),
                    menuWahl,
                  );
                  const gesamt = offeneWahlgruppen(
                    speisen.map((it) => ({ courseLabel: it.course_label, choiceGroupId: it.choice_group_id ?? null })),
                    {},
                  );
                  if (gesamt === 0 || angenommeneVariante?.chosen_alternatives) return null;
                  return (
                    <>
                      {wahlFehler && (
                        <p className="mt-3 font-body-sm text-body-sm text-error dark:text-error">{t.wahlFehler}</p>
                      )}
                      {offen > 0 ? (
                        <>
                          <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.wahlAufforderung}</p>
                          <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.wahlOffen(offen)}</p>
                        </>
                      ) : (
                        <p className="mt-3 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                          <Icon name="check_circle" className="text-[20px] text-tertiary dark:text-tertiary-fixed-dim" />
                          {t.wahlGespeichert}
                        </p>
                      )}
                    </>
                  );
                })()}
                {offer.event.menu_confirmed_at && (
                  <p className="mt-4 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="check_circle" className="text-[20px] text-tertiary dark:text-tertiary-fixed-dim" />
                    {t.menuConfirmedAt(formatDate(offer.event.menu_confirmed_at, dateLocale))}
                  </p>
                )}
              </div>
            </section>
          </div>
          )}

          {(phase === "ready" || phase === "submitting") && offer && (
            <>
              {/* Buchungsstrecke B2: persoenliche Notiz (Anschreiben der versendeten Fassung) + Ansprechpartner. */}
              {v2 && <PersoenlicheNotiz v2={v2} lang={lang} />}
              <BegruessungsVideo v2={v2} lang={lang} token={token} vorschau={vorschau} />
              {/*
                Kartenbreite folgt der Anzahl aktiver Varianten (seit 12.10.2026 ohne Obergrenze -
                ab der vierten bricht das 3-Spalten-Grid in die naechste Zeile um): eine einzelne Karte auf volle Grid-Breite gestreckt sah riesig
                und leer aus, zwei Karten in einem 3er-Grid blieben auf 2/3 der Breite gequetscht,
                mit leerer dritter Spalte. Ab 3 Varianten bleibt das bisherige volle 3-Spalten-Grid.
              */}
              {/*
                ═══ Die Auswahl: Menükarten, „Auf Wunsch dazu“, Gäste je Gericht, Kasten „Ihre Auswahl“ ═══

                Eine Komponente (`ui/AngebotsAuswahl.tsx`) zeichnet alles, was der Gast vor der Zusage
                wählt — aus dem Angebot, nichts davon ist an einen Betrieb oder eine Zahl gebunden. Die
                Seite hält nur den Zustand (`addonWahl`, `mengen`, `selected`) und die Anzahlung vom Server.
              */}
              {(() => {
                const zahlAnz = zahlungNachAnnahme(selected ?? undefined);
                const anzahlung: AnzahlungAnzeige | null =
                  paymentPathway === "kostenuebernahme" || !selected
                    ? null
                    : zahlAnz === "spaeter"
                      ? { cents: null, vollzahlung: false, spaeter: true }
                      : zahlAnz.betrag
                        ? { cents: zahlAnz.betrag.betragCents, vollzahlung: zahlAnz.betrag.vollzahlung, spaeter: false, prozentBp: zahlAnz.betrag.prozentBp ?? null }
                        : null;
                const zusaetze = zusatzZeilen(offer.options);
                return (
                  <AngebotsAuswahl
                    offer={offer}
                    lang={lang}
                    optionen={variantenReihenfolge(offer.options)}
                    selected={selected}
                    onSelect={(optId) => {
                      setSelected(optId);
                      meldeEreignis("variante_gewaehlt");
                    }}
                    wahl={addonWahl}
                    onWahl={(aenderung) => setAddonWahl((prev) => ({ ...prev, ...aenderung }))}
                    mengen={mengen}
                    onMenge={setzeMenge}
                    menuWahl={menuWahl}
                    onMenuWahl={(g, i) => void waehleGericht(g, i)}
                    // Vorschau: bedienbar (nur im Browser, siehe setzeMenge/waehleGericht), nur „annehmen“ bleibt aus.
                    nurAnsicht={isKostenuebernahme || expired || erstattet}
                    vorschau={vorschau}
                    markenfarbe={offer.markenfarbe}
                    anzahlung={anzahlung}
                    onAnnehmen={() => {
                      setBlattOffen(false);
                      scrolleZu(!billingComplete ? "rechnungsadresse" : !agbAccepted ? "agb-zustimmung" : "annahme-bereich");
                    }}
                    annehmenMoeglich={(phase === "ready" || phase === "submitting") && !expired && !gueltigeKueBlock}
                    blattOffen={blattOffen}
                    onBlatt={setBlattOffen}
                    speicherFehler={mengenFehler}
                    foto={(idx) => ({
                      src: offer.branding?.option_photo_urls?.[idx % 3] || OPTION_PHOTOS[idx % OPTION_PHOTOS.length]!,
                      stil: fokusStil(offer.branding?.option_focal_points?.[idx % 3]),
                    })}
                    sektionLabel={(section) => sectionLabel[section] ?? section}
                    positionsName={(it, o) => {
                      const baseName = it.translations?.[lang]?.name ?? it.name;
                      return it.guest_count != null && it.price_mode === "per_person" && it.guest_count < o.guest_count
                        ? t.teilgruppe(it.guest_count, baseName, money(it.unit_price_cents))
                        : baseName;
                    }}
                    zusatz={(optId) => zusaetze.get(optId) ?? []}
                    aLaCarte={istALaCarteOhneBetrag}
                    proPersonCents={(o) => v2?.proPersonCents[o.id] ?? (o.guest_count > 0 ? Math.round(o.amount_total_cents / o.guest_count) : null)}
                    kartenZusatz={(o, gewaehlt) => {
                      if (!(vorschau && gewaehlt && !istALaCarteOhneBetrag(o))) return null;
                      const zeile = zahlungsstandZeile(zahlungen, o.amount_total_cents);
                      return zeile ? (
                        <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[12px] text-[#6F675A]">
                          <Icon name="payments" className="mt-px shrink-0 text-[14px]" />
                          <span className="min-w-0 break-words">{zeile}</span>
                        </p>
                      ) : null;
                    }}
                    optionalTag={t.optionalTag}
                    surchargeTag={t.surchargeTag}
                    fehltZeigen={fehltZeigen}
                    onFehltZeigen={() => setFehltZeigen(true)}
                  />
                );
              })()}

              {/* LMIV-Legende: Codes ausschreiben + „auf Anfrage"-Hinweis bei ungepflegten
                  Positionen. Pflichtinformation VOR der verbindlichen Annahme. */}
              {hasFoodItems && (
                <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                  <h4 className="flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                    <Icon name="info" className="text-[16px] text-outline dark:text-outline" /> {t.allergenHeading}
                  </h4>
                  {usedAllergenCodes.length > 0 ? (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {usedAllergenCodes.map((code) => `${code} = ${lmivLabel(code, lang)}`).join(" · ")}
                    </p>
                  ) : !hasUnresolvedAllergens ? (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.allergenNone}</p>
                  ) : null}
                  {hasUnresolvedAllergens && (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.allergenOnRequest}</p>
                  )}
                </div>
              )}

              {/*
                Buchungsstrecke B2 (Mockup Station 4): echte Bewertungen, „Frage an …“ und „Mit
                Mitentscheider teilen“ — vor dem Annahmebereich. Die Frage laeuft ueber den
                bestehenden Nachrichtenweg unten (gleiches Formular, gleicher Versand).
              */}
              {v2 && (
                <div className="space-y-6">
                  <Bewertungen v2={v2} lang={lang} betrieb={tenantLabel} />
                  {!expired && <FrageKarte v2={v2} lang={lang} onFrage={() => oeffneNachricht("frage")} />}
                  <TeilenKnopf url={gastLink} lang={lang} betrieb={tenantLabel} deaktiviert={vorschau} />
                </div>
              )}

              {/* H-16: abgelaufen -> nur noch lesend, Ablauf-Karte statt Anmerkungen + CTA. */}
              {expired ? (
                <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 text-center shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                  <Icon name="schedule" className="text-[32px] text-outline dark:text-outline" />
                  <h3 className="mt-3 font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                    {t.expiredHeading}
                  </h3>
                  <p className="mx-auto mt-3 max-w-xl font-body-md text-body-md text-secondary dark:text-secondary-fixed-dim">
                    {t.expiredBody}
                  </p>
                  {/* CX-Audit 2026-07-17: „kontaktieren Sie uns einfach" hatte keinen Kontaktweg.
                      Bevorzugt echte Kontaktdaten (E-Mail/Telefon aus tenant_settings, sql/136),
                      der Chat bleibt als loginfreier Kanal immer dabei. */}
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    {offer.contact?.company_email && (
                      <a href={`mailto:${offer.contact.company_email}`} className="btn-primary inline-flex">
                        <Icon name="mail" className="text-[18px]" /> {offer.contact.company_email}
                      </a>
                    )}
                    {offer.contact?.company_phone && (
                      <a href={`tel:${offer.contact.company_phone.replace(/[^\d+]/g, "")}`} className="btn-secondary inline-flex">
                        <Icon name="call" className="text-[18px]" /> {offer.contact.company_phone}
                      </a>
                    )}
                    <a href="/chat" className={offer.contact?.company_email ? "btn-secondary inline-flex" : "btn-primary inline-flex"}>
                      <Icon name="chat" className="text-[18px]" /> {t.contactCta}
                    </a>
                  </div>
                </div>
              ) : (
              <div
                id="annahme-bereich"
                className="scroll-mt-4 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 shadow-sm dark:border-outline/30 dark:bg-inverse-surface"
              >
                {/*
                  Buchungsstrecke C1 (Mockup Station 5): auf der neuen Seite zuerst das Ja zur
                  Variante (Schritt 1), danach nur die fehlenden Angaben (Schritt 2) — darunter
                  unveraendert Zahlungsbedingungen, AGB-Haekchen, S0-Zusammenfassung und -Knopf.
                */}
                {zusageSchritt1 && (
                  <ZusageSchritt1
                    auswahl={zusageAuswahl}
                    lang={lang}
                    fokus={zusageSchrittGewechselt}
                    onWeiter={zusageWeiter}
                    onAuswahlAendern={() => scrolleZu("varianten-auswahl")}
                    nebenweg={kuWegVerfuegbar ? <KostenuebernahmeNebenweg lang={lang} onOeffnen={kuNebenwegOeffnen} /> : null}
                  />
                )}
                {zweiSchritte && zusageSchritt === 2 && (
                  <>
                    <ZusageSchritt2Kopf auswahl={zusageAuswahl} lang={lang} fokus={zusageSchrittGewechselt} allesDa={zusageAllesDa} onZurueck={zusageZurueck} />
                    <ZusageAngaben
                      lang={lang}
                      offen={zusageKontaktOffen}
                      onOeffnen={(feld) => setZusageKontaktOffen((prev) => ({ ...prev, [feld]: true }))}
                      adresseZeilen={[
                        [billingAdresse.name, billingAdresse.company].map((w) => (w ?? "").trim()).filter(Boolean).join(" · "),
                        (billingAdresse.street ?? "").trim(),
                        (billingAdresse.addition ?? "").trim(),
                        `${(billingAdresse.zip ?? "").trim()} ${(billingAdresse.city ?? "").trim()}`.trim(),
                      ].filter(Boolean)}
                      telefon={billingPhone.trim()}
                      handy={isKostenuebernahme}
                      adresseFormular={billingAdresseFelder}
                      telefonFormular={
                        <div className="grid grid-cols-1 gap-3">
                          {billingTelefonFeld}
                          {billingHandyGrund}
                        </div>
                      }
                      referenzFormular={billingRefFeld}
                      referenzGesetzt={billingRef.trim() !== ""}
                      pflichtHinweis={billingPflichtHinweis}
                    />
                  </>
                )}
                {/* B5: Eingabefeld und Annahme-Geste sind Bildschirm-Bedienung. Auf Papier
                    bleiben die Zahlungs- und Stornobedingungen darunter stehen — genau das, was
                    die Kundin schwarz auf weiss braucht. */}
                {!zusageSchritt1 && (
                <div className={zweiSchritte ? "maestro-druck-aus mt-6" : "maestro-druck-aus"}>
                  <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                    {t.notesHeading}
                  </h3>
                  <textarea
                    rows={4}
                    className="input mt-6 resize-none"
                    placeholder={t.notesPlaceholder}
                    value={notes}
                    onChange={(e) => {
                      meldeEreignis("zusage_begonnen");
                      meldeEreignis("anmerkung_begonnen");
                      setNotes(e.target.value);
                    }}
                  />
                </div>
                )}
                {/* §5.20 (E-K10): Die Checkbox darunter sagt „…akzeptiere die genannten
                    Zahlungsbedingungen" — also stehen sie HIER, vor der Zustimmung, nicht nur
                    im PDF. Zeilen je gewählter Variante (Anzahlung hängt am Variantenbetrag);
                    ohne Auswahl die der ersten Variante. */}
                {(() => {
                  // Migration 731: bei „À la carte“ gibt es keine Zahlungsbedingungen - der Satz ersetzt den
                  // Block, und „Zahlungsbedingungen geöffnet“ (Einblicke) kann hier gar nicht feuern.
                  if (aLaCarteGewaehlt) {
                    return (
                      <p
                        data-testid="a-la-carte-keine-anzahlung"
                        className="mt-6 rounded-xl border border-outline-variant/40 bg-surface p-5 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-inverse-surface dark:text-inverse-on-surface"
                      >
                        {alcT.keineAnzahlung}
                      </p>
                    );
                  }
                  // Ohne Auswahl die Bedingungen der ersten BEPREISTEN Variante (eine À-la-carte-Variante hat keine).
                  const ersteBepreiste = offer.options.find((o) => !istALaCarteOhneBetrag(o)) ?? offer.options[0];
                  const zeilen = offer.paymentTerms?.[selected ?? ""] ?? offer.paymentTerms?.[ersteBepreiste?.id ?? ""] ?? null;
                  if (!zeilen || zeilen.length === 0) return null;
                  // Labels aus apps/api/src/lib/payment-terms.ts (Stornobedingungen/-staffel, de+en).
                  const istStorno = (l: string) => /^(Storno|Cancellation)/.test(l);
                  const zahlZeilen = zeilen.filter((z) => !istStorno(z.label));
                  const stornoZeilen = zeilen.filter((z) => istStorno(z.label));
                  return (
                    <div
                      className={`mt-6 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface${
                        // C1, Schritt 1: am Bildschirm erst in Schritt 2, auf Papier wie bisher immer.
                        zusageSchritt1 ? " hidden print:block" : ""
                      }`}
                    >
                      {/* Antoine, 13.10.2026 (Korrektur): Zahlungsbedingungen „soll immer zu sehen
                          sein", nur die Stornobedingungen zum Ausklappen, Default zu. Im Druck offen. */}
                      <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                        {t.paymentHeading}
                      </h3>
                      {[zahlZeilen, stornoZeilen].map((liste, i) =>
                        liste.length === 0 ? null : (
                          <div key={i}>
                            {i === 1 && (
                              <button
                                type="button"
                                className="mt-3 flex w-full items-center justify-between gap-2 text-left font-body-sm text-body-sm font-semibold text-on-surface print:hidden dark:text-inverse-on-surface"
                                aria-expanded={bedingungenOffen}
                                onClick={() => {
                                  // Einblicke (730): Aufklappen = „Zahlungsbedingungen geöffnet“ (erstes Mal).
                                  if (!bedingungenOffen) meldeEreignis("zahlungsbedingungen_geoeffnet");
                                  setBedingungenOffen((o) => !o);
                                }}
                                data-testid="bedingungen-umschalter"
                              >
                                {t.stornoHeading}
                                <Icon name={bedingungenOffen ? "expand_less" : "expand_more"} className="text-[20px]" />
                              </button>
                            )}
                            <dl
                              className={`mt-3 space-y-2${i === 1 && !bedingungenOffen ? " hidden print:block" : ""}`}
                              data-testid={i === 1 ? "bedingungen-liste" : "zahlungsbedingungen-liste"}
                            >
                              {liste.map((z) => (
                                <div key={z.label} className="font-body-sm text-body-sm">
                                  <dt className="font-semibold text-on-surface dark:text-inverse-on-surface">{z.label}</dt>
                                  {/* Bughunt 01.09.2026: Stornobedingungen tragen echte "\n" aus einem
                                      mehrzeiligen Einstellungsfeld — ohne `whitespace-pre-line` kollabieren sie. */}
                                  <dd className="whitespace-pre-line text-on-surface-variant dark:text-secondary-fixed-dim">{z.value}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        ),
                      )}
                      {/* Antoine, 16.09.2026, zum Bildschirmfoto: „Es sollte hier zu sehen sein,
                          dass im zweiten Step ein Stripe Link kommt, falls dem so ist."

                          Der Gast las bisher die Bedingungen als Text und wusste trotzdem nicht,
                          dass nach dem Annehmen noch ein Zahlungsschritt kommt — und schon gar
                          nicht, über welchen Betrag. Der Knopf zeigt beides und ist ausgegraut:
                          er nimmt die Annahme nicht vorweg, sondern erklaert sie.

                          Der BETRAG kommt fertig vom Server (`zahlungsVorschau`, dieselbe
                          `faelligeZahlungNachAnnahme` wie die Annahme, seit Punkt 117). Hier wird bewusst
                          NICHT gerechnet — sonst zeigte die Seite irgendwann etwas anderes an,
                          als der Zahlungslink abbucht.

                          Nicht gezeichnet wird er bei `kostenuebernahme`: auf diesem Weg entsteht
                          nach der Annahme gar kein Zahlungslink (`lib/offer-accept.ts`,
                          `linkBlockiert`). Ein Zahlknopf waere dort schlicht unwahr. */}
                      {/*
                        Punkt 54 (Antoine 18.09.2026, mit Bildschirmfoto): „Erst Angebot annehmen,
                        dann Zahlen. Zahlen muss darunter stehen, aber ausgegraut sein, wie jetzt
                        schon. Aber die Reihenfolge ist falsch."

                        Der ausgegraute Zahl-Knopf stand hier, also ÜBER dem Annehmen-Bereich — und
                        damit las sich die Seite von oben nach unten als „zahlen, dann annehmen".
                        Genau andersherum ist es. Der Knopf ist nach unten gewandert, direkt unter
                        die Annahme; ausgegraut bleibt er wie bisher. Sein Aufbau steht jetzt bei
                        der Annahme (Suche: `zahlVorschauKnopf`).
                      */}
                    </div>
                  );
                })()}
                {/* Punkt 50 b2 (Migration 453): die AGB des Betriebs — hinter einem Klick, nicht als
                    Textwand (Antoine, 19.09.2026: „Nur AGBs mit Klick zu sehen"). Ohne gepflegte
                    AGB gibt es den Klick nicht, und das Haekchen unten nennt sie nicht — ein
                    Haekchen fuer einen Text, den es nicht gibt, war genau der Befund von Punkt 50. */}
                {!zusageSchritt1 && offer.agb?.url ? (
                  <div className="maestro-druck-aus mt-4">
                    <a
                      href={offer.agb.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                      data-testid="offer-agb-link"
                    >
                      {t.agbAnzeigen}
                    </a>
                  </div>
                ) : null}
                {!zusageSchritt1 && offer.agb?.text && (
                  <div className="maestro-druck-aus mt-4">
                    <button
                      type="button"
                      className="font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                      aria-expanded={agbOffen}
                      onClick={() => setAgbOffen((o) => !o)}
                    >
                      {agbOffen ? t.agbAusblenden : t.agbAnzeigen}
                    </button>
                    {agbOffen && (
                      <div className="mt-3 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface">
                        <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.agbHeading}</h3>
                        <p
                          className="mt-3 whitespace-pre-line font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim"
                          data-testid="offer-agb-text"
                        >
                          {offer.agb.text}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* sql/344 (Antoine 30.08.2026): Rechnungsadresse + Telefon direkt hier
                    miterheben — vor der Annahme-Checkbox, damit "Annehmen" die vollstaendigen
                    Daten schon traegt. Bereits vorhandene Kundenwerte sind vorbefuellt (Effekt
                    oben), leere Pflichtfelder muss der Gast ergaenzen. */}
                {/* C1: auf der neuen Seite fragt Schritt 2 (oben) nach denselben Feldern. */}
                {!zweiSchritte && (
                <div id="rechnungsadresse" className="maestro-druck-aus mt-6 scroll-mt-4 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface">
                  <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.billingHeading}</h3>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      {billingAdresseFelder}
                    </div>
                    {billingTelefonFeld}
                    {/*
                      Punkt 57 (Antoine 18.09.2026): „Handy ist Pflicht für eSign. Das muss dort
                      stehen, da die TAN auf ein Handy geschickt wird."

                      Die Beschriftung sagte bisher nur „Handynummer" — wer eine Festnetznummer
                      eintippt, tut das nicht aus Trotz, sondern weil ihm niemand gesagt hat, warum
                      es eine Mobilnummer sein muss. Der Satz steht direkt unter dem Feld, nicht in
                      einer Fußnote: er ist der Grund für das Feld, nicht eine Randbemerkung.
                    */}
                    {billingHandyGrund}
                    {billingRefFeld}
                  </div>
                  {/* Sichtbare Pflichtfeld-Kennzeichnung (§ 8 Punkt 4) - bisher nur funktional per
                      JS-Gate (billingComplete), ohne sichtbaren Hinweis auf der Seite selbst. */}
                  {billingPflichtHinweis}
                </div>
                )}
                {/* KÜ V2 (S2): ohne Annehmen-Knopf auch kein Annahme-Häkchen (die Unterschrift ist die Annahme). */}
                {!gueltigeKueBlock && !zusageSchritt1 && (
                <label id="agb-zustimmung" className="maestro-druck-aus mt-6 flex scroll-mt-4 cursor-pointer items-start gap-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {/* 24px statt Browser-Standard (~13px): Pflichtfeld, muss auf dem Handy treffbar sein. */}
                  <Checkbox
                    className="mt-0.5 !h-6 !w-6"
                    checked={agbAccepted}
                    onChange={(e) => {
                      meldeEreignis("zusage_begonnen");
                      setAgbAccepted(e.target.checked);
                    }}
                  />
                  {/* AGB-Link STORIA (28.09.2026): das Haekchen nennt die AGB, sobald es sie gibt —
                      als Link (Regelfall) ODER als Volltext. Vorher nur beim Volltext. */}
                  <span>
                    {aLaCarteGewaehlt
                      ? offer.agb?.text || offer.agb?.url
                        ? alcT.agbLabelMitAgb
                        : alcT.agbLabel
                      : offer.agb?.text || offer.agb?.url
                        ? t.agbLabelMitAgb
                        : t.agbLabel}
                  </span>
                </label>
                )}
                {bewertungsbitte?.aktiv && !zusageSchritt1 && (
                  <label className="maestro-druck-aus mt-4 flex cursor-pointer items-start gap-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="bewertung-einwilligung">
                    <Checkbox className="mt-0.5 !h-6 !w-6" checked={bewertungEinwilligung} onChange={(e) => setBewertungEinwilligung(e.target.checked)} />
                    <span>
                      <span className="block text-on-surface dark:text-inverse-on-surface">
                        {lang === "en" ? "Yes, ask me once after the event how I enjoyed it." : "Ja, fragen Sie mich nach der Feier einmal, wie es mir gefallen hat."}
                      </span>
                      <span className="mt-0.5 block text-[12px]">
                        {lang === "en"
                          ? "Voluntary and not required for the booking. You can withdraw at any time in the customer portal or via the link in the email."
                          : "Freiwillig und für die Buchung nicht nötig. Jederzeit widerrufbar im Kundenportal oder über den Link in der E-Mail."}
                        {bewertungsbitte.privacyUrl && (
                          <>
                            {" "}
                            <a href={bewertungsbitte.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">
                              {lang === "en" ? "Privacy" : "Datenschutz"}
                            </a>
                          </>
                        )}
                      </span>
                    </span>
                  </label>
                )}
                {error && (
                  <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">
                    {error}
                  </p>
                )}
                {/* sql/565 (R1): steht schon eine gueltige, unterschriebene Kostenuebernahme (409
                    oben), bleibt der Annehmen-Knopf aus — die Fehlermeldung darueber sagt bereits,
                    warum; ein weiterer Klick koennte ohnehin nichts mehr schreiben. */}
                {!gueltigeKueBlock && !zusageSchritt1 && (
                  <>
                    {/*
                      ═══ Buchungsstrecke S0: die Zusammenfassung direkt über dem Knopf ═══

                      § 312j Abs. 2 BGB verlangt die wesentlichen Punkte unmittelbar vor der
                      Bestellung — und psychologisch gilt dasselbe: was der Gast bestätigt, soll die
                      Zahl sein, die er zuletzt gesehen hat. Datum, Summe, Anzahlung.

                      Punkt 32, Konzept § 7.3 (Zubuchungen) ist hier aufgegangen: die Zeilen
                      „Angebot“ und „Zubuchungen“ erscheinen nur, wenn wirklich etwas dazugebucht
                      ist — ohne Zubuchung stünde dort ein Summand 0,00 €, der nur fragen lässt,
                      was er soll. Gerechnet wird nur zur Anzeige; verbindlich ist, was der Server
                      neu rechnet. Die Anzahlung kommt fertig vom Server (`zahlungsVorschau`,
                      dieselbe Quelle wie der ausgegraute Zahlknopf) — hier wird sie NICHT
                      gerechnet. Bei `kostenuebernahme` entsteht kein Zahlungslink, also auch
                      keine Anzahlungszeile.
                    */}
                    {(() => {
                      const variante = offer.options.find((o) => o.id === selected) ?? null;
                      if (!variante) return null;
                      const zahlAnzeige = zahlungNachAnnahme(variante.id);
                      const zahlVorschau = zahlAnzeige === "spaeter" ? null : zahlAnzeige.betrag;
                      return (
                        <div
                          className="maestro-druck-aus mt-8 rounded-xl border border-outline-variant/60 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-inverse-surface"
                          data-testid="annahme-zusammenfassung"
                        >
                          <h3 className="font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">{t.summaryHeading}</h3>
                          <dl className="mt-3 space-y-1.5 font-body-sm text-body-sm">
                            {offer.event.event_date && (
                              <div className="flex items-baseline justify-between gap-4">
                                <dt className="text-on-surface-variant dark:text-secondary-fixed-dim">{t.summaryDatum}</dt>
                                <dd className="text-right text-on-surface dark:text-inverse-on-surface">{formatDate(offer.event.event_date, dateLocale)}</dd>
                              </div>
                            )}
                            {aLaCarteGewaehlt ? (
                              <div className="flex items-baseline justify-between gap-4 border-t border-outline-variant pt-1.5 dark:border-outline/40" data-testid="annahme-zusammenfassung-a-la-carte">
                                <dt className="font-semibold text-on-surface dark:text-inverse-on-surface">{alcT.summePreis}</dt>
                                <dd className="text-right font-semibold text-on-surface dark:text-inverse-on-surface">{alcT.preisZeile}</dd>
                              </div>
                            ) : (
                              <>
                            {hatZubuchung && (
                              <>
                                <div className="flex items-baseline justify-between gap-4">
                                  <dt className="text-on-surface-variant dark:text-secondary-fixed-dim">{t.addonSummeAngebot}</dt>
                                  <dd className="text-on-surface dark:text-inverse-on-surface">{money(variante.amount_total_cents)}</dd>
                                </div>
                                <div className="flex items-baseline justify-between gap-4">
                                  <dt className="text-on-surface-variant dark:text-secondary-fixed-dim">{t.addonSummeZubuchungen}</dt>
                                  <dd className="text-on-surface dark:text-inverse-on-surface">{money(zubuchungenSummeCents)}</dd>
                                </div>
                              </>
                            )}
                            <div className="flex items-baseline justify-between gap-4 border-t border-outline-variant pt-1.5 dark:border-outline/40">
                              <dt className="font-semibold text-on-surface dark:text-inverse-on-surface">{t.addonSummeGesamt}</dt>
                              <dd className="font-headline-sm text-headline-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                                {money(variante.amount_total_cents + zubuchungenSummeCents)}
                              </dd>
                            </div>
                            {zahlVorschau && zahlVorschau.betragCents > 0 && (
                              <div className="flex items-baseline justify-between gap-4">
                                <dt className="text-on-surface-variant dark:text-secondary-fixed-dim">
                                  {zahlVorschau.vollzahlung ? t.summaryVollzahlung : t.summaryAnzahlung}
                                </dt>
                                <dd className="font-semibold text-on-surface dark:text-inverse-on-surface">{money(zahlVorschau.betragCents)}</dd>
                              </div>
                            )}
                              </>
                            )}
                          </dl>
                          {zahlAnzeige === "spaeter" && (
                            <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="anzahlung-spaeter">
                              {t.anzahlungSpaeter}
                            </p>
                          )}
                          <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="widerrufs-hinweis">
                            {t.widerrufsHinweis}
                          </p>
                        </div>
                      );
                    })()}
                    {/*
                      Feste Leiste (Antoine, 14.10.2026): der Annehmen-Knopf steht immer sichtbar
                      unten — gleiche Bedingungen, gleiches `accept()`, nur ein anderer Ort. Die
                      Zusammenfassung (S0, § 312j) bleibt direkt darüber im Annahmebereich; die
                      Leiste wiederholt Gesamt und Anzahlung knapp. Rechts daneben der ausgegraute
                      Zahlknopf (Punkt 54: erst annehmen, dann zahlen — er steht nach dem Annehmen).
                    */}
                    {(() => {
                      const variante = offer.options.find((o) => o.id === selected) ?? null;
                      // Nachbesserung PR #1495 / Punkt 117: Betrag fertig vom Server, hier nie gerechnet.
                      const zahlAnzeige = zahlungNachAnnahme(selected ?? offer.options[0]?.id);
                      const zahlVorschau = zahlAnzeige === "spaeter" ? null : zahlAnzeige.betrag;
                      const zahlArt = zahlVorschau
                        ? `${zahlVorschau.vollzahlung ? t.vorschauArtVollzahlung : t.vorschauArtAnzahlung}${zahlVorschau.artText ? ` — ${zahlVorschau.artText}` : ""}`
                        : null;
                      const [hinweis, hinweisTestId] = vorschau
                        ? [`${VORSCHAU_DEAKTIVIERT} — annehmen kann nur der Gast über seinen Angebots-Link.`, undefined]
                        : variante && wahlLueckeText
                          ? [wahlLueckeText, "wahl-hinweis"]
                          : variante && !billingComplete
                          ? [t.hintBillingIncomplete, undefined]
                          : variante && !agbAccepted
                            ? [t.hintAcceptAgb, undefined]
                            : zahlAnzeige === "spaeter"
                              ? [t.anzahlungSpaeter, "zahlknopf-spaeter"]
                              : [zahlArt, undefined];
                      // Mockup „Zahlleiste“ (Antoine, 14.10.2026): auf dem Handy statt des grauen Knopfs
                      // ein schlichtes „Weiter“, das zur ersten offenen Angabe scrollt. Ab `md` wie bisher.
                      const nochOffen = !vorschau && phase !== "submitting" && (!selected || !agbAccepted || !billingComplete || wahlLuecke);
                      const zurOffenenAngabe = () => {
                        const ziel = !selected || wahlLuecke ? "varianten-auswahl" : !billingComplete ? "rechnungsadresse" : "agb-zustimmung";
                        scrolleZu(document.getElementById(ziel) ? ziel : "annahme-bereich");
                      };
                      return (
                        <FesteLeiste
                          testId="feste-leiste-annahme"
                          info={
                            variante ? (
                              <>
                                <span className="block sm:inline" data-testid="feste-leiste-summe">
                                  {aLaCarteGewaehlt
                                    ? alcT.nachKarte
                                    : `${t.addonSummeGesamt} ${money(variante.amount_total_cents + zubuchungenSummeCents)}`}
                                </span>
                                {zahlVorschau && zahlVorschau.betragCents > 0 && !zahlVorschau.vollzahlung && (
                                  <span className="block font-normal text-on-surface-variant sm:inline dark:text-secondary-fixed-dim">
                                    <span className="hidden sm:inline"> · </span>
                                    {t.vorschauArtAnzahlung} {money(zahlVorschau.betragCents)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="font-normal">{t.hintChooseOption}</span>
                            )
                          }
                          hinweis={hinweis}
                          hinweisTestId={hinweisTestId}
                        >
                          {selected && (
                            <Button type="button" variant="ghost" className="!px-2 !py-2.5 md:hidden" onClick={() => setBlattOffen(true)} data-testid="feste-leiste-auswahl">
                              {AUSWAHL_T[lang].meineAuswahl}
                            </Button>
                          )}
                          {nochOffen && (
                            <Button type="button" variant="ghost" className="!px-2 !py-2.5 !text-primary md:hidden dark:!text-primary-fixed-dim" onClick={zurOffenenAngabe} data-testid="feste-leiste-weiter">
                              {t.leisteWeiter}
                              <Icon name="arrow_downward" className="text-[18px]" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            className={`max-w-[12.5rem] !px-4 !py-2.5 text-center !leading-tight sm:max-w-none ${nochOffen ? "hidden md:inline-flex" : ""}`}
                            disabled={vorschau || !selected || !agbAccepted || !billingComplete || wahlLuecke || phase === "submitting"}
                            title={vorschau ? VORSCHAU_DEAKTIVIERT : undefined}
                            onClick={() => void accept()}
                          >
                            {phase === "submitting" ? t.ctaSubmitting : aLaCarteGewaehlt ? alcT.cta : ctaLabel}
                            <Icon name="arrow_forward" className="hidden text-[18px] sm:inline" />
                          </Button>
                          {/* Nicht bei `kostenuebernahme`: dort entsteht nach der Annahme kein Zahlungslink. */}
                          {paymentPathway !== "kostenuebernahme" && zahlVorschau && zahlVorschau.betragCents > 0 && (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled
                              aria-disabled="true"
                              title={`${zahlArt}${/[.!?]$/.test(zahlArt ?? "") ? "" : "."} ${t.vorschauHinweis}`}
                              className="hidden cursor-not-allowed !px-4 !py-2.5 opacity-60 md:inline-flex"
                            >
                              {t.vorschauCta(money(zahlVorschau.betragCents))}
                            </Button>
                          )}
                        </FesteLeiste>
                      );
                    })()}
                  </>
                )}

                {/*
                  ═══ Punkt 57 Etappe 3: der Gast unterschreibt die Kostenübernahme selbst ═══

                  Antoine, 18.09.2026: „Nur wenn der Betreiber es freigibt, dass eine neue
                  ausgelöst werden soll" · „Er muss AGB und Stornobedingungen separat anklicken
                  und akzeptieren" · „Meistens bestellt z. B. die Sekretärin und der Chef muss
                  unterschreiben."

                  BEWUSST ZUSÄTZLICH, nicht anstelle des Annehmen-Knopfes darüber. Die
                  Kostenübernahme ist ein zweiter Weg zur verbindlichen Zusage, kein Ersatz für
                  den ersten: fällt der Signaturanbieter aus, muss der Gast trotzdem zusagen
                  können. Genau das ist die oberste Regel dieses Systems.

                  Sichtbar nur, wenn der Betreiber freigegeben hat UND Signaturzugang besteht —
                  sonst stünde hier ein Weg, den niemand zu Ende gehen kann.
                */}
                {/* C1: auf der neuen Seite ein Nebenweg — der Link oeffnet genau diese Karte (unveraendert). */}
                {zweiSchritte && zusageSchritt === 2 && kuWegVerfuegbar && !kuNebenwegOffen && (
                  <KostenuebernahmeNebenweg lang={lang} onOeffnen={kuNebenwegOeffnen} />
                )}
                {kuWegVerfuegbar && (!zweiSchritte || (zusageSchritt === 2 && kuNebenwegOffen)) && (
                  <div id={zweiSchritte ? "ku-nebenweg" : undefined} className="maestro-druck-aus mt-8 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface">
                    <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.kuHeading}</h3>
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.kuIntro}</p>

                    <h4 className="mt-5 font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">{t.kuSignerHeading}</h4>
                    <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.kuSignerHint}</p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Input placeholder={t.kuSignerName} value={kuSignerName} onChange={(e) => setKuSignerName(e.target.value)} />
                      <Input type="email" placeholder={t.kuSignerEmail} value={kuSignerEmail} onChange={(e) => setKuSignerEmail(e.target.value)} />
                      <Input className="sm:col-span-2" placeholder={t.kuSignerCompany} value={kuSignerCompany} onChange={(e) => setKuSignerCompany(e.target.value)} />
                    </div>

                    {/* ZWEI Häkchen, getrennt. Ein Sammelfeld „ich akzeptiere alles" wäre genau
                        das, was Antoine ausdrücklich nicht wollte — und bei einem Vertrag über
                        Geld ist der Unterschied nicht formal. */}
                    <label className="mt-5 flex items-start gap-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      <Checkbox className="mt-0.5" checked={kuAgb} onChange={(e) => setKuAgb(e.target.checked)} />
                      <span>{t.kuAgbLabel}</span>
                    </label>
                    {/* AGB-Link STORIA (28.09.2026): wer hier bestaetigt, die AGB gelesen zu haben,
                        muss sie auch direkt daneben oeffnen koennen — nicht nur weiter oben. */}
                    {offer.agb?.url ? (
                      <a
                        href={offer.agb.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-9 mt-1 inline-block font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                        data-testid="offer-ku-agb-link"
                      >
                        {t.agbAnzeigen}
                      </a>
                    ) : offer.agb?.text ? (
                      <button
                        type="button"
                        className="ml-9 mt-1 font-body-sm text-body-sm font-semibold text-on-surface underline underline-offset-2 dark:text-inverse-on-surface"
                        aria-expanded={agbOffen}
                        onClick={() => setAgbOffen((o) => !o)}
                        data-testid="offer-ku-agb-knopf"
                      >
                        {agbOffen ? t.agbAusblenden : t.agbAnzeigen}
                      </button>
                    ) : null}
                    <label className="mt-3 flex items-start gap-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      <Checkbox className="mt-0.5" checked={kuStorno} onChange={(e) => setKuStorno(e.target.checked)} />
                      <span>{t.kuStornoLabel}</span>
                    </label>

                    {kuFehler && (
                      <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{kuFehler}</p>
                    )}
                    <Button
                      type="button"
                      className="mt-5 w-full !py-4"
                      disabled={vorschau || kuBusy || !kuAgb || !kuStorno || !billingComplete}
                      title={vorschau ? VORSCHAU_DEAKTIVIERT : undefined}
                      onClick={() => void kostenuebernahmeAusloesen()}
                    >
                      {kuBusy ? t.kuBusy : t.kuCta}
                      <Icon name="draw" className="text-[18px]" />
                    </Button>
                    {(!kuAgb || !kuStorno || !billingComplete) && (
                      <p className="mt-3 text-center font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{t.kuHintPflicht}</p>
                    )}
                  </div>
                )}

                {/* Bereits angefordert — der Link zur Unterschrift, egal ob gerade ausgelöst oder
                    aus einem früheren Besuch. Ohne ihn müsste der Gast auf eine Mail warten, die
                    es bewusst nicht gibt. */}
                {kuSignUrl && kuStatus?.status !== "signiert" && (
                  <div className="maestro-druck-aus mt-8 rounded-xl border border-outline-variant/60 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface">
                    <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                      {kuStatus?.ersetzt_signiert_am ? t.kuErsetztHeading : t.kuBereitHeading}
                    </h3>
                    {kuStatus?.ersetzt_signiert_am && (
                      <p className="mt-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" data-testid="offer-ku-ersetzt">
                        {t.kuErsetzt(formatDate(kuStatus.ersetzt_signiert_am, dateLocale))}
                      </p>
                    )}
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.kuBereitBody}</p>
                    <ButtonLink className="mt-4 inline-flex w-full justify-center !py-4" href={kuSignUrl} target="_blank" rel="noopener noreferrer">
                      {t.kuBereitCta}
                      <Icon name="open_in_new" className="text-[18px]" />
                    </ButtonLink>
                  </div>
                )}

                {kuStatus?.status === "signiert" && (
                  <p className="maestro-druck-aus mt-8 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="verified" className="text-[20px] text-secondary dark:text-secondary-fixed-dim" />
                    {t.kuSigniert(kuStatus.signiert_am ? formatDate(kuStatus.signiert_am, dateLocale) : "")}
                  </p>
                )}

                {/*
                  `zahlVorschauKnopf` — Punkt 54: steht jetzt UNTER der Annahme, nicht mehr darüber.
                  Seit 14.10.2026 in der festen Leiste (`feste-leiste-annahme`) rechts neben dem
                  Annehmen-Knopf statt in einem eigenen Kasten hier.

                  Antoine, 16.09.2026, zum ersten Bildschirmfoto: „Es sollte hier zu sehen sein,
                  dass im zweiten Step ein Stripe Link kommt, falls dem so ist." Der Gast las die
                  Bedingungen als Text und wusste trotzdem nicht, dass nach dem Annehmen noch ein
                  Zahlungsschritt kommt — und schon gar nicht, über welchen Betrag. Der Knopf zeigt
                  beides und ist ausgegraut: er nimmt die Annahme nicht vorweg, sondern erklärt sie.

                  Antoine, 18.09.2026, zum zweiten Bildschirmfoto: die Reihenfolge stimmte nicht.
                  Jetzt steht er, wo der Ablauf ihn hinstellt — nach dem Annehmen.

                  Der BETRAG kommt fertig vom Server (`zahlungsVorschau`, dieselbe
                  `faelligeZahlungNachAnnahme` wie die Annahme, seit Punkt 117). Hier wird bewusst NICHT
                  gerechnet — sonst zeigte die Seite irgendwann etwas anderes an, als der
                  Zahlungslink abbucht.

                  Nicht gezeichnet bei `kostenuebernahme`: auf diesem Weg entsteht nach der Annahme
                  gar kein Zahlungslink (`lib/offer-accept.ts`, `linkBlockiert`). Ein Zahlknopf wäre
                  dort schlicht unwahr.
                */}

                {/* E4.6: Ablehnen-Weg — nur wenn der Betrieb ihn anbietet (ablehnenEnabled,
                    sql/194; serverseitig zusaetzlich erzwungen). Bewusst dezent unterhalb der
                    Annahme: ehrlich moeglich, aber keine Einladung. */}
                {/* sql/565 (R1): dieselbe Sperre wie beim Annehmen-Knopf oben. */}
                {!v2 && offer.ablehnenEnabled && !gueltigeKueBlock && !declineOpen && (
                  <p className="maestro-druck-aus mt-6 text-center">
                    <button
                      type="button"
                      className="font-body-sm text-body-sm text-secondary underline underline-offset-2 hover:text-on-surface dark:text-secondary-fixed-dim"
                      onClick={() => setDeclineOpen(true)}
                    >
                      {t.declineToggle}
                    </button>
                  </p>
                )}
                {offer.ablehnenEnabled && !gueltigeKueBlock && declineOpen && (
                  <div
                    id={v2 ? "gast-absage" : undefined}
                    className="maestro-druck-aus mt-6 scroll-mt-4 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface"
                  >
                    <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.declineHeading}</h3>
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.declineBody}</p>
                    <textarea
                      value={declineGrund}
                      onChange={(e) => setDeclineGrund(e.target.value)}
                      placeholder={t.declinePlaceholder}
                      maxLength={500}
                      rows={2}
                      className="mt-3 w-full rounded-lg border border-outline-variant/50 bg-surface-container-lowest px-3 py-2 font-body-sm text-body-sm text-on-surface outline-none focus:border-primary dark:border-outline/40 dark:bg-surface-dim/30 dark:text-inverse-on-surface"
                    />
                    <Zeichenzaehler current={declineGrund.length} max={500} />
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        className="font-body-sm text-body-sm text-secondary underline underline-offset-2 dark:text-secondary-fixed-dim sm:mr-auto"
                        onClick={() => setDeclineOpen(false)}
                        disabled={declining}
                      >
                        {t.declineCancel}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-outline-variant/60 px-4 py-2 font-body-sm text-body-sm text-on-surface-variant hover:bg-surface-container dark:border-outline/40 dark:text-secondary-fixed-dim"
                        onClick={() => void decline()}
                        disabled={declining || vorschau}
                        title={vorschau ? VORSCHAU_DEAKTIVIERT : undefined}
                      >
                        {declining ? t.declineSubmitting : t.declineCta}
                      </button>
                    </div>
                  </div>
                )}

                {/* K5.2 (E-K11): Änderungswunsch — neben dem Absagen-Weg, gleiche Zurückhaltung
                    (dezenter Aufklapp-Link statt prominenter Knopf). Nur solange das Angebot
                    versendet + nicht abgelaufen ist (dieser Block liegt bereits im ready-Zweig
                    hinter dem expired-Gate); serverseitig gilt zusätzlich status='offer_sent'.
                    Der Text läuft in DIESELBE v2-Pipeline wie eine Mail-Antwort. */}
                {wunschDone ? (
                  <p className="maestro-druck-aus mt-6 rounded-xl border border-outline-variant/40 bg-surface p-5 text-center font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30 dark:bg-inverse-surface dark:text-secondary-fixed-dim">
                    {v2 && wunschArt === "frage" ? v2t.frageDanke(v2Vorname) : t.wunschDanke}
                  </p>
                ) : !wunschOpen ? (
                  v2 ? null : (
                  <p className="maestro-druck-aus mt-4 text-center">
                    <button
                      type="button"
                      className="font-body-sm text-body-sm text-secondary underline underline-offset-2 hover:text-on-surface dark:text-secondary-fixed-dim"
                      onClick={() => setWunschOpen(true)}
                    >
                      {t.wunschToggle}
                    </button>
                  </p>
                  )
                ) : (
                  <div
                    id={v2 ? "gast-nachricht" : undefined}
                    data-testid={v2 ? `v2-nachricht-${wunschArt}` : undefined}
                    className="maestro-druck-aus mt-6 scroll-mt-4 rounded-xl border border-outline-variant/40 bg-surface p-5 dark:border-outline/30 dark:bg-inverse-surface"
                  >
                    <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                      {v2 && wunschArt === "frage" ? v2t.frageFormTitel(v2Vorname) : t.wunschHeading}
                    </h3>
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {v2 && wunschArt === "frage" ? v2t.frageFormText : t.wunschBody}
                    </p>
                    <textarea
                      value={wunschText}
                      onChange={(e) => setWunschText(e.target.value)}
                      placeholder={v2 && wunschArt === "frage" ? v2t.fragePlatzhalter : t.wunschPlaceholder}
                      maxLength={2000}
                      rows={3}
                      className="mt-3 w-full rounded-lg border border-outline-variant/50 bg-surface-container-lowest px-3 py-2 font-body-sm text-body-sm text-on-surface outline-none focus:border-primary dark:border-outline/40 dark:bg-surface-dim/30 dark:text-inverse-on-surface"
                    />
                    <Zeichenzaehler current={wunschText.length} max={2000} />
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        className="font-body-sm text-body-sm text-secondary underline underline-offset-2 dark:text-secondary-fixed-dim sm:mr-auto"
                        onClick={() => setWunschOpen(false)}
                        disabled={wunschSending}
                      >
                        {t.declineCancel}
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-outline-variant/60 px-4 py-2 font-body-sm text-body-sm text-on-surface-variant hover:bg-surface-container dark:border-outline/40 dark:text-secondary-fixed-dim"
                        onClick={() => void sendeAenderungswunsch()}
                        disabled={wunschSending || vorschau || wunschText.trim().length < 5}
                        title={vorschau ? VORSCHAU_DEAKTIVIERT : undefined}
                      >
                        {wunschSending ? t.wunschSubmitting : v2 && wunschArt === "frage" ? v2t.frageSenden : t.wunschCta}
                      </button>
                    </div>
                    {vorschau && (
                      <p className="mt-2 text-center font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {VORSCHAU_DEAKTIVIERT}
                      </p>
                    )}
                  </div>
                )}
                {/*
                  Buchungsstrecke B2: Anpassung und Absage als dezente Fusslinks — dieselben Wege wie
                  bisher (Aenderungswunsch-Formular, Absage-Formular mit `decline()`), nur nicht mehr
                  direkt unter dem Knopf als Einladung zum Ausstieg.
                */}
                {v2 && ((!wunschOpen && !wunschDone) || (offer.ablehnenEnabled && !gueltigeKueBlock && !declineOpen)) && (
                  <nav
                    aria-label={v2t.fussAria}
                    className="maestro-druck-aus mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-surface-variant pt-4 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim"
                    data-testid="v2-fusslinks"
                  >
                    {!wunschOpen && !wunschDone && (
                      <button type="button" className="underline-offset-2 hover:text-on-surface hover:underline" onClick={() => oeffneNachricht("wunsch")}>
                        {v2t.fussAnpassung}
                      </button>
                    )}
                    {!wunschOpen && !wunschDone && offer.ablehnenEnabled && !gueltigeKueBlock && !declineOpen && <span aria-hidden>·</span>}
                    {offer.ablehnenEnabled && !gueltigeKueBlock && !declineOpen && (
                      <button type="button" className="underline-offset-2 hover:text-on-surface hover:underline" onClick={() => {
                          setDeclineOpen(true);
                          setTimeout(() => scrolleZu("gast-absage"), 0);
                        }}
                      >
                        {v2t.fussAbsagen}
                      </button>
                    )}
                  </nav>
                )}
              </div>
              )}
            </>
          )}
        </div>
      </main>

      {/*
        Buchungsstrecke B2: mobile Leiste unten — gewaehlte Variante, Summe vom Server und ein Knopf,
        der NUR zum Annahmebereich scrollt (angenommen wird ausschliesslich mit dem Knopf dort).
      */}
      {leistenVariante && schrittLeisteDa && (
        <MobileLeiste
          titel={leistenVariante.title || (istALaCarteOhneBetrag(leistenVariante) ? alcT.nachKarte : money(leistenVariante.amount_total_cents))}
          gaeste={leistenVariante.guest_count}
          gesamtCents={leistenVariante.amount_total_cents}
          aLaCarte={aLaCarteGewaehlt}
          mitZubuchungen={hatZubuchung}
          lang={lang}
          onWeiter={() => scrolleZu("annahme-bereich")}
        />
      )}

      {/* Migration 327: frei anlegbare "Ueberschrift/Bild/Text"-Bereiche (Betreiber-Einstellungen,
          Tab "Angebots-Vorlagen & Branding"). Nach den Varianten/Aktionen, vor dem Footer - eine
          Entscheidung ohne Vorgabe von Antoine (reversibel, s. Session-Notiz): die Bereiche sind
          Marketing/Vertrauensbildung ("Lernen Sie unser Haus kennen"), keine Kaufentscheidung -
          sie sollen die Options-Karten/CTA nicht unterbrechen. maestro-druck-aus wie der Hero:
          Fotos drucken nicht mit, der Beleg bleibt kompakt. */}
      {offer && offer.branding?.content_blocks && offer.branding.content_blocks.length > 0 && (
        <section className="maestro-druck-aus mx-auto max-w-5xl space-y-10 px-margin-mobile py-12 md:px-margin-desktop">
          {offer.branding.content_blocks.map((block) => (
            <div key={block.id} className="grid grid-cols-1 items-center gap-6 md:grid-cols-2">
              {block.image_url && (
                <div className="overflow-hidden rounded-2xl">
                  <img src={block.image_url} alt="" className="h-56 w-full object-cover md:h-72" />
                </div>
              )}
              <div className={block.image_url ? "" : "md:col-span-2"}>
                <h2 className="font-serif text-headline-md text-on-background dark:text-inverse-on-surface">{block.heading}</h2>
                <p className="mt-3 whitespace-pre-line font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">{block.body}</p>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Footer — CX-Audit 2026-07-17: zeigt jetzt den echten Betriebskontakt (token-gebunden
          aus der Public-API, sql/136), sobald gepflegt; sonst wie zuvor nur Name + Tagline. */}
      <footer className="mt-12 border-t border-surface-variant bg-surface-container-high px-margin-mobile py-10 dark:border-outline/30 dark:bg-surface-dim/20 md:px-margin-desktop">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-8 text-center md:flex-row md:text-left">
          <div className="flex items-center gap-4 text-secondary dark:text-secondary-fixed-dim">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-surface-container-high text-on-surface-variant dark:bg-inverse-surface dark:text-secondary-fixed-dim">
              <Icon name="verified" className="text-2xl" />
            </span>
            <div>
              <p className="font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                {tenantLabel}
              </p>
              <p className="font-body-sm text-body-sm">{t.footerTagline}</p>
              {(offer?.contact?.company_email || offer?.contact?.company_phone) && (
                <p className="mt-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-body-sm text-body-sm md:justify-start">
                  {offer.contact?.company_email && (
                    <a href={`mailto:${offer.contact.company_email}`} className="inline-flex items-center gap-1.5 hover:text-on-surface hover:underline">
                      <Icon name="mail" className="text-[15px]" /> {offer.contact.company_email}
                    </a>
                  )}
                  {offer.contact?.company_phone && (
                    <a href={`tel:${offer.contact.company_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-on-surface hover:underline">
                      <Icon name="call" className="text-[15px]" /> {offer.contact.company_phone}
                    </a>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-center gap-2 md:items-end">
            {/* AGB-Link STORIA (28.09.2026): Impressum und Datenschutz im Fuss, sofern hinterlegt. */}
            {(offer?.rechtliches?.impressumUrl || offer?.rechtliches?.privacyUrl) && (
              <nav aria-label={lang === "en" ? "Legal" : "Rechtliches"} className="flex gap-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {offer.rechtliches?.impressumUrl && (
                  <a href={offer.rechtliches.impressumUrl} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:text-on-surface hover:underline" data-testid="offer-impressum-link">
                    {lang === "en" ? "Legal notice" : "Impressum"}
                  </a>
                )}
                {offer.rechtliches?.privacyUrl && (
                  <a href={offer.rechtliches.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:text-on-surface hover:underline" data-testid="offer-datenschutz-link">
                    {lang === "en" ? "Privacy" : "Datenschutz"}
                  </a>
                )}
              </nav>
            )}
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              powered by MAESTRO
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
